import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CustomerTermsRepair from './CustomerTermsRepair';
import axios from 'axios';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Popover,
  Radio,
  Select,
  Step,
  StepButton,
  Stepper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { useHistory } from 'react-router-dom';

const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
const steps = ['Upload Sources', 'Map Fields', 'Match & Enrich', 'Assign & Review', 'Dry Run & Import', 'Results'];
const artworkSteps = ['Upload / Ingest', 'Analyze', 'Map Folders', 'Review', 'Import', 'Results'];
const decoratorMatrixSteps = ['Upload', 'Preview', 'Dry Run', 'Import'];
const customSupplierSteps = ['Upload', 'Preview', 'Import'];
const customerTermsSteps = ['Upload & Review'];
const hasAsiSupplierReference = (supplier) => (
  Array.isArray(supplier?.References)
  && supplier.References.some((reference) => reference?.source === 'asi-live')
);
const CUSTOMER_PREVIEW_PAGE_SIZE = 50;
const importOptions = [
  { key: 'customer-terms', title: 'Customer Terms Update', description: 'Match existing customers and update approved payment terms from a spreadsheet.', icon: <PeopleOutlineOutlinedIcon color="primary" /> },
  {
    key: 'contact-repair',
    title: 'Contact Repair',
    description: 'Repair imported customer contacts from the original spreadsheet, with a review before applying.',
    icon: <PeopleOutlineOutlinedIcon color="primary" />,
    administratorOnly: true,
  },
  {
    key: 'customers',
    title: 'Customers',
    description: 'Import customers and leads from spreadsheets.',
    icon: <PeopleOutlineOutlinedIcon color="primary" />,
  },
  {
    key: 'artwork',
    title: 'Artwork',
    description: 'Import existing artwork libraries and connect folders/files to customers.',
    icon: <DriveFolderUploadOutlinedIcon color="primary" />,
  },
  {
    key: 'decorator-matrix',
    title: 'Decoration Matrix',
    description: 'Replace the active decoration charge matrix from an Elite Core matrix export while retaining archived legacy charges.',
    icon: <SettingsOutlinedIcon color="primary" />,
  },
  {
    key: 'custom-suppliers',
    title: 'Custom Suppliers',
    description: 'Import standalone custom Core Suppliers or connect existing shared Core Suppliers without overwriting shared supplier data.',
    icon: <StorefrontOutlinedIcon color="primary" />,
  },
];

const formatBytes = (size = 0) => {
  if (!size) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const idx = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
  return `${(size / (1024 ** idx)).toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`;
};

const evidenceLabel = (type = '') => ({
  external_customer_id: 'Same source customer ID',
  company_email: 'Same company email',
  contact_email: 'Same contact email',
  business_name: 'Same normalized business name',
  postal_code: 'Same postal code',
  phone: 'Same phone number',
  website_domain: 'Same website/email domain',
}[type] || type.replaceAll('_', ' '));

const suggestionValue = (suggestion) => {
  if (suggestion.type === 'add_contact') {
    return [suggestion.suggestedValue?.firstName, suggestion.suggestedValue?.lastName].filter(Boolean).join(' ')
      + (suggestion.suggestedValue?.email ? ` — ${suggestion.suggestedValue.email}` : '');
  }
  if (suggestion.type === 'add_address') {
    const address = suggestion.suggestedValue || {};
    return [address.streetAddress, address.city, address.stateProvince, address.postalZipCode].filter(Boolean).join(', ');
  }
  return String(suggestion.suggestedValue ?? '—');
};

const sha256File = async (file) => {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(hashBuffer)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
};

const getFileRelativePath = (file) => file.webkitRelativePath || file.relativePath || file.name;
const canon = (value) => String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
const profilePictureUrl = (value) => {
  if (!value) return undefined;

  const resolve = (candidate) => {
    if (!candidate) return undefined;
    if (Array.isArray(candidate)) {
      const selected = candidate.find((item) => item?.selected) || candidate[0];
      return resolve(selected);
    }
    if (typeof candidate === 'object') {
      return candidate.url
        || candidate.location
        || candidate.src
        || candidate.path
        || candidate.fileUrl
        || candidate.fileURL;
    }
    return typeof candidate === 'string' ? candidate : undefined;
  };

  if (typeof value === 'string') {
    try {
      return resolve(JSON.parse(value));
    } catch (_error) {
      return value;
    }
  }

  return resolve(value);
};
const initialsFor = (user) => [user?.first_name, user?.last_name].filter(Boolean).map((part) => part[0]).join('').toUpperCase() || '?';

const readEntryFiles = (entry, prefix = '') => new Promise((resolve) => {
  if (!entry) return resolve([]);
  if (entry.isFile) {
    entry.file((file) => {
      file.relativePath = `${prefix}${file.name}`;
      resolve([file]);
    }, () => resolve([]));
    return;
  }
  if (!entry.isDirectory) return resolve([]);
  const reader = entry.createReader();
  const allEntries = [];
  const readBatch = () => {
    reader.readEntries(async (entries) => {
      if (!entries.length) {
        const nested = await Promise.all(allEntries.map((child) => readEntryFiles(child, `${prefix}${entry.name}/`)));
        resolve(nested.flat());
        return;
      }
      allEntries.push(...entries);
      readBatch();
    }, () => resolve([]));
  };
  readBatch();
});

const ImportToolDialog = ({ open, onClose, instanceUniqueId, initialImportType = null, canRepairContacts = false, onOpenContactRepair }) => {
  const history = useHistory();
  const inputRef = useRef(null);
  const sourceInputRef = useRef(null);
  const previewPageRequestRef = useRef(0);
  const previewFilterRefreshReadyRef = useRef(0);
  const progressiveReadyRequestRef = useRef(0);
  const progressiveFetchInFlightRef = useRef(false);
  const progressiveLoadedCountRef = useRef(0);
  const [activeStep, setActiveStep] = useState(0);
  const [highestReachedStep, setHighestReachedStep] = useState(0);
  const [selectedTool, setSelectedTool] = useState(null);
  const [destination, setDestination] = useState('customers');
  const [file, setFile] = useState(null);
  const [worksheetName, setWorksheetName] = useState('');
  const [mappingOverrides, setMappingOverrides] = useState({});
  const [preview, setPreview] = useState(null);
  const [recordActions, setRecordActions] = useState({});
  const [recordActionKinds, setRecordActionKinds] = useState({});
  const [representativeDecisions, setRepresentativeDecisions] = useState({});
  const [matchDecisions, setMatchDecisions] = useState({});
  const [previewFilter, setPreviewFilter] = useState('all');
  const [previewPage, setPreviewPage] = useState(1);
  const [importSession, setImportSession] = useState(null);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunInputKey, setDryRunInputKey] = useState(null);
  const [dryRunning, setDryRunning] = useState(false);
  const [importing, setImporting] = useState(false);
  const [addingSource, setAddingSource] = useState(false);
  const [pendingSource, setPendingSource] = useState(null);
  const [pendingSourceFile, setPendingSourceFile] = useState(null);
  const [sourceMappingOverrides, setSourceMappingOverrides] = useState({});
  const [sourceWorksheet, setSourceWorksheet] = useState('');
  const [unmatchedPolicy, setUnmatchedPolicy] = useState('review');
  const [reviewSource, setReviewSource] = useState(null);
  const [replacingSource, setReplacingSource] = useState(null);
  const [artworkSession, setArtworkSession] = useState(null);
  const [artworkManifesting, setArtworkManifesting] = useState(false);
  const [artworkFolderLevel, setArtworkFolderLevel] = useState(1);
  const [artworkMappings, setArtworkMappings] = useState({});
  const [artworkFilter, setArtworkFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [importErrorDetails, setImportErrorDetails] = useState(null);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [templateHelpOpen, setTemplateHelpOpen] = useState(false);
  const [representativeGroupTarget, setRepresentativeGroupTarget] = useState(null);
  const [representativeGroupDraft, setRepresentativeGroupDraft] = useState([]);
  const [mergeDetailsAnchor, setMergeDetailsAnchor] = useState(null);
  const [mergeDetailsRecord, setMergeDetailsRecord] = useState(null);
  const [unassignedContinueConfirmOpen, setUnassignedContinueConfirmOpen] = useState(false);
  const [allowUnassignedImport, setAllowUnassignedImport] = useState(false);
  const [needsReviewContinueConfirmOpen, setNeedsReviewContinueConfirmOpen] = useState(false);
  const [skipUnresolvedMatches, setSkipUnresolvedMatches] = useState(false);
  const [previewNeedsRefresh, setPreviewNeedsRefresh] = useState(false);
  const [previewProgress, setPreviewProgress] = useState(null);
  const [previewPageLoading, setPreviewPageLoading] = useState(false);
  const [previewPageLoadingLabel, setPreviewPageLoadingLabel] = useState('');
  const [previewFilteredReadyCount, setPreviewFilteredReadyCount] = useState(null);
  const [representativeReviewComplete, setRepresentativeReviewComplete] = useState(false);
  const [importMode, setImportMode] = useState('merge_missing');
  const [representativeFilter, setRepresentativeFilter] = useState('all');
  const [representativeFilterActivated, setRepresentativeFilterActivated] = useState(false);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkAssignUsers, setBulkAssignUsers] = useState([]);
  const [bulkAssignMode, setBulkAssignMode] = useState('add');
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [livePreviewEnabled, setLivePreviewEnabled] = useState(true);
  const [reviewCustomer, setReviewCustomer] = useState(null);
  const [dryRunInspectRecord, setDryRunInspectRecord] = useState(null);
  const [dryRunCopyStatus, setDryRunCopyStatus] = useState('');
  const [dryRunResultFilter, setDryRunResultFilter] = useState('blocked');
  const [dryRunResultPage, setDryRunResultPage] = useState(1);
  const [testImportSize, setTestImportSize] = useState(10);
  const [testImportMode, setTestImportMode] = useState('mixed');
  const [testImportRunning, setTestImportRunning] = useState(false);
  const [testImportResult, setTestImportResult] = useState(null);
  const [testImportInspectOpen, setTestImportInspectOpen] = useState(false);
  const [testImportInspectIndex, setTestImportInspectIndex] = useState(0);
  const [decoratorMatrixFile, setDecoratorMatrixFile] = useState(null);
  const [decoratorMatrixPreview, setDecoratorMatrixPreview] = useState(null);
  const [decoratorMatrixDryRun, setDecoratorMatrixDryRun] = useState(null);
  const [decoratorMatrixDryRunning, setDecoratorMatrixDryRunning] = useState(false);
  const [decoratorMatrixDryRunFilter, setDecoratorMatrixDryRunFilter] = useState('meaningful');
  const [decoratorMatrixDryRunPage, setDecoratorMatrixDryRunPage] = useState(1);
  const [decoratorMatrixValuePreview, setDecoratorMatrixValuePreview] = useState(null);
  const [decoratorMatrixImporting, setDecoratorMatrixImporting] = useState(false);
  const [decoratorMatrixArchiveExisting, setDecoratorMatrixArchiveExisting] = useState(true);
  const [decoratorMatrixResult, setDecoratorMatrixResult] = useState(null);
  const [decoratorMatrixPage, setDecoratorMatrixPage] = useState(1);
  const [customSupplierFile, setCustomSupplierFile] = useState(null);
  const [customSupplierPreview, setCustomSupplierPreview] = useState(null);
  const [selectedCustomSupplierIds, setSelectedCustomSupplierIds] = useState([]);
  const [customSupplierMatchDecisions, setCustomSupplierMatchDecisions] = useState({});
  const [asiBatchReviewOpen, setAsiBatchReviewOpen] = useState(false);
  const [asiBatchSelectedIds, setAsiBatchSelectedIds] = useState([]);
  const [customSupplierImporting, setCustomSupplierImporting] = useState(false);
  const [customSupplierTestCount, setCustomSupplierTestCount] = useState(null);
  const [customSupplierResult, setCustomSupplierResult] = useState(null);
  const [customSupplierPage, setCustomSupplierPage] = useState(1);
  const [customSupplierDetailsId, setCustomSupplierDetailsId] = useState(null);
  const [customSupplierSearch, setCustomSupplierSearch] = useState('');
  const [customSupplierDecisionFilter, setCustomSupplierDecisionFilter] = useState('all');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [totalUploadBytes, setTotalUploadBytes] = useState(0);
  const [uploadStartedAt, setUploadStartedAt] = useState(null);
  const [processingStartedAt, setProcessingStartedAt] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [uploadPhase, setUploadPhase] = useState(null);
  const [uploadContext, setUploadContext] = useState(null);

  const hasProgress = Boolean(selectedTool || file || preview || activeStep > 0);
  const activeSteps = selectedTool === 'customer-terms' ? customerTermsSteps : selectedTool === 'artwork'
    ? artworkSteps
    : selectedTool === 'decorator-matrix'
      ? decoratorMatrixSteps
      : selectedTool === 'custom-suppliers'
        ? customSupplierSteps
        : steps;
  const columns = preview?.columns || [];
  const worksheetOptions = preview?.file?.worksheets || [];
  const records = preview?.records || [];
  const isProgressivePreview = Boolean(preview?.progressive || previewProgress);
  const readyRecordCount = previewProgress?.readyRecords ?? records.length;
  const totalPreviewRecordCount = previewProgress?.totalRecords || preview?.summary?.proposedCustomers || records.length;
  const previewFile = previewProgress?.file || preview?.file || null;
  const previewCommitReady = Boolean(previewProgress?.complete || preview?.commitEnabled);
  const progressivePhaseLabel = (phase) => ({
    queued: 'Queued',
    parsing: 'Parsing spreadsheet',
    matching: 'Matching existing customers',
    processing: 'Preparing preview',
    previewing: 'Preparing preview',
    previewed: 'Preview complete',
    failed: 'Preview stopped',
  }[phase] || 'Processing');
  const sources = preview?.sources || [];
  const representativeMappings = preview?.representativeMappings || [];
  const suggestions = preview?.suggestions || [];
  const selectedForImport = useCallback(
    (record) => (recordActions[record.id] || defaultActionForRecord(record)) !== 'skip',
    [recordActions],
  );

  // Build assignment membership once per data/decision change instead of walking
  // every representative resolution repeatedly during each render, filter pass,
  // footer calculation, and representative sort.
  const assignedUserIdsByRecord = useMemo(() => {
    const byRecord = new Map();
    const persistedRepresentativeDecisions = preview?.representativeDecisions || {};
    const effectiveRepresentativeDecisions = {
      ...persistedRepresentativeDecisions,
      ...representativeDecisions,
    };

    records.forEach((record) => {
      const ids = new Set();
      (record.representativeResolutions || []).forEach((resolution) => {
        const rowDecision = representativeDecisions[`${record.id}:${resolution.field}`];
        const sourceDecision = representativeDecisions[`source:${resolution.field}:${canon(resolution.sourceValue)}`]
          || persistedRepresentativeDecisions[`source:${resolution.field}:${canon(resolution.sourceValue)}`];
        const decision = rowDecision || sourceDecision;

        if (decision?.action === 'leave_unassigned') return;
        if (decision?.action === 'assign_group') {
          (decision.assignments || []).forEach((assignment) => {
            if (assignment?.userId) ids.add(String(assignment.userId));
          });
          return;
        }
        if (decision?.userId) {
          ids.add(String(decision.userId));
          return;
        }
        if (resolution.status === 'matched') {
          const resolvedId = resolution.assigned?.id || resolution.matchedUser?.id;
          if (resolvedId) ids.add(String(resolvedId));
        }
      });

      const manual = representativeDecisions[`${record.id}:manual`];
      if (manual?.userId) ids.add(String(manual.userId));

      if (!record?.replaceExistingAssignedReps) {
        (record?.existingCustomer?.assignedReps || []).forEach((assignment) => {
          if (assignment?.id) ids.add(String(assignment.id));
        });
      }
      (record?.assignedReps || []).forEach((assignment) => {
        if (assignment?.id) ids.add(String(assignment.id));
      });

      Object.entries(effectiveRepresentativeDecisions).forEach(([key, decision]) => {
        if (!key.startsWith(`${record.id}:remove:`) || decision?.action !== 'remove_user') return;
        const userId = String(decision.userId || key.split(':').pop() || '');
        if (userId) ids.delete(userId);
      });

      byRecord.set(String(record.id), [...ids]);
    });

    return byRecord;
  }, [preview?.representativeDecisions, records, representativeDecisions]);

  const assignedUserIds = useCallback(
    (record) => assignedUserIdsByRecord.get(String(record?.id)) || [],
    [assignedUserIdsByRecord],
  );

  const enrichedRecordIds = useMemo(() => new Set(
    suggestions
      .filter((suggestion) => ['accepted', 'auto_accepted'].includes(suggestion.status))
      .map((suggestion) => String(suggestion.candidateCustomerId))
  ), [suggestions]);

  const filteredRecords = useMemo(() => records.filter((record) => {
    const assignedIds = assignedUserIdsByRecord.get(String(record.id)) || [];
    if (representativeFilter === 'unassigned' && assignedIds.length) return false;
    if (representativeFilter === 'needs_review' && record.plannedAction !== 'needs_review') return false;
    if (!['all', 'unassigned', 'needs_review'].includes(representativeFilter) && !assignedIds.includes(String(representativeFilter))) return false;
    if (previewFilter === 'all') return true;
    if (previewFilter === 'new') return record.proposedAction === 'New';
    if (previewFilter === 'existing') return record.proposedAction.startsWith('Existing');
    if (previewFilter === 'updates') return record.proposedAction.startsWith('Existing');
    if (previewFilter === 'enriched') return enrichedRecordIds.has(String(record.id));
    if (previewFilter === 'needs_review') return (record.warnings || []).length > 0 || record.proposedAction === 'Conflict';
    if (previewFilter === 'representatives') return (record.representativeResolutions || []).some((rep) => ['review', 'unresolved'].includes(rep.status));
    if (previewFilter === 'conflicts') return record.proposedAction === 'Conflict';
    if (previewFilter === 'warnings') return (record.warnings || []).length > 0;
    if (previewFilter === 'skipped') return (recordActions[record.id] || defaultActionForRecord(record)) === 'skip';
    return true;
  }), [
    assignedUserIdsByRecord,
    enrichedRecordIds,
    previewFilter,
    recordActions,
    records,
    representativeFilter,
  ]);

  const filteredReadyRecordCount = isProgressivePreview && representativeFilter !== 'all'
    ? (previewFilteredReadyCount ?? filteredRecords.length)
    : (isProgressivePreview ? readyRecordCount : filteredRecords.length);

  // Never render thousands of customer cards at once. Progressive mode still loads
  // and preserves every ready record, but the browser only mounts one 50-row page.
  // This keeps all review/edit functionality while removing the dominant DOM cost.
  const clientPagedRecordCount = isProgressivePreview && !representativeFilterActivated
    ? filteredRecords.length
    : filteredReadyRecordCount;
  const previewTotalPages = Math.max(1, Math.ceil(clientPagedRecordCount / CUSTOMER_PREVIEW_PAGE_SIZE));
  const visiblePreviewPage = Math.min(previewPage, previewTotalPages);
  const previewStartIndex = clientPagedRecordCount ? (visiblePreviewPage - 1) * CUSTOMER_PREVIEW_PAGE_SIZE : 0;
  const visiblePreviewRecords = useMemo(
    () => filteredRecords.slice(previewStartIndex, previewStartIndex + CUSTOMER_PREVIEW_PAGE_SIZE),
    [filteredRecords, previewStartIndex],
  );
  const previewEndIndex = previewStartIndex + visiblePreviewRecords.length;
  const selectedRecords = useMemo(() => records.filter(selectedForImport), [records, selectedForImport]);
  const importWideSummary = preview?.summary || previewProgress?.summary || {};
  const importWideAssignmentSummary = preview?.assignmentSummary || {};
  const importWideReadyCount = Number(
    importWideAssignmentSummary.totalRecords
      ?? importWideSummary.proposedCustomers
      ?? readyRecordCount
      ?? records.length
  );

  const resolvedMatchDecisions = Object.values(matchDecisions || {}).filter((decision) =>
    ['use_existing', 'create_new', 'skip'].includes(decision?.action)
  );
  const resolvedBlockingMatchDecisions = resolvedMatchDecisions.filter((decision) => !decision.wasDefaultCreate);
  const resolvedCreateFromBlockingCount = resolvedBlockingMatchDecisions.filter((decision) => decision.action === 'create_new').length;
  const resolvedExistingCount = resolvedMatchDecisions.filter((decision) => decision.action === 'use_existing').length;
  const resolvedSkipCount = resolvedMatchDecisions.filter((decision) => decision.action === 'skip').length;
  const defaultCreateOverridesAway = resolvedMatchDecisions.filter((decision) =>
    decision.wasDefaultCreate && ['use_existing', 'skip'].includes(decision.action)
  ).length;

  const baseNeedsReviewCount = Number(
    importWideSummary.blockingConflictCount
      ?? Math.max(
        0,
        Number(importWideSummary.conflictCount || 0)
          - Number(importWideSummary.defaultCreateReviewCount || 0)
      )
  );
  const needsReviewCount = Math.max(0, baseNeedsReviewCount - resolvedBlockingMatchDecisions.length);

  let footerNewCount = Math.max(
    0,
    Number(importWideSummary.newCount || 0) - defaultCreateOverridesAway + resolvedCreateFromBlockingCount
  );
  let footerMergeCount = importMode === 'merge_missing'
    ? Number(importWideSummary.existingCount || 0) + resolvedExistingCount
    : 0;
  let footerSkippedCount = importMode === 'keep_existing'
    ? Number(importWideSummary.existingCount || 0) + resolvedExistingCount + resolvedSkipCount
    : resolvedSkipCount;

  // Explicit row selection changes are local decisions layered over the server's
  // import-wide summary. recordActionKinds remembers the record's original bucket
  // so these totals remain stable after paging/filtering.
  Object.entries(recordActions || {}).forEach(([recordId, action]) => {
    if (action !== 'skip') return;
    const kind = recordActionKinds[recordId];
    if (kind === 'create') footerNewCount = Math.max(0, footerNewCount - 1);
    if (kind === 'merge') footerMergeCount = Math.max(0, footerMergeCount - 1);
    if (kind === 'needs_review' && !matchDecisions[recordId]) {
      // Needs Review is already represented by the unresolved count; an explicit
      // per-row skip should no longer be presented as an unresolved review item.
    }
    footerSkippedCount += 1;
  });

  const footerUnassignedCount = Number(
    importWideAssignmentSummary.unassignedCount
      ?? selectedRecords.filter((record) => assignedUserIds(record).length === 0).length
  );
  const footerSelectedCount = Math.max(
    0,
    importWideReadyCount - footerSkippedCount - (skipUnresolvedMatches ? needsReviewCount : 0)
  );
  const unassignedCount = footerUnassignedCount;
  const reviewBlocked = false;

  const headers = useMemo(
    () => ({ headers: { accessToken: localStorage.getItem('accessToken') } }),
    [],
  );

  const fetchPreviewRecordPage = useCallback(async (
    sessionId,
    page = previewPage,
    repFilter = representativeFilter,
    label = '',
    { loadingMode = 'blocking' } = {},
  ) => {
    if (!sessionId) return;

    const requestId = previewPageRequestRef.current + 1;
    previewPageRequestRef.current = requestId;
    const blocking = loadingMode === 'blocking';

    if (blocking) {
      setPreviewPageLoading(true);
      setPreviewPageLoadingLabel(label || (repFilter === 'all' ? 'All Customers' : 'matching customers'));

      // Only an explicit filter/page action replaces the current grid with the
      // controlled loading surface. Bootstrap and live polling refreshes keep the
      // currently editable rows on screen.
      setPreview((current) => ({
        ...(current || {}),
        records: [],
      }));
    }

    try {
      const res = await axios.get(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/customers/sessions/${sessionId}/preview-records`,
        {
          ...headers,
          params: { page, pageSize: CUSTOMER_PREVIEW_PAGE_SIZE, representativeFilter: repFilter },
        },
      );
      if (requestId !== previewPageRequestRef.current) return;

      const payload = res.data || {};
      setPreview((current) => ({
        ...(current || {}),
        records: payload.records || [],
        summary: payload.summary || current?.summary,
        assignmentSummary: payload.assignmentSummary || current?.assignmentSummary,
        commitEnabled: Boolean(payload.complete),
        progressive: true,
      }));
      if (repFilter === 'all') {
        setPreviewFilteredReadyCount(null);
      } else {
        setPreviewFilteredReadyCount(Number(payload.filteredTotal ?? payload.readyRecords ?? 0));
      }
      setPreviewProgress((current) => ({
        ...(current || {}),
        complete: payload.complete,
        failed: payload.failed,
        file: payload.file || current?.file,
        summary: payload.summary || current?.summary,
        assignmentSummary: payload.assignmentSummary || current?.assignmentSummary,
      }));
    } catch (err) {
      if (requestId === previewPageRequestRef.current) {
        setError(err?.response?.data?.message || err.message || 'Unable to load preview records.');
      }
    } finally {
      if (blocking && requestId === previewPageRequestRef.current) {
        setPreviewPageLoading(false);
        setPreviewPageLoadingLabel('');
      }
    }
  }, [headers, instanceUniqueId, previewPage, representativeFilter]);

  const fetchProgressiveReadyRecords = useCallback(async (sessionId, readyTarget) => {
    if (!sessionId || !livePreviewEnabled || representativeFilterActivated || progressiveFetchInFlightRef.current) return;

    const pageSize = 250;
    const currentLoaded = Math.max(
      Number(progressiveLoadedCountRef.current || 0),
      Number(preview?.records?.length || 0),
    );
    if (readyTarget <= currentLoaded) return;

    progressiveFetchInFlightRef.current = true;
    const startPage = currentLoaded > 0
      ? Math.floor(currentLoaded / pageSize) + 1
      : 1;
    const targetPage = Math.max(startPage, Math.ceil(readyTarget / pageSize));
    // Fetch up to 1,000 newly-ready records per pass. The next polling pass
    // continues catching up until the editable grid reaches readyRecords.
    const endPage = Math.min(targetPage, startPage + 3);
    const pages = Array.from({ length: endPage - startPage + 1 }, (_item, index) => startPage + index);

    try {
      const responses = await Promise.all(pages.map((page) =>
        axios.get(
          `${SERVER}/api/instances/${instanceUniqueId}/tools/import/customers/sessions/${sessionId}/preview-records`,
          {
            ...headers,
            params: { page, pageSize, representativeFilter: 'all' },
          },
        ).then((res) => ({ page, records: res.data?.records || [] }))
      ));

      if (representativeFilterActivated) return;

      setPreview((current) => {
        // Never let a delayed response from an old import session populate a new one.
        if (!current || String(current.sessionId || '') !== String(sessionId)) return current;

        const currentRecords = current.records || [];
        const existingById = new Map(currentRecords.map((record) => [record.id, record]));
        const nextRecords = [...currentRecords];

        responses
          .sort((a, b) => a.page - b.page)
          .forEach(({ records: pageRecords }) => {
            pageRecords.forEach((record) => {
              if (existingById.has(record.id)) return;
              existingById.set(record.id, record);
              nextRecords.push(record);
            });
          });

        progressiveLoadedCountRef.current = nextRecords.length;

        return {
          ...current,
          records: nextRecords,
          progressive: true,
        };
      });
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Unable to load ready customer records.');
    } finally {
      progressiveFetchInFlightRef.current = false;
    }
  }, [headers, instanceUniqueId, livePreviewEnabled, preview?.records?.length, representativeFilterActivated]);

  useEffect(() => {
    if (
      selectedTool !== 'customers'
      || !livePreviewEnabled
      || representativeFilterActivated
      || !preview?.sessionId
      || !isProgressivePreview
    ) return;

    const readyNow = Number(previewProgress?.readyRecords || 0);
    const loadedNow = Math.max(
      Number(progressiveLoadedCountRef.current || 0),
      Number(preview?.records?.length || 0),
    );

    if (readyNow > loadedNow) {
      fetchProgressiveReadyRecords(preview.sessionId, readyNow);
    }
  }, [
    fetchProgressiveReadyRecords,
    isProgressivePreview,
    preview?.records?.length,
    preview?.sessionId,
    previewProgress?.readyRecords,
    representativeFilterActivated,
    selectedTool,
    livePreviewEnabled,
  ]);

  useEffect(() => {
    if (!livePreviewEnabled || selectedTool !== 'customers' || !previewProgress) return;

    // Rehydrate the lightweight metadata that was intentionally frozen while quiet
    // mode was active. The existing progressive catch-up effect then pulls only the
    // missing ready-record pages.
    setPreview((current) => {
      if (!current) return current;
      const next = {
        ...current,
        file: previewProgress.file || current.file,
        summary: previewProgress.summary || current.summary,
        commitEnabled: Boolean(previewProgress.complete),
        progressive: true,
      };
      if (Array.isArray(previewProgress.columns)) next.columns = previewProgress.columns;
      if (Array.isArray(previewProgress.mappings)) next.mappings = previewProgress.mappings;
      if (Array.isArray(previewProgress.additionalColumns)) next.additionalColumns = previewProgress.additionalColumns;
      if (Array.isArray(previewProgress.requiredFields)) next.requiredFields = previewProgress.requiredFields;
      if (previewProgress.headerSignature) next.headerSignature = previewProgress.headerSignature;
      if (Array.isArray(previewProgress.instanceUsers)) next.instanceUsers = previewProgress.instanceUsers;
      if (Array.isArray(previewProgress.sources)) next.sources = previewProgress.sources;
      if (Array.isArray(previewProgress.representativeMappings)) next.representativeMappings = previewProgress.representativeMappings;
      if (previewProgress.representativeDecisions) next.representativeDecisions = previewProgress.representativeDecisions;
      if (Array.isArray(previewProgress.bulkAssignmentRules)) next.bulkAssignmentRules = previewProgress.bulkAssignmentRules;
      if (previewProgress.assignmentSummary) next.assignmentSummary = previewProgress.assignmentSummary;
      return next;
    });
  }, [livePreviewEnabled, previewProgress, selectedTool]);

  useEffect(() => {
    if (!open) return;
    if (initialImportType === 'artwork') setSelectedTool('artwork');
    if (initialImportType === 'customers') setSelectedTool('customers');
    if (initialImportType === 'customer-terms') setSelectedTool('customer-terms');
    if (initialImportType === 'decorator-matrix') setSelectedTool('decorator-matrix');
    if (initialImportType === 'custom-suppliers') setSelectedTool('custom-suppliers');
  }, [initialImportType, open]);

  useEffect(() => {
    if (previewPage > previewTotalPages) setPreviewPage(previewTotalPages);
  }, [previewPage, previewTotalPages]);

  useEffect(() => {
    if (!uploadPhase) return undefined;
    const tick = () => {
      const startedAt = uploadPhase === 'processing' ? processingStartedAt : uploadStartedAt;
      setElapsedSeconds(startedAt ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000)) : 0);
    };
    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, [processingStartedAt, uploadPhase, uploadStartedAt]);

  useEffect(() => {
    const sessionId = preview?.sessionId;
    const shouldPoll = selectedTool === 'customers'
      && sessionId
      && previewProgress
      && !previewProgress.complete
      && !previewProgress.failed
      && !importing;
    if (!shouldPoll) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await axios.get(
          `${SERVER}/api/instances/${instanceUniqueId}/tools/import/customers/sessions/${sessionId}/progress`,
          headers,
        );
        if (cancelled) return;
        const progress = res.data || {};
        setPreviewProgress(progress);

        // Quiet mode deliberately keeps the large review surface frozen. We still
        // poll the lightweight progress endpoint so the user gets one reliable
        // "ready / total" counter, but we do not append records, rebuild filters,
        // avatars, assignment summaries, or customer cards until Live Preview is
        // turned back on.
        if (!livePreviewEnabled) {
          return;
        }

        // Representative mappings can continue arriving while progressive preview is
        // processing, but they are advisory. Never move the reviewer back to the mapping
        // stage from this polling loop after they have chosen to continue.
        setPreview((current) => {
          const next = {
            ...(current || {}),
            file: progress.file || current?.file,
            summary: progress.summary || current?.summary,
            commitEnabled: Boolean(progress.complete),
            progressive: true,
          };
          if (Array.isArray(progress.columns)) next.columns = progress.columns;
          if (Array.isArray(progress.mappings)) next.mappings = progress.mappings;
          if (Array.isArray(progress.additionalColumns)) next.additionalColumns = progress.additionalColumns;
          if (Array.isArray(progress.requiredFields)) next.requiredFields = progress.requiredFields;
          if (progress.headerSignature) next.headerSignature = progress.headerSignature;
          if (Array.isArray(progress.instanceUsers)) next.instanceUsers = progress.instanceUsers;
          if (Array.isArray(progress.sources)) next.sources = progress.sources;
          if (Array.isArray(progress.representativeMappings)) next.representativeMappings = progress.representativeMappings;
          if (progress.representativeDecisions) next.representativeDecisions = progress.representativeDecisions;
          if (Array.isArray(progress.bulkAssignmentRules)) next.bulkAssignmentRules = progress.bulkAssignmentRules;
          if (progress.assignmentSummary) next.assignmentSummary = progress.assignmentSummary;
          return next;
        });
        const readyNow = Number(progress.readyRecords || 0);

        // Before the reviewer clicks any representative filter, preserve the original
        // progressive experience: every ready customer is appended to the editable
        // review grid as processing advances.
        const loadedNow = Math.max(
          Number(progressiveLoadedCountRef.current || 0),
          Number(preview?.records?.length || 0),
        );
        if (!representativeFilterActivated && readyNow > loadedNow) {
          await fetchProgressiveReadyRecords(sessionId, readyNow);

          // Progressive preview can finish hydrating after the reviewer has already
          // advanced beyond Assign & Review. Keep loading newly-ready rows in the
          // background, but never let the polling loop navigate the user backwards.
          if (!cancelled && activeStep < 3) {
            setWorkflowStep(3);
          }

          previewFilterRefreshReadyRef.current = readyNow;
          return;
        }

        // Once any filter is explicitly activated (including All Customers), switch
        // to server-backed filtered pagination. While processing continues, silently
        // refresh the active page whenever more source records become ready.
        if (
          representativeFilterActivated
          && activeStep === 3
          && readyNow > previewFilterRefreshReadyRef.current
          && !previewPageLoading
        ) {
          previewFilterRefreshReadyRef.current = readyNow;
          const selectedUser = (progress.instanceUsers || preview?.instanceUsers || [])
            .find((user) => String(user.id) === String(representativeFilter));
          const label = representativeFilter === 'all'
            ? 'All Customers'
            : representativeFilter === 'unassigned'
              ? 'Unassigned'
              : representativeFilter === 'needs_review'
                ? 'Needs Review'
                : userLabel(selectedUser || {});
          await fetchPreviewRecordPage(
            sessionId,
            previewPage,
            representativeFilter,
            label,
            { loadingMode: 'silent' },
          );
        }
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || err.message || 'Unable to check preview progress.');
      }
    };
    poll();
    const intervalId = window.setInterval(poll, 1500);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [activeStep, fetchPreviewRecordPage, fetchProgressiveReadyRecords, headers, importing, instanceUniqueId, preview?.instanceUsers, preview?.records, preview?.sessionId, previewPage, previewPageLoading, previewProgress, representativeFilter, representativeFilterActivated, selectedTool, livePreviewEnabled]);

  const resetUploadProgress = () => {
    setUploadProgress(0);
    setUploadedBytes(0);
    setTotalUploadBytes(0);
    setUploadStartedAt(null);
    setProcessingStartedAt(null);
    setElapsedSeconds(0);
    setUploadPhase(null);
    setUploadContext(null);
  };

  const startUploadProgress = (context, totalBytes = 0) => {
    const now = Date.now();
    setUploadProgress(0);
    setUploadedBytes(0);
    setTotalUploadBytes(totalBytes || 0);
    setUploadStartedAt(now);
    setProcessingStartedAt(null);
    setElapsedSeconds(0);
    setUploadPhase('uploading');
    setUploadContext(context);
  };

  const updateUploadProgress = (event, fallbackTotal = 0) => {
    const total = event.total || fallbackTotal || totalUploadBytes || 0;
    const loaded = event.loaded || 0;
    if (total) setTotalUploadBytes(total);
    setUploadedBytes(loaded);
    if (total) {
      const nextProgress = Math.min(100, Math.round((loaded / total) * 100));
      setUploadProgress(nextProgress);
      if (nextProgress >= 100) {
        setUploadPhase('processing');
        setProcessingStartedAt((current) => current || Date.now());
      }
    }
  };

  const setWorkflowStep = (step) => {
    setActiveStep(step);
    setHighestReachedStep((current) => Math.max(current, step));
  };

  const navigateToStep = (targetStep, { allowAdvance = false } = {}) => {
    if (loading || importing) return;
    if (activeStep === 5 && targetStep !== 5) return;
    if (targetStep < 0 || targetStep >= activeSteps.length) return;
    const isNextStep = targetStep === activeStep + 1;
    if (targetStep > highestReachedStep && !(allowAdvance && isNextStep)) return;
    if (selectedTool === 'customers' && previewNeedsRefresh && targetStep > activeStep) return;
    setWorkflowStep(targetStep);
  };

  const resetWorkflow = () => {
    setActiveStep(0);
    setHighestReachedStep(0);
    setSelectedTool(null);
    setDestination('customers');
    setFile(null);
    setWorksheetName('');
    setMappingOverrides({});
    setPreview(null);
    setRecordActions({});
    setRecordActionKinds({});
    setRepresentativeDecisions({});
    setMatchDecisions({});
    setPreviewFilter('all');
    setPreviewPage(1);
    setImportSession(null);
    setDryRunResult(null);
    setDryRunInputKey(null);
    setDryRunning(false);
    setImporting(false);
    setAddingSource(false);
    setPendingSource(null);
    setPendingSourceFile(null);
    setSourceMappingOverrides({});
    setSourceWorksheet('');
    setUnmatchedPolicy('review');
    setReviewSource(null);
    setReplacingSource(null);
    setArtworkSession(null);
    setArtworkManifesting(false);
    setArtworkFolderLevel(1);
    setArtworkMappings({});
    setArtworkFilter('all');
    setLoading(false);
    setError('');
    setCloseConfirmOpen(false);
    setUnassignedContinueConfirmOpen(false);
    setAllowUnassignedImport(false);
    setNeedsReviewContinueConfirmOpen(false);
    setSkipUnresolvedMatches(false);
    setPreviewNeedsRefresh(false);
    setPreviewProgress(null);
    setPreviewPageLoading(false);
    setPreviewPageLoadingLabel('');
    setPreviewFilteredReadyCount(null);
    previewFilterRefreshReadyRef.current = 0;
    progressiveFetchInFlightRef.current = false;
    progressiveLoadedCountRef.current = 0;
    setRepresentativeReviewComplete(false);
    setImportMode('merge_missing');
    setRepresentativeFilter('all');
    setRepresentativeFilterActivated(false);
    setBulkAssignOpen(false);
    setBulkAssignUsers([]);
    setBulkAssignMode('add');
    setBulkAssigning(false);
    progressiveReadyRequestRef.current += 1;
    setLivePreviewEnabled(true);
    setReviewCustomer(null);
    setDryRunInspectRecord(null);
    setDryRunCopyStatus('');
    setDryRunResultFilter('blocked');
    setDryRunResultPage(1);
    setTestImportSize(10);
    setTestImportMode('mixed');
    setTestImportRunning(false);
    setTestImportResult(null);
    setTestImportInspectOpen(false);
    setTestImportInspectIndex(0);
    setDecoratorMatrixFile(null);
    setDecoratorMatrixPreview(null);
    setDecoratorMatrixDryRun(null);
    setDecoratorMatrixDryRunning(false);
    setDecoratorMatrixDryRunFilter('meaningful');
    setDecoratorMatrixDryRunPage(1);
    setDecoratorMatrixValuePreview(null);
    setDecoratorMatrixImporting(false);
    setDecoratorMatrixResult(null);
    setDecoratorMatrixPage(1);
    setCustomSupplierFile(null);
    setCustomSupplierPreview(null);
    setCustomSupplierMatchDecisions({});
    setCustomSupplierImporting(false);
    setCustomSupplierResult(null);
    setCustomSupplierPage(1);
    resetUploadProgress();
  };

  const selectTool = (toolKey) => {
    setSelectedTool(toolKey);
    if (toolKey === 'customer-terms') {
      history.push(`/instance/${instanceUniqueId}/settings/global-settings/tools/import/customer-terms`);
      return;
    }
    if (toolKey === 'artwork') {
      history.push(`/instance/${instanceUniqueId}/settings/global-settings/tools/import/artwork`);
      return;
    }
    if (toolKey === 'decorator-matrix') {
      history.push(`/instance/${instanceUniqueId}/settings/global-settings/tools/import/decorator-matrix`);
      return;
    }
    if (toolKey === 'custom-suppliers') {
      history.push(`/instance/${instanceUniqueId}/settings/global-settings/tools/import/custom-suppliers`);
      return;
    }
    history.push(`/instance/${instanceUniqueId}/settings/global-settings/tools/import/customers`);
  };

  const closeAndReset = () => {
    resetWorkflow();
    onClose();
  };

  const requestClose = () => {
    if (importing || decoratorMatrixImporting || customSupplierImporting) {
      setError('Import is currently processing. Please wait for it to finish before closing.');
      return;
    }
    if (hasProgress) {
      setCloseConfirmOpen(true);
      return;
    }
    closeAndReset();
  };

  const handleDialogClose = (_event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
  };

  const runPreview = async ({
    nextFile = file,
    nextWorksheetName = worksheetName,
    nextMappingOverrides = mappingOverrides,
    nextDestination = destination,
  } = {}) => {
    if (!nextFile) return;
    setLoading(true);
    setError('');
    startUploadProgress('initial', nextFile.size);
    try {
      const form = new FormData();
      form.append('file', nextFile);
      form.append('destination', nextDestination);
      form.append('importMode', importMode);
      if (nextWorksheetName) form.append('worksheetName', nextWorksheetName);
      form.append('mappingOverrides', JSON.stringify(nextMappingOverrides || {}));
      const res = await axios.post(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/customers/preview`,
        form,
        {
          ...headers,
          headers: {
            ...(headers.headers || {}),
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (event) => updateUploadProgress(event, nextFile.size),
        },
      );
      const nextPreview = res.data?.preview || null;
      const nextProgress = res.data?.progress || {
        status: res.data?.status || 'processing',
        readyRecords: nextPreview?.records?.length || 0,
        totalRecords: nextPreview?.summary?.proposedCustomers || 0,
        complete: Boolean(nextPreview?.commitEnabled),
        failed: false,
        summary: nextPreview?.summary,
      };
      setPreview(nextPreview);
      setImportMode(nextPreview?.importMode || 'merge_missing');
      setPreviewProgress(nextProgress);
      setImportSession(null);
      setRecordActions({});
      setRecordActionKinds({});
      setRepresentativeDecisions({});
      setMatchDecisions({});
      setRepresentativeReviewComplete(false);
      setAllowUnassignedImport(false);
      setSkipUnresolvedMatches(false);
      setNeedsReviewContinueConfirmOpen(false);
      setRepresentativeFilter('all');
      setRepresentativeFilterActivated(false);
      progressiveReadyRequestRef.current += 1;
      progressiveFetchInFlightRef.current = false;
      progressiveLoadedCountRef.current = Number(nextPreview?.records?.length || 0);
      setPreviewPage(1);
      const detectedWorksheet = res.data?.preview?.file?.detectedWorksheet || '';
      setWorksheetName(detectedWorksheet);
      setPreviewNeedsRefresh(false);
      setWorkflowStep(nextProgress.readyRecords > 0 ? 3 : 0);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || err.message || 'Unable to preview import.');
    } finally {
      setLoading(false);
      resetUploadProgress();
    }
  };

  const addCustomerSource = async (nextFile, applyMappings = false) => {
    if (!nextFile || !preview?.sessionId) return;
    const name = nextFile.name || '';
    if (!/\.(csv|xlsx|xls)$/i.test(name)) {
      setError('Choose a .csv, .xlsx, or .xls file.');
      return;
    }
    setAddingSource(true);
    setLoading(true);
    setError('');
    startUploadProgress('source', nextFile.size);
    try {
      const form = new FormData();
      form.append('file', nextFile);
      form.append('applyMappings', String(applyMappings));
      form.append('unmatchedPolicy', unmatchedPolicy);
      if (sourceWorksheet) form.append('worksheetName', sourceWorksheet);
      form.append('mappingOverrides', JSON.stringify(sourceMappingOverrides));
      if (applyMappings || pendingSourceFile === nextFile) form.append('replaceDuplicate', 'true');
      const res = await axios.post(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/customers/sessions/${preview.sessionId}/sources`,
        form,
        {
          ...headers,
          headers: {
            ...(headers.headers || {}),
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (event) => updateUploadProgress(event, nextFile.size),
        },
      );
      let nextPreview = res.data?.preview || null;
      if (applyMappings && replacingSource) {
        const removed = await axios.delete(
          `${SERVER}/api/instances/${instanceUniqueId}/tools/import/customers/sessions/${preview.sessionId}/sources/${replacingSource.id}`,
          headers,
        );
        nextPreview = removed.data?.preview || nextPreview;
        setReplacingSource(null);
      }
      setPreview(nextPreview);
      setRecordActions({});
      setRecordActionKinds({});
      setRepresentativeDecisions({});
      setRepresentativeReviewComplete(false);
      setAllowUnassignedImport(false);
      setSkipUnresolvedMatches(false);
      setNeedsReviewContinueConfirmOpen(false);
      setRepresentativeFilter('all');
      setRepresentativeFilterActivated(false);
      progressiveReadyRequestRef.current += 1;
      progressiveFetchInFlightRef.current = false;
      progressiveLoadedCountRef.current = Number(nextPreview?.records?.length || 0);
      setPreviewPage(1);
      setPreviewNeedsRefresh(false);
      if (applyMappings) {
        setPendingSource(null);
        setPendingSourceFile(null);
        setSourceMappingOverrides({});
        setSourceWorksheet('');
        setWorkflowStep(2);
      } else {
        const pending = res.data?.preview?.pendingSource || null;
        setPendingSource(pending);
        setPendingSourceFile(nextFile);
        setSourceWorksheet(pending?.worksheetName || '');
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || err.message || 'Unable to add source file.');
    } finally {
      setLoading(false);
      setAddingSource(false);
      resetUploadProgress();
    }
  };

  const downloadCustomerTemplate = async () => {
    setError('');
    try {
      const response = await axios.get(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/customers/template`,
        { ...headers, responseType: 'blob' },
      );
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'elite-core-customer-import-template.csv';
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to download the template.');
    }
  };

  const removeCustomerSource = async (source) => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.delete(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/customers/sessions/${preview.sessionId}/sources/${source.id}`,
        headers,
      );
      setPreview(response.data?.preview || null);
      setPreviewPage(1);
      if (reviewSource?.id === source.id) setReviewSource(null);
      return true;
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to remove this source.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleAdditionalSourceFile = async (nextFile) => {
    if (!nextFile) return;
    if (!/\.(csv|xlsx|xls)$/i.test(nextFile.name || '')) {
      resetUploadProgress();
      setError('Choose a .csv, .xlsx, or .xls file.');
      return;
    }
    await addCustomerSource(nextFile);
  };

  const resolveIdentity = async (item, action, candidateId = null) => {
    setLoading(true);
    setError('');
    try {
      const key = `${item.sourceId}:${item.record.id}`;
      const response = await axios.patch(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/customers/sessions/${preview.sessionId}/identity-decisions`,
        { decisions: { [key]: { action, candidateId } } },
        headers,
      );
      setPreview(response.data?.preview || null);
      setPreviewPage(1);
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to save this match decision.');
    } finally {
      setLoading(false);
    }
  };

  const updateSuggestionDecisions = async (decisions) => {
    if (!preview?.sessionId) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.patch(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/customers/sessions/${preview.sessionId}/suggestions`,
        { decisions },
        headers,
      );
      setPreview(res.data?.preview || null);
      setPreviewPage(1);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || err.message || 'Unable to update suggestions.');
    } finally {
      setLoading(false);
    }
  };

  const handleFile = (nextFile) => {
    if (!nextFile) return;
    const name = nextFile.name || '';
    if (!/\.(csv|xlsx|xls)$/i.test(name)) {
      resetUploadProgress();
      setError('Choose a .csv, .xlsx, or .xls file.');
      return;
    }
    resetUploadProgress();
    setFile(nextFile);
    setPreview(null);
    setMappingOverrides({});
    setRecordActions({});
    setRecordActionKinds({});
    setRepresentativeDecisions({});
    setRepresentativeReviewComplete(false);
    setImportSession(null);
    setHighestReachedStep(0);
    setPreviewNeedsRefresh(false);
    runPreview({ nextFile, nextMappingOverrides: {} });
  };

  const createArtworkSession = async (files) => {
    setArtworkManifesting(true);
    setLoading(true);
    setError('');
    try {
      const sourceName = files[0] ? getFileRelativePath(files[0]).split('/')[0] : 'Artwork library';
      const created = await axios.post(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/artwork/sessions`,
        { sourceName },
        headers,
      );
      let session = created.data?.session;
      const batchSize = 100;
      for (let idx = 0; idx < files.length; idx += batchSize) {
        const batch = files.slice(idx, idx + batchSize);
        const items = [];
        for (const selectedFile of batch) {
          items.push({
            clientItemId: `${idx}-${items.length}-${selectedFile.name}`,
            relativePath: getFileRelativePath(selectedFile),
            fileName: selectedFile.name,
            size: selectedFile.size,
            mimeType: selectedFile.type || null,
            lastModified: selectedFile.lastModified || null,
            checksum: await sha256File(selectedFile),
          });
        }
        const registered = await axios.post(
          `${SERVER}/api/instances/${instanceUniqueId}/tools/import/artwork/sessions/${session.id}/manifest`,
          { items },
          headers,
        );
        session = registered.data?.session;
        setArtworkSession(session);
      }
      const analyzed = await axios.post(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/artwork/sessions/${session.id}/analyze`,
        { customerFolderLevel: artworkFolderLevel },
        headers,
      );
      setArtworkSession(analyzed.data?.session || session);
      setWorkflowStep(1);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || err.message || 'Unable to ingest artwork folder.');
    } finally {
      setLoading(false);
      setArtworkManifesting(false);
    }
  };

  const handleArtworkFiles = async (files) => {
    const fileList = Array.from(files || []).filter((item) => item && item.size > 0);
    if (!fileList.length) {
      setError('Drop a folder or choose files to create an artwork manifest.');
      return;
    }
    await createArtworkSession(fileList);
  };

  const handleArtworkDrop = async (event) => {
    event.preventDefault();
    const entries = Array.from(event.dataTransfer.items || [])
      .map((item) => item.webkitGetAsEntry?.())
      .filter(Boolean);
    if (entries.length) {
      const nested = await Promise.all(entries.map((entry) => readEntryFiles(entry)));
      await handleArtworkFiles(nested.flat());
      return;
    }
    await handleArtworkFiles(event.dataTransfer.files);
  };

  const reanalyzeArtwork = async (level = artworkFolderLevel) => {
    if (!artworkSession?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/artwork/sessions/${artworkSession.id}/analyze`,
        { customerFolderLevel: level },
        headers,
      );
      setArtworkSession(res.data?.session || null);
      setArtworkFolderLevel(level);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Unable to analyze artwork.');
    } finally {
      setLoading(false);
    }
  };

  const updateArtworkMapping = async (folderKey, patch) => {
    const next = {
      ...artworkMappings,
      [folderKey]: {
        ...(artworkMappings[folderKey] || {}),
        ...patch,
      },
    };
    setArtworkMappings(next);
    if (!artworkSession?.id) return;
    const res = await axios.patch(
      `${SERVER}/api/instances/${instanceUniqueId}/tools/import/artwork/sessions/${artworkSession.id}/folder-mappings`,
      { mappings: next },
      headers,
    );
    setArtworkSession(res.data?.session || artworkSession);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    handleFile(event.dataTransfer.files?.[0]);
  };

  const handleMappingChange = (key, sourceColumn) => {
    const next = { ...mappingOverrides, [key]: sourceColumn };
    setMappingOverrides(next);
    setPreviewNeedsRefresh(true);
  };

  const handleFallbackMappingChange = (key, sourceColumn) => {
    const fallback = { ...(mappingOverrides.__fallback || {}), [key]: sourceColumn };
    setMappingOverrides({ ...mappingOverrides, __fallback: fallback });
    setPreviewNeedsRefresh(true);
  };

  const handleAdditionalColumnChange = (column, action, targetField = null) => {
    const additional = { ...(mappingOverrides.__additional || {}) };
    additional[column] = { action, targetField };
    const next = { ...mappingOverrides, __additional: additional };
    if (action === 'map_standard' && targetField) next[targetField] = column;
    setMappingOverrides(next);
    setPreviewNeedsRefresh(true);
  };

  const handleDestinationChange = (value) => {
    setDestination(value);
    if (file) setPreviewNeedsRefresh(true);
  };

  const handleWorksheetChange = (value) => {
    setWorksheetName(value);
    if (file) setPreviewNeedsRefresh(true);
  };

  const applyPreviewChanges = () => runPreview({
    nextWorksheetName: worksheetName,
    nextMappingOverrides: mappingOverrides,
    nextDestination: destination,
  });

  function defaultActionForRecord(record) {
    if (record.plannedAction === 'skip') return 'skip';
    if (record.plannedAction === 'needs_review') return 'import';
    if (record.plannedAction === 'merge') return 'update';
    if (record.plannedAction === 'create') return 'import';
    if (record.proposedAction === 'Conflict' || record.proposedAction === 'Missing required information') return 'skip';
    if (record.proposedAction.startsWith('Existing')) return 'update';
    return 'import';
  }

  const setRecordAction = (recordId, action) => {
    const record = records.find((item) => String(item.id) === String(recordId));
    if (record) {
      setRecordActionKinds((current) => ({
        ...current,
        [recordId]: record.plannedAction || (
          record.proposedAction?.startsWith('Existing') ? 'merge' : 'create'
        ),
      }));
    }
    setRecordActions((prev) => ({ ...prev, [recordId]: action }));
  };

  const changeImportMode = async (nextMode) => {
    if (nextMode === 'overwrite_existing' || nextMode === importMode) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.patch(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/customers/sessions/${preview.sessionId}/import-mode`,
        { importMode: nextMode },
        headers,
      );
      setImportMode(nextMode);
      setPreview((current) => ({
        ...current,
        importMode: nextMode,
        records: response.data?.records || (current?.records || []).map((record) => ({
          ...record,
          importMode: nextMode,
          plannedAction: record.plannedAction === 'needs_review'
            ? 'needs_review'
            : record.existingCustomer ? (nextMode === 'keep_existing' ? 'skip' : 'merge') : 'create',
        })),
      }));
      setRecordActions({});
      setRecordActionKinds({});
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to change import mode.');
    } finally {
      setLoading(false);
    }
  };

  const chooseCustomerMatch = (record, action, candidate = null) => {
    const decision = {
      action,
      ...(candidate?.id ? { customerId: candidate.id } : {}),
      wasDefaultCreate: record.defaultMatchDecision === 'create_new',
    };
    setMatchDecisions((current) => ({ ...current, [record.id]: decision }));
    if (action === 'skip') setRecordAction(record.id, 'skip');
    else setRecordAction(record.id, action === 'create_new' ? 'import' : (importMode === 'keep_existing' ? 'skip' : 'update'));
    setPreview((current) => ({
      ...current,
      records: (current?.records || []).map((item) => item.id !== record.id ? item : {
        ...item,
        existingCustomer: action === 'use_existing' ? { id: candidate.id, businessName: candidate.businessName, confidence: 'reviewed', matchType: 'reviewer_selected' } : null,
        plannedAction: action === 'skip' ? 'skip' : action === 'create_new' ? 'create' : importMode === 'keep_existing' ? 'skip' : 'merge',
        matchDecision: action,
      }),
    }));
  };

  const goToPreviewPage = (page) => {
    const nextPage = Math.min(previewTotalPages, Math.max(1, page));
    setPreviewPage(nextPage);
    if (isProgressivePreview && representativeFilterActivated && preview?.sessionId) {
      const selectedUser = (preview?.instanceUsers || []).find((user) => String(user.id) === String(representativeFilter));
      const label = representativeFilter === 'all'
        ? 'All Customers'
        : representativeFilter === 'unassigned'
          ? 'Unassigned'
          : representativeFilter === 'needs_review'
            ? 'Needs Review'
            : userLabel(selectedUser || {});
      fetchPreviewRecordPage(preview.sessionId, nextPage, representativeFilter, label, { loadingMode: 'blocking' });
    }
  };

  const applyRepresentativeFilter = (nextFilter) => {
    const normalizedFilter = String(nextFilter || 'all');
    progressiveReadyRequestRef.current += 1;
    progressiveFetchInFlightRef.current = false;
    setRepresentativeFilterActivated(true);
    setRepresentativeFilter(normalizedFilter);
    setPreviewPage(1);

    // Progressive imports keep polling in the background. Each click (including
    // re-clicking the active filter) re-queries all records that are ready so far.
    if (isProgressivePreview && preview?.sessionId) {
      const selectedUser = (preview?.instanceUsers || []).find((user) => String(user.id) === normalizedFilter);
      const label = normalizedFilter === 'all'
        ? 'All Customers'
        : normalizedFilter === 'unassigned'
          ? 'Unassigned'
          : normalizedFilter === 'needs_review'
            ? 'Needs Review'
            : userLabel(selectedUser || {});
      previewFilterRefreshReadyRef.current = Number(previewProgress?.readyRecords || 0);
      fetchPreviewRecordPage(preview.sessionId, 1, normalizedFilter, label, { loadingMode: 'blocking' });
      return;
    }

    setPreviewFilteredReadyCount(null);
  };


  const openBulkAssignment = () => {
    setBulkAssignUsers([]);
    setBulkAssignMode('add');
    setBulkAssignOpen(true);
  };

  const applyBulkAssignment = async () => {
    if (!preview?.sessionId || !bulkAssignUsers.length) return;
    setBulkAssigning(true);
    setError('');
    try {
      const res = await axios.patch(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/customers/sessions/${preview.sessionId}/bulk-assignments`,
        {
          filter: representativeFilter,
          userIds: bulkAssignUsers.map((user) => user.id),
          mode: bulkAssignMode,
        },
        headers,
      );

      setPreview((current) => ({
        ...(current || {}),
        assignmentSummary: res.data?.assignmentSummary || current?.assignmentSummary,
        bulkAssignmentRules: res.data?.bulkAssignmentRules || current?.bulkAssignmentRules || [],
      }));
      setPreviewProgress((current) => ({
        ...(current || {}),
        assignmentSummary: res.data?.assignmentSummary || current?.assignmentSummary,
        bulkAssignmentRules: res.data?.bulkAssignmentRules || current?.bulkAssignmentRules || [],
      }));
      setDryRunResult(null);
      setDryRunInputKey(null);
      setDryRunResultPage(1);
      setBulkAssignOpen(false);
      setBulkAssignUsers([]);

      const selectedUser = (preview?.instanceUsers || []).find(
        (user) => String(user.id) === String(representativeFilter)
      );
      const label = representativeFilter === 'all'
        ? 'All Customers'
        : representativeFilter === 'unassigned'
          ? 'Unassigned'
          : representativeFilter === 'needs_review'
            ? 'Needs Review'
            : userLabel(selectedUser || {});

      // The mass assignment can make records enter or leave the active filter.
      // Refresh page 1 immediately so counts and visible customers reflect the rule.
      setPreviewPage(1);
      await fetchPreviewRecordPage(
        preview.sessionId,
        1,
        representativeFilter,
        label,
        { loadingMode: 'blocking' },
      );
    } catch (err) {
      setError(
        err?.response?.data?.message
        || err.message
        || 'Unable to mass-assign customers.'
      );
    } finally {
      setBulkAssigning(false);
    }
  };



  const showCustomerImportError = (err, fallback) => {
    const response = err?.response;
    const data = response?.data || {};
    setError(data.message || data.error || err?.message || fallback);
    setImportErrorDetails({
      status: response?.status || null,
      code: data.error || null,
      details: data.details || null,
      message: data.message || err?.message || fallback,
    });
  };

  const copyCustomerImportError = () => {
    if (!importErrorDetails) return;
    const diagnostic = {
      status: importErrorDetails.status,
      code: importErrorDetails.code,
      message: importErrorDetails.message,
      details: importErrorDetails.details,
      sessionId: preview?.sessionId || null,
    };
    navigator.clipboard?.writeText(JSON.stringify(diagnostic, null, 2));
  };

  const continueToImportReview = () => {
    if (previewProgress?.complete && !preview?.commitEnabled) {
      setPreview((current) => current ? { ...current, commitEnabled: true } : current);
    }
    if (needsReviewCount > 0 && !skipUnresolvedMatches) {
      setNeedsReviewContinueConfirmOpen(true);
      return;
    }
    if (unassignedCount > 0 && !allowUnassignedImport) {
      setUnassignedContinueConfirmOpen(true);
      return;
    }
    navigateToStep(4, { allowAdvance: true });
  };

  const confirmSkipNeedsReviewAndContinue = () => {
    setSkipUnresolvedMatches(true);
    setDryRunResult(null);
    setDryRunInputKey(null);
    setNeedsReviewContinueConfirmOpen(false);
    if (unassignedCount > 0 && !allowUnassignedImport) {
      setUnassignedContinueConfirmOpen(true);
      return;
    }
    navigateToStep(4, { allowAdvance: true });
  };

  const confirmContinueWithUnassignedCustomers = () => {
    // This is a policy confirmation, not a bulk data mutation. Once the reviewer
    // explicitly allows unassigned imports, Dry Run/Commit receive
    // requireAssignments=false and unresolved representative mappings are treated
    // as intentionally unassigned. Avoid the old full-preview representative
    // decision PATCH here; on 15k imports that could hydrate every preview chunk
    // and make this button appear unresponsive.
    setAllowUnassignedImport(true);
    setDryRunResult(null);
    setDryRunInputKey(null);
    setUnassignedContinueConfirmOpen(false);
    navigateToStep(4, { allowAdvance: true });
  };

  const currentDryRunInputKey = JSON.stringify({
    sessionId: preview?.sessionId || null,
    recordActions,
    representativeDecisions,
    matchDecisions,
    importMode,
    requireAssignments: !allowUnassignedImport,
    skipUnresolvedMatches,
  });
  const dryRunIsCurrent = Boolean(
    dryRunResult?.fingerprint
      && dryRunInputKey === currentDryRunInputKey
  );

  const runDryRun = async () => {
    if (!preview?.sessionId) {
      setError('Preview session is missing. Refresh preview before running Dry Run.');
      return;
    }
    setDryRunning(true);
    setLoading(true);
    setError('');
    try {
      const inputKey = currentDryRunInputKey;
      const res = await axios.post(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/customers/sessions/${preview.sessionId}/dry-run`,
        {
          actions: recordActions,
          representativeDecisions,
          matchDecisions,
          importMode,
          requireAssignments: !allowUnassignedImport,
          skipUnresolvedMatches,
        },
        headers,
      );
      setDryRunResult(res.data?.dryRun || null);
      setDryRunInputKey(inputKey);
      setTestImportResult(null);
      setDryRunResultFilter((res.data?.dryRun?.summary?.blocked || 0) > 0 ? 'blocked' : 'all');
      setDryRunResultPage(1);
    } catch (err) {
      setDryRunResult(null);
      setDryRunInputKey(null);
      showCustomerImportError(err, 'Unable to run customer import simulation.');
    } finally {
      setLoading(false);
      setDryRunning(false);
    }
  };

  const runTestImport = async () => {
    if (!preview?.sessionId || !dryRunResult?.fingerprint) {
      setError('Run Dry Run before running Test Import.');
      return;
    }

    const successfulRows = (dryRunResult.records || []).filter((row) => {
      if (testImportMode === 'create') return row.action === 'create';
      if (testImportMode === 'merge') return row.action === 'merge';
      return ['create', 'merge'].includes(row.action);
    });
    const selectedRecordIds = successfulRows
      .slice(0, Number(testImportSize || 10))
      .map((row) => row.recordId);

    if (!selectedRecordIds.length) {
      setError('No successful Dry Run records match the selected Test Import sample.');
      return;
    }

    setTestImportRunning(true);
    setError('');
    try {
      const res = await axios.post(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/customers/sessions/${preview.sessionId}/test-import`,
        {
          actions: recordActions,
          representativeDecisions,
          matchDecisions,
          importMode,
          requireAssignments: !allowUnassignedImport,
          skipUnresolvedMatches,
          dryRunFingerprint: dryRunResult.fingerprint,
          recordIds: selectedRecordIds,
          sampleSize: Number(testImportSize || 10),
        },
        headers,
      );
      const testResult = res.data?.testImport || null;
      setTestImportResult(testResult);
      setTestImportInspectIndex(0);
      setTestImportInspectOpen(Boolean(testResult?.results?.length));
    } catch (err) {
      setTestImportResult(null);
      showCustomerImportError(err, 'Unable to run Test Import.');
    } finally {
      setTestImportRunning(false);
    }
  };

  const commitImport = async () => {
    if (!preview?.sessionId) {
      setError('Preview session is missing. Refresh preview before importing.');
      return;
    }
    setImporting(true);
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/customers/commit`,
        {
          sessionId: preview.sessionId,
          actions: recordActions,
          representativeDecisions,
          matchDecisions,
          importMode,
          requireAssignments: !allowUnassignedImport,
          skipUnresolvedMatches,
          dryRunFingerprint: dryRunResult?.fingerprint || null,
        },
        headers,
      );
      setImportSession(res.data?.session || null);
      setWorkflowStep(5);
    } catch (err) {
      showCustomerImportError(err, 'Unable to import customers.');
    } finally {
      setLoading(false);
      setImporting(false);
    }
  };

  const downloadCustomSupplierTemplate = async (format = 'flat') => {
    try {
      const related = format === 'related';
      const res = await axios.get(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/custom-suppliers/template`,
        { ...headers, params: related ? { format: 'related' } : {}, responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = related
        ? 'elite-core-supplier-related-rows-example.csv'
        : 'elite-core-custom-supplier-import-template.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Unable to download the supplier example.');
    }
  };

  const previewCustomSupplierFile = async (nextFile) => {
    if (!nextFile) return;
    if (!/\.(csv|xlsx|xls)$/i.test(nextFile.name || '')) {
      setError('Choose a CSV, XLS, or XLSX supplier file.');
      return;
    }
    setCustomSupplierFile(nextFile);
    setCustomSupplierMatchDecisions({});
    setAsiBatchReviewOpen(false);
    setAsiBatchSelectedIds([]);
    setCustomSupplierPreview(null);
    setCustomSupplierResult(null);
    setCustomSupplierPage(1);
    setLoading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', nextFile);
      const res = await axios.post(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/custom-suppliers/preview`,
        form,
        {
          ...headers,
          headers: {
            ...(headers.headers || {}),
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      setCustomSupplierPreview(res.data?.preview || null);
      setWorkflowStep(1);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Unable to preview custom suppliers.');
    } finally {
      setLoading(false);
    }
  };

  // Only propose exact, unambiguous ASI associations which have local details worth saving.
  // Existing manual decisions take precedence, and no database writes occur in this dialog.
  const asiBatchCandidates = (customSupplierPreview?.records || []).flatMap((record) => {
    if (customSupplierMatchDecisions[record.id]) return [];
    if (!(record.contacts?.length || record.addresses?.length)) return [];
    // Defaults are already in the preview; batch review is for unresolved candidates.
    if (['asi_local_default', 'asi_custom_default', 'asi_custom_unlinked_default'].includes(record.matchType)) return [];
    const proposal = record.action === 'blocked' && record.suggestedAssociation;
    if (proposal) {
      const asi = (record.matchCandidates || []).find(candidate =>
        String(candidate.Id) === String(proposal.asiSupplierId));
      if (!asi || asi.marketConflict) return [];
      return [{ record, supplier: asi, customSupplierId: proposal.customSupplierId,
        decision: { action: 'link_existing_custom_asi',
          supplierId: proposal.customSupplierId, asiSupplierId: proposal.asiSupplierId } }];
    }
    const supplier = record.action === 'asi_existing'
      ? record.existingSupplier
      : record.action === 'blocked' && record.matchCandidates?.length === 1
        ? record.matchCandidates[0]
        : null;
    if (!supplier || supplier.marketConflict ||
        !(hasAsiSupplierReference(supplier) || supplier.supplierSource === 'asi-live')) return [];
    return [{ record, supplier, decision: { action: 'connect_asi', supplierId: supplier.Id } }];
  });

  const applyAsiBatchReview = async () => {
    if (!customSupplierFile || loading) return;
    const selected = new Set(asiBatchSelectedIds);
    const nextDecisions = { ...customSupplierMatchDecisions };
    asiBatchCandidates.forEach(({ record, decision }) => {
      if (selected.has(record.id)) nextDecisions[record.id] = decision;
    });
    // A single preview request validates every proposed decision server-side.
    setLoading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', customSupplierFile);
      form.append('matchDecisions', JSON.stringify(nextDecisions));
      const res = await axios.post(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/custom-suppliers/preview`,
        form,
        { ...headers, headers: { ...(headers.headers || {}), 'Content-Type': 'multipart/form-data' } },
      );
      setCustomSupplierPreview(res.data?.preview || null);
      setCustomSupplierMatchDecisions(nextDecisions);
      setAsiBatchReviewOpen(false);
      setAsiBatchSelectedIds([]);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Unable to validate ASI associations. No suppliers were imported.');
    } finally {
      setLoading(false);
    }
  };

  const selectCustomSupplierMatch = async (id, decision) => {
    if (!customSupplierFile) return;
    const nextDecisions = { ...customSupplierMatchDecisions };
    if (decision) nextDecisions[id] = decision;
    else delete nextDecisions[id];
    setLoading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', customSupplierFile);
      form.append('matchDecisions', JSON.stringify(nextDecisions));
      const res = await axios.post(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/custom-suppliers/preview`,
        form,
        { ...headers, headers: { ...(headers.headers || {}), 'Content-Type': 'multipart/form-data' } },
      );
      setCustomSupplierPreview(res.data?.preview || null);
      setCustomSupplierMatchDecisions(nextDecisions);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Unable to resolve supplier match.');
    } finally {
      setLoading(false);
    }
  };

  const commitCustomSupplierImport = async (testCount = null) => {
    if (testCount !== null && selectedCustomSupplierIds.length !== testCount) return;
    if (!customSupplierFile || (testCount === null && !customSupplierPreview?.commitEnabled)) return;
    if (testCount !== null && ![1, 5].includes(testCount)) return;
    setCustomSupplierTestCount(testCount);
    setCustomSupplierImporting(true);
    setLoading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', customSupplierFile);
      form.append('matchDecisions', JSON.stringify(customSupplierMatchDecisions));
      if (testCount !== null) {
        form.append('testImportCount', String(testCount));
        form.append('selectedPreviewIds', JSON.stringify(selectedCustomSupplierIds));
      }
      const res = await axios.post(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/custom-suppliers/commit`,
        form,
        {
          ...headers,
          headers: {
            ...(headers.headers || {}),
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      setCustomSupplierResult(res.data?.result || null);
      setWorkflowStep(2);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Unable to import custom suppliers.');
    } finally {
      setLoading(false);
      setCustomSupplierImporting(false);
    }
  };

  const previewDecoratorMatrixFile = async (nextFile) => {
    if (!nextFile) return;
    if (!/\.xlsx$/i.test(nextFile.name || '')) {
      setError('Choose the .xlsx workbook generated by Decorator Matrix Export.');
      return;
    }
    setDecoratorMatrixFile(nextFile);
    setDecoratorMatrixPreview(null);
    setDecoratorMatrixDryRun(null);
    setDecoratorMatrixDryRunFilter('all');
    setDecoratorMatrixDryRunPage(1);
    setDecoratorMatrixResult(null);
    setDecoratorMatrixPage(1);
    setLoading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', nextFile);
      const res = await axios.post(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/decorator-matrix/preview`,
        form,
        {
          ...headers,
          headers: {
            ...(headers.headers || {}),
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      setDecoratorMatrixPreview(res.data?.preview || null);
      setWorkflowStep(1);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Unable to preview decoration matrix.');
    } finally {
      setLoading(false);
    }
  };


  const runDecoratorMatrixDryRun = async () => {
    if (!decoratorMatrixFile || !decoratorMatrixPreview?.fingerprint || !decoratorMatrixPreview?.commitEnabled) return;
    setDecoratorMatrixDryRunning(true);
    setLoading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', decoratorMatrixFile);
      form.append('fingerprint', decoratorMatrixPreview.fingerprint);
      form.append('archiveExisting', String(decoratorMatrixArchiveExisting));
      const res = await axios.post(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/decorator-matrix/dry-run`,
        form,
        {
          ...headers,
          headers: {
            ...(headers.headers || {}),
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      setDecoratorMatrixDryRun(res.data?.dryRun || null);
      setDecoratorMatrixDryRunFilter('meaningful');
      setDecoratorMatrixDryRunPage(1);
      setWorkflowStep(2);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Unable to run decoration matrix dry run.');
    } finally {
      setLoading(false);
      setDecoratorMatrixDryRunning(false);
    }
  };

  const commitDecoratorMatrixImport = async () => {
    if (!decoratorMatrixFile || !decoratorMatrixPreview?.fingerprint || !decoratorMatrixPreview?.commitEnabled) return;
    setDecoratorMatrixImporting(true);
    setLoading(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', decoratorMatrixFile);
      form.append('fingerprint', decoratorMatrixPreview.fingerprint);
      form.append('archiveExisting', String(decoratorMatrixArchiveExisting));
      const res = await axios.post(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/decorator-matrix/commit`,
        form,
        {
          ...headers,
          headers: {
            ...(headers.headers || {}),
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      setDecoratorMatrixResult(res.data?.result || null);
      setWorkflowStep(3);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Unable to import decoration matrix.');
    } finally {
      setLoading(false);
      setDecoratorMatrixImporting(false);
    }
  };

  const renderUploadProgress = (context) => {
    if (!uploadPhase || uploadContext !== context) return null;
    const isSource = context === 'source';
    const title = uploadPhase === 'processing'
      ? 'Upload complete'
      : isSource ? 'Uploading enrichment source...' : 'Uploading file';
    const detail = uploadPhase === 'processing'
      ? `${isSource ? 'Preparing enrichment source' : 'Preparing customer preview'}… ${elapsedSeconds}s`
      : `${uploadProgress}% · ${formatBytes(uploadedBytes)} / ${formatBytes(totalUploadBytes)} · ${elapsedSeconds}s`;
    return (
      <Paper variant="outlined" sx={{ p: 2, display: 'grid', gap: 1, backgroundColor: '#fff' }}>
        <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{detail}</Typography>
        <LinearProgress
          variant={uploadPhase === 'uploading' ? 'determinate' : 'indeterminate'}
          value={uploadPhase === 'uploading' ? uploadProgress : undefined}
        />
      </Paper>
    );
  };

  const renderUploadStep = () => (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Box>
        <Typography variant="h6" fontWeight={700}>Import Customers</Typography>
        <Typography variant="body2" color="text.secondary">
          Start with your main customer export. You can add additional spreadsheets afterward to fill in contacts, addresses and other customer information.
        </Typography>
      </Box>
      <Box
        onDragOver={(event) => event.preventDefault()}
        onDrop={handleDrop}
        sx={{
          border: '1px dashed #9aa7b1',
          borderRadius: 1,
          p: 3,
          backgroundColor: '#fff',
          textAlign: 'center',
        }}
      >
        <FileUploadOutlinedIcon color="primary" sx={{ fontSize: 42, mb: 1 }} />
        <Typography variant="subtitle1" fontWeight={600}>
          Drop your main customer file here
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          CSV, XLS and XLSX supported. Nothing is imported until you review and approve it.
        </Typography>
        <Button variant="contained" onClick={() => inputRef.current?.click()} sx={{ textTransform: 'none' }}>
          Choose Customer File
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          hidden
          onChange={(event) => handleFile(event.target.files?.[0])}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '260px 1fr' }, gap: 2 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Import as</InputLabel>
          <Select
            label="Import as"
            value={destination}
            onChange={(event) => handleDestinationChange(event.target.value)}
          >
            <MenuItem value="customers">Customers</MenuItem>
            <MenuItem value="leads">Leads</MenuItem>
          </Select>
        </FormControl>
        <Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={downloadCustomerTemplate}
              sx={{ textTransform: 'none' }}
            >
              Download Example File
            </Button>
            <Button
              variant="text"
              onClick={() => setTemplateHelpOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              How does this work?
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Use our example as a guide, or upload your existing customer spreadsheet and review the detected fields.
          </Typography>
        </Box>
      </Box>

      {file && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={600}>{file.name}</Typography>
          <Typography variant="body2" color="text.secondary">{formatBytes(file.size)}</Typography>
          {previewFile?.rowCount != null && previewFile?.columnCount != null ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              <Chip size="small" label={`${Number(previewFile.rowCount || 0).toLocaleString()} rows`} />
              <Chip size="small" label={`${Number(previewFile.columnCount || 0).toLocaleString()} columns`} />
              {previewFile.detectedWorksheet && <Chip size="small" label={`Worksheet: ${previewFile.detectedWorksheet}`} />}
            </Box>
          ) : preview?.sessionId ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Analyzing file…
            </Typography>
          ) : null}
        </Paper>
      )}
      {renderUploadProgress('initial')}
      {preview?.sessionId && previewProgress && !previewProgress.complete && !previewProgress.failed && (
        <Alert severity="info">
          <strong>{progressivePhaseLabel(previewProgress.phase)}…</strong>{' '}
          {previewProgress.phase === 'matching'
            ? `Matching ${(previewProgress.totalRecords || 0).toLocaleString()} customers against existing Elite Core customers…`
            : previewProgress.readyRecords > 0
              ? `${(previewProgress.readyRecords || 0).toLocaleString()} of ${(previewProgress.totalRecords || 0).toLocaleString()} customers ready.`
              : 'Elite Core is preparing the customer preview.'}
        </Alert>
      )}
      {preview?.sessionId && previewProgress?.complete && (
        <Alert severity="success">
          Preview complete — {(previewProgress.totalRecords || 0).toLocaleString()} customers.
        </Alert>
      )}

      {preview?.sessionId && (
        <Paper variant="outlined" sx={{ p: 2, display: 'grid', gap: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>Add another customer file</Typography>
              <Typography variant="body2" color="text.secondary">
                Have another spreadsheet with contacts, addresses or other customer details? Elite Core will match it against the customers already assembled from your primary file and fill in useful missing information.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This does not replace your primary file. Unmatched rows can be reviewed, ignored, or created as new candidates.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={() => { setReplacingSource(null); sourceInputRef.current?.click(); }}
              disabled={loading || addingSource}
              sx={{ textTransform: 'none', alignSelf: 'start' }}
            >
              Add Another Source
            </Button>
            <input
              ref={sourceInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              hidden
              onChange={(event) => {
                const selectedFile = event.target.files?.[0];
                event.target.value = '';
                handleAdditionalSourceFile(selectedFile);
              }}
            />
          </Box>
          {renderUploadProgress('source')}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 1 }}>
            {(sources.length ? sources : [{
              id: 'current',
              sequence: 1,
              fileName: previewFile?.name || file?.name || 'Primary source',
              rowCount: previewFile?.rowCount,
              columnCount: previewFile?.columnCount,
              candidateCount: totalPreviewRecordCount,
              readyCount: readyRecordCount,
              status: previewProgress?.complete ? 'processed' : 'processing',
              worksheetName: previewFile?.detectedWorksheet,
            }]).map((source) => (
              <Paper key={source.id} variant="outlined" sx={{ p: 1.5, backgroundColor: '#fff' }}>
                <Typography variant="body2" fontWeight={600}>{source.fileName}</Typography>
                <Chip size="small" color={source.sequence === 1 ? 'primary' : 'default'} label={source.sequence === 1 ? 'Primary source' : 'Enrichment source'} sx={{ my: 0.75 }} />
                <Typography variant="caption" color="text.secondary" display="block">
                  {source.rowCount != null ? `${Number(source.rowCount || 0).toLocaleString()} rows` : 'Analyzing file…'}
                </Typography>
                {source.worksheetName && <Typography variant="caption" color="text.secondary" display="block">Worksheet: {source.worksheetName}</Typography>}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                  <Chip size="small" label={`${Number(source.candidateCount || 0).toLocaleString()} customer candidates`} />
                  {source.sequence === 1 && source.status === 'processing' && <Chip size="small" color="info" label={`${Number(source.readyCount || 0).toLocaleString()} ready`} />}
                  {source.sequence > 1 && <Chip size="small" color="info" label={`${(source.matchedCount || 0).toLocaleString()} matched`} />}
                  {source.sequence > 1 && <Chip size="small" color="success" label={`${(source.usefulAdditionCount || 0).toLocaleString()} useful additions`} />}
                  {source.reviewCount > 0 && <Chip size="small" color="warning" label={`${source.reviewCount} need review`} />}
                </Box>
                {source.sequence === 1 && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Confirm how this file maps to Elite Core while processing continues.
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                  {source.sequence === 1 ? (
                    <Button size="small" onClick={() => navigateToStep(1, { allowAdvance: true })} sx={{ textTransform: 'none' }}>Confirm Field Mappings</Button>
                  ) : (
                    <Button size="small" onClick={() => setReviewSource(source)} sx={{ textTransform: 'none' }}>Review mappings</Button>
                  )}
                  {source.sequence > 1 && <Button size="small" onClick={() => { setReplacingSource(source); sourceInputRef.current?.click(); }} sx={{ textTransform: 'none' }}>Replace source</Button>}
                  {source.sequence > 1 && <Button size="small" color="error" onClick={() => removeCustomerSource(source)} sx={{ textTransform: 'none' }}>Remove</Button>}
                </Box>
              </Paper>
            ))}
          </Box>
          {reviewSource && !pendingSource && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>Mappings for {reviewSource.fileName}</Typography>
                  <Typography variant="caption" color="text.secondary">This is the source currently being reviewed.</Typography>
                </Box>
                <Button size="small" onClick={() => setReviewSource(null)}>Close</Button>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 1, mt: 1.5 }}>
                {(reviewSource.mappings || []).filter((mapping) => mapping.sourceColumn).map((mapping) => (
                  <Box key={mapping.key}>
                    <Typography variant="caption" color="text.secondary">{mapping.label}</Typography>
                    <Typography variant="body2">{mapping.sourceColumn} · {mapping.status}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
          {pendingSource && (
            <Paper variant="outlined" sx={{ p: 2, display: 'grid', gap: 1.5 }}>
              <Alert severity="info">
                {pendingSource.mappingConfidence === 'high'
                  ? `${pendingSource.mappings.filter((mapping) => mapping.sourceColumn).length} fields mapped automatically — review mappings before applying.`
                  : 'Review this source mapping before it is matched to customer candidates.'}
              </Alert>
              {(pendingSource.worksheets || []).length > 1 && (
                <FormControl size="small" fullWidth>
                  <InputLabel>Source worksheet</InputLabel>
                  <Select label="Source worksheet" value={sourceWorksheet} onChange={(event) => setSourceWorksheet(event.target.value)}>
                    {pendingSource.worksheets.map((sheet) => (
                      <MenuItem key={sheet.name} value={sheet.name}>{sheet.name} ({sheet.rowCount} rows)</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl size="small" fullWidth>
                <InputLabel>Unmatched rows</InputLabel>
                <Select label="Unmatched rows" value={unmatchedPolicy} onChange={(event) => setUnmatchedPolicy(event.target.value)}>
                  <MenuItem value="review">Review unmatched rows</MenuItem>
                  <MenuItem value="create">Create new candidate customers</MenuItem>
                  <MenuItem value="ignore">Ignore unmatched rows</MenuItem>
                </Select>
              </FormControl>
              <Table size="small">
                <TableBody>
                  {(pendingSource.mappings || []).map((mapping) => (
                    <TableRow key={mapping.key}>
                      <TableCell>{mapping.label}</TableCell>
                      <TableCell>
                        <Select
                          fullWidth
                          size="small"
                          value={sourceMappingOverrides[mapping.key] ?? mapping.sourceColumn ?? ''}
                          onChange={(event) => setSourceMappingOverrides((current) => ({ ...current, [mapping.key]: event.target.value }))}
                        >
                          <MenuItem value="">Unmapped</MenuItem>
                          {(pendingSource.detectedColumns || []).map((column) => <MenuItem key={column} value={column}>{column}</MenuItem>)}
                        </Select>
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        {['emailAddress', 'phoneNumber'].includes(mapping.key) ? (
                          <Select
                            fullWidth
                            size="small"
                            displayEmpty
                            value={sourceMappingOverrides.__fallback?.[mapping.key] ?? mapping.fallbackSourceColumn ?? ''}
                            onChange={(event) => setSourceMappingOverrides((current) => ({
                              ...current,
                              __fallback: {
                                ...(current.__fallback || {}),
                                [mapping.key]: event.target.value,
                              },
                            }))}
                          >
                            <MenuItem value="">No fallback</MenuItem>
                            {(pendingSource.detectedColumns || []).map((column) => <MenuItem key={column} value={column}>{column}</MenuItem>)}
                          </Select>
                        ) : '—'}
                      </TableCell>
                      <TableCell><Chip size="small" label={mapping.confidence || mapping.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {(pendingSource.additionalColumns || []).length > 0 && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>Additional columns</Typography>
                  <Typography variant="caption" color="text.secondary">Choose whether to keep unrecognized information with the import review.</Typography>
                  {(pendingSource.additionalColumns || []).map((column) => (
                    <Box key={column.sourceColumn} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
                      <Box>
                        <Typography variant="body2">{column.sourceColumn}</Typography>
                        <Typography variant="caption" color="text.secondary">{column.sampleValue || '—'}</Typography>
                      </Box>
                      <Select
                        size="small"
                        value={sourceMappingOverrides.__additional?.[column.sourceColumn]?.action || 'preserve_metadata'}
                        onChange={(event) => setSourceMappingOverrides((current) => ({
                          ...current,
                          __additional: {
                            ...(current.__additional || {}),
                            [column.sourceColumn]: { action: event.target.value },
                          },
                        }))}
                      >
                        <MenuItem value="preserve_metadata">Preserve with import details</MenuItem>
                        <MenuItem value="ignore">Ignore</MenuItem>
                      </Select>
                    </Box>
                  ))}
                </Box>
              )}
              <Button
                variant="contained"
                disabled={loading}
                onClick={() => addCustomerSource(pendingSourceFile, sourceWorksheet === (pendingSource.worksheetName || ''))}
              >
                {sourceWorksheet === (pendingSource.worksheetName || '') ? 'Apply Mapping & Match Source' : 'Preview Worksheet Mapping'}
              </Button>
            </Paper>
          )}
        </Paper>
      )}

      {worksheetOptions.length > 1 && (
        <FormControl fullWidth size="small">
          <InputLabel>Worksheet</InputLabel>
          <Select
            label="Worksheet"
            value={worksheetName}
            onChange={(event) => handleWorksheetChange(event.target.value)}
          >
            {worksheetOptions.map((sheet) => (
              <MenuItem key={sheet.name} value={sheet.name}>
                {sheet.name} ({sheet.rowCount} rows, {sheet.columnCount} columns)
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {previewNeedsRefresh && (
        <Alert
          severity="warning"
          action={(
            <Button color="inherit" size="small" disabled={loading} onClick={applyPreviewChanges} sx={{ textTransform: 'none' }}>
              Apply Changes
            </Button>
          )}
        >
          Import settings changed — preview needs refresh.
        </Alert>
      )}
    </Box>
  );

  const renderMappingStep = () => (
    <Box sx={{ display: 'grid', gap: 2 }}>
      {isProgressivePreview && previewProgress && !previewProgress.complete && !previewProgress.failed && (
        <Alert
          severity="info"
          action={(
            <Button color="inherit" size="small" onClick={() => navigateToStep(0)} sx={{ textTransform: 'none' }}>
              View Processing Progress
            </Button>
          )}
        >
          Customer preview continues in the background — {(previewProgress.readyRecords || 0).toLocaleString()} of {(previewProgress.totalRecords || 0).toLocaleString()} customers ready.
        </Alert>
      )}
      <Alert severity="info">
        Review detected fields before previewing actions. Business Name is required; other fields can remain unmapped.
      </Alert>
      {previewNeedsRefresh && (
        <Alert
          severity="warning"
          action={(
            <Button
              color="inherit"
              size="small"
              disabled={loading}
              onClick={applyPreviewChanges}
              sx={{ textTransform: 'none' }}
            >
              Apply Changes
            </Button>
          )}
        >
          Mapping changed — preview needs refresh.
        </Alert>
      )}
      {!(preview?.mappings || []).length && !columns.length && (
        <Alert severity="info">
          Detecting spreadsheet fields… Elite Core is still reading the file. Field mappings will appear here shortly.
        </Alert>
      )}
      {!(preview?.mappings || []).length && columns.length > 0 && (
        <Alert
          severity="warning"
          action={(
            <Button color="inherit" size="small" onClick={() => navigateToStep(0)} sx={{ textTransform: 'none' }}>
              Return to Upload Sources
            </Button>
          )}
        >
          No field mappings were generated for this source.
        </Alert>
      )}
      {(preview?.mappings || []).length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Target Field</TableCell>
              <TableCell>Source Column</TableCell>
              <TableCell>Fallback Source</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Sample Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(preview?.mappings || []).map((mapping) => (
              <TableRow key={mapping.key}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{mapping.label}</Typography>
                  {mapping.required && <Typography variant="caption" color="error">Required</Typography>}
                </TableCell>
                <TableCell sx={{ minWidth: 220 }}>
                  <FormControl fullWidth size="small">
                    <Select
                      displayEmpty
                      value={mappingOverrides[mapping.key] ?? mapping.sourceColumn ?? ''}
                      onChange={(event) => handleMappingChange(mapping.key, event.target.value)}
                    >
                      <MenuItem value="">Unmapped</MenuItem>
                      {columns.map((column) => (
                        <MenuItem key={column} value={column}>{column}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell sx={{ minWidth: 220 }}>
                  {['emailAddress', 'phoneNumber'].includes(mapping.key) ? (
                    <Box sx={{ display: 'grid', gap: .35 }}>
                      <FormControl fullWidth size="small">
                        <Select
                          displayEmpty
                          value={mappingOverrides.__fallback?.[mapping.key] ?? mapping.fallbackSourceColumn ?? ''}
                          onChange={(event) => handleFallbackMappingChange(mapping.key, event.target.value)}
                        >
                          <MenuItem value="">No fallback</MenuItem>
                          {columns.map((column) => (
                            <MenuItem key={column} value={column}>{column}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography variant="caption" color="text.secondary">
                        Used only when the normal {mapping.key === 'emailAddress' ? 'email' : 'phone'} is blank.
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.disabled">—</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={mapping.status}
                    color={mapping.status === 'Detected' || mapping.status === 'Manual' ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 260 }}>
                    {mapping.sampleValue || '-'}
                  </Typography>
                  {['emailAddress', 'phoneNumber'].includes(mapping.key) && mapping.fallbackSampleValue && (
                    <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ maxWidth: 260 }}>
                      Fallback: {mapping.fallbackSampleValue}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {(preview?.additionalColumns || []).length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={700}>Additional columns</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            These columns were not recognized as standard customer fields. Keep them with the import for reference, ignore them, or map them yourself.
          </Typography>
          {(preview.additionalColumns || []).map((column) => {
            const choice = mappingOverrides.__additional?.[column.sourceColumn] || column;
            return (
              <Box key={column.sourceColumn} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 1, alignItems: 'center', py: 0.75 }}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{column.sourceColumn}</Typography>
                  <Typography variant="caption" color="text.secondary">Example: {column.sampleValue || '—'}</Typography>
                </Box>
                <Select
                  size="small"
                  value={choice.action || 'preserve_metadata'}
                  onChange={(event) => handleAdditionalColumnChange(column.sourceColumn, event.target.value, choice.targetField)}
                >
                  <MenuItem value="preserve_metadata">Preserve with import details</MenuItem>
                  <MenuItem value="ignore">Ignore</MenuItem>
                  <MenuItem value="map_standard">Map to a standard customer field</MenuItem>
                  <MenuItem value="map_custom" disabled>Map to customer custom field (not available)</MenuItem>
                  <MenuItem value="create_custom" disabled>Create customer custom field (not available)</MenuItem>
                </Select>
                {choice.action === 'map_standard' && (
                  <Select
                    size="small"
                    displayEmpty
                    value={choice.targetField || ''}
                    onChange={(event) => handleAdditionalColumnChange(column.sourceColumn, 'map_standard', event.target.value)}
                  >
                    <MenuItem value="">Choose customer field</MenuItem>
                    {(preview.mappings || []).map((mapping) => <MenuItem key={mapping.key} value={mapping.key}>{mapping.label}</MenuItem>)}
                  </Select>
                )}
              </Box>
            );
          })}
          <Typography variant="caption" color="text.secondary">
            Customer custom fields are not currently available in the customer data model, so this importer does not create a separate custom-field system.
          </Typography>
        </Paper>
      )}
    </Box>
  );

  const userLabel = (user) => [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || (user?.id ? `User #${user.id}` : '');

  const representativeOptions = (resolution) => {
    const byId = new Map();
    [resolution.matchedUser, ...(resolution.possibleMatches || [])].filter(Boolean).forEach((user) => byId.set(String(user.id), user));
    (preview?.instanceUsers || []).forEach((user) => byId.set(String(user.id), user));
    const exactId = resolution.matchedUser?.id ? String(resolution.matchedUser.id) : null;
    const suggestedIds = new Set((resolution.possibleMatches || []).map((user) => String(user.id)));
    const users = [...byId.values()].sort((a, b) => {
      const score = (user) => {
        if (exactId && String(user.id) === exactId) return 0;
        if (suggestedIds.has(String(user.id))) return 1;
        if (user.role === resolution.requestedRole) return 2;
        return 3;
      };
      const scoreA = score(a);
      const scoreB = score(b);
      if (scoreA !== scoreB) return scoreA - scoreB;
      return userLabel(a).localeCompare(userLabel(b));
    });
    return [
      { kind: 'empty', label: 'Choose user' },
      ...users.map((user) => ({ kind: 'user', user, label: `${userLabel(user)}${user.role ? ` · ${user.role}` : ''}` })),
      { kind: 'unassigned', label: 'Leave unassigned' },
    ];
  };

  const setRepresentativeSourceDecision = (field, sourceValue, decision) => {
    const key = `source:${field}:${canon(sourceValue)}`;
    setRepresentativeDecisions((prev) => ({ ...prev, [key]: decision }));
    setPreview((current) => ({
      ...(current || {}),
      representativeDecisions: {
        ...(current?.representativeDecisions || {}),
        [key]: decision,
      },
      representativeMappings: (current?.representativeMappings || []).map((mapping) => (
        mapping.field === field && canon(mapping.sourceValue) === canon(sourceValue)
          ? { ...mapping, decision, status: decision.action === 'leave_unassigned' ? 'resolved_unassigned' : 'matched' }
          : mapping
      )),
    }));
    if (preview?.sessionId) {
      axios.patch(
        `${SERVER}/api/instances/${instanceUniqueId}/tools/import/customers/sessions/${preview.sessionId}/representative-decisions`,
        { decisions: { [key]: decision } },
        headers,
      ).then((res) => {
        setPreview((current) => ({
          ...(current || {}),
          representativeMappings: res.data?.representativeMappings || current?.representativeMappings || [],
          representativeDecisions: res.data?.representativeDecisions || current?.representativeDecisions || {},
          assignmentSummary: res.data?.assignmentSummary || current?.assignmentSummary,
        }));
        setPreviewProgress((current) => ({
          ...(current || {}),
          assignmentSummary: res.data?.assignmentSummary || current?.assignmentSummary,
        }));
      }).catch((err) => setError(err?.response?.data?.message || err.message || 'Unable to save representative mapping.'));
    }
  };

  const setRecordAssignmentRemoved = (record, user, removed = true) => {
    if (!record?.id || !user?.id) return;
    const key = `${record.id}:remove:${user.id}`;
    const decision = removed
      ? { action: 'remove_user', userId: user.id }
      : { action: 'keep_user', userId: user.id };

    setRepresentativeDecisions((current) => ({ ...current, [key]: decision }));
    setDryRunResult(null);
    setDryRunInputKey(null);
    setDryRunResultPage(1);

    if (!preview?.sessionId) return;

    axios.patch(
      `${SERVER}/api/instances/${instanceUniqueId}/tools/import/customers/sessions/${preview.sessionId}/representative-decisions`,
      { decisions: { [key]: decision } },
      headers,
    ).then((res) => {
      const nextSummary = res.data?.assignmentSummary || null;
      setPreview((current) => ({
        ...(current || {}),
        representativeMappings: res.data?.representativeMappings || current?.representativeMappings || [],
        representativeDecisions: res.data?.representativeDecisions || current?.representativeDecisions || {},
        assignmentSummary: nextSummary || current?.assignmentSummary,
      }));
      setPreviewProgress((current) => ({
        ...(current || {}),
        assignmentSummary: nextSummary || current?.assignmentSummary,
      }));

      if (representativeFilterActivated && nextSummary) {
        if (representativeFilter === 'unassigned') {
          setPreviewFilteredReadyCount(Number(nextSummary.unassignedCount || 0));
        } else if (!['all', 'needs_review'].includes(representativeFilter)) {
          const nextCount = (nextSummary.byUser || []).find(
            (item) => String(item.userId) === String(representativeFilter)
          )?.customerCount;
          setPreviewFilteredReadyCount(Number(nextCount || 0));
        }
      }
    }).catch((err) => {
      setRepresentativeDecisions((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
      setError(err?.response?.data?.message || err.message || 'Unable to update customer assignment.');
    });
  };

  const representativeMappingDecision = (mapping) => {
    const key = `source:${mapping.field}:${canon(mapping.sourceValue)}`;
    return representativeDecisions[key] || preview?.representativeDecisions?.[key] || mapping.decision || null;
  };

  const representativeMappingResolved = (mapping) => {
    const decision = representativeMappingDecision(mapping);
    return Boolean(
      decision?.action === 'leave_unassigned'
      || decision?.userId
      || (decision?.action === 'assign_group' && (decision.assignments || []).length > 0)
      || (mapping.status === 'matched' && mapping.matchedUser?.id)
    );
  };

  const openRepresentativeGroup = (mapping) => {
    const decision = representativeMappingDecision(mapping);
    const existing = decision?.action === 'assign_group' && Array.isArray(decision.assignments)
      ? decision.assignments
      : [];
    setRepresentativeGroupTarget(mapping);
    setRepresentativeGroupDraft(existing.map((assignment) => ({
      userId: String(assignment.userId || ''),
      role: assignment.role || mapping.requestedRole || 'client-rep',
    })));
  };

  const closeRepresentativeGroup = () => {
    setRepresentativeGroupTarget(null);
    setRepresentativeGroupDraft([]);
  };

  const addRepresentativeGroupMember = () => {
    setRepresentativeGroupDraft((current) => [
      ...current,
      { userId: '', role: representativeGroupTarget?.requestedRole || 'client-rep' },
    ]);
  };

  const saveRepresentativeGroup = () => {
    if (!representativeGroupTarget) return;
    const assignments = representativeGroupDraft
      .filter((assignment) => assignment.userId)
      .map((assignment) => ({
        userId: Number(assignment.userId),
        role: assignment.role || representativeGroupTarget.requestedRole || 'client-rep',
      }));
    if (!assignments.length) {
      setError('Choose at least one Elite Core user for this representative group.');
      return;
    }
    setRepresentativeSourceDecision(
      representativeGroupTarget.field,
      representativeGroupTarget.sourceValue,
      { action: 'assign_group', assignments },
    );
    closeRepresentativeGroup();
  };

  const confirmPrefilledRepresentative = (mapping, option, decision) => {
    if (decision || option?.kind !== 'user' || !mapping?.matchedUser?.id) return;
    if (String(option.user?.id || '') !== String(mapping.matchedUser.id)) return;
    if (mapping.status === 'matched') return;
    setRepresentativeSourceDecision(
      mapping.field,
      mapping.sourceValue,
      { action: 'select_user', userId: option.user.id },
    );
  };

  const renderRepresentativeMappingStage = () => {
    const manuallyMapped = representativeMappings.filter((mapping) => {
      const decision = representativeMappingDecision(mapping);
      return decision?.userId || decision?.action === 'leave_unassigned' || decision?.action === 'assign_group';
    }).length;
    const autoMatched = representativeMappings.filter((mapping) => !representativeMappingDecision(mapping) && mapping.status === 'matched' && mapping.matchedUser?.id).length;
    const unresolved = representativeMappings.filter((mapping) => !representativeMappingResolved(mapping)).length;
    return (
      <Paper variant="outlined" sx={{ p: 2, display: 'grid', gap: 1.5 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Representative Mapping</Typography>
          <Typography variant="body2" color="text.secondary">
            Elite Core found representative names in your source file. Confirm how they map to users in this instance once, and those assignments will be applied to every matching customer below.
          </Typography>
          {isProgressivePreview && !previewProgress?.complete && (
            <Typography variant="caption" color="text.secondary">
              Representative values may continue to appear while the preview is processing.
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip label={`${representativeMappings.length} representative values`} />
          <Chip color="success" label={`${autoMatched} matched automatically`} />
          <Chip color="info" label={`${manuallyMapped} manually mapped`} />
          <Chip color={unresolved ? 'warning' : 'success'} label={unresolved ? `${unresolved} requires review` : 'Representative mappings complete'} />
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Source representative</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Used on</TableCell>
              <TableCell>Elite Core user</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {representativeMappings.map((mapping) => {
              const decision = representativeMappingDecision(mapping);
              const options = representativeOptions(mapping);
              const selected = decision?.action === 'leave_unassigned'
                ? options.find((option) => option.kind === 'unassigned') || null
                : decision?.userId
                  ? options.find((option) => option.kind === 'user' && String(option.user.id) === String(decision.userId)) || null
                  : mapping.matchedUser?.id
                    ? options.find((option) => option.kind === 'user' && String(option.user.id) === String(mapping.matchedUser.id)) || null
                    : options.find((option) => option.kind === 'empty') || null;
              const resolved = representativeMappingResolved(mapping);
              return (
                <TableRow key={`${mapping.field}:${mapping.sourceValue}`}>
                  <TableCell>
                    <Box sx={{ position: 'relative', pr: 4, minWidth: 150 }}>
                      <Typography variant="body2" fontWeight={600}>{mapping.sourceValue}</Typography>
                      <IconButton
                        size="small"
                        onClick={() => openRepresentativeGroup(mapping)}
                        title="Advanced assignment"
                        sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' }}
                      >
                        <SettingsOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>{mapping.label}</TableCell>
                  <TableCell>{Number(mapping.occurrenceCount || 0).toLocaleString()}</TableCell>
                  <TableCell sx={{ minWidth: 280 }}>
                    <Autocomplete
                      size="small"
                      disabled={decision?.action === 'assign_group'}
                      options={options}
                      value={selected}
                      getOptionLabel={(option) => option?.label || ''}
                      isOptionEqualToValue={(option, optionValue) => option.kind === optionValue.kind && String(option.user?.id || '') === String(optionValue.user?.id || '')}
                      onChange={(_event, option) => {
                        if (!option || option.kind === 'empty') return;
                        if (option.kind === 'unassigned') {
                          setRepresentativeSourceDecision(mapping.field, mapping.sourceValue, { action: 'leave_unassigned' });
                        } else {
                          setRepresentativeSourceDecision(mapping.field, mapping.sourceValue, { action: 'select_user', userId: option.user.id });
                        }
                      }}
                      renderOption={(props, option) => (
                        <li
                          {...props}
                          onClick={(event) => {
                            props.onClick?.(event);
                            confirmPrefilledRepresentative(mapping, option, decision);
                          }}
                        >
                          {option?.label || ''}
                        </li>
                      )}
                      renderInput={(params) => <TextField {...params} placeholder="Search instance users..." />}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={resolved ? 'success' : 'warning'}
                      label={
                        decision?.action === 'leave_unassigned'
                          ? 'Unassigned'
                          : decision?.action === 'assign_group'
                            ? `Group · ${(decision.assignments || []).length} users`
                            : resolved
                              ? mapping.matchType?.replaceAll('_', ' ') || 'Mapped'
                              : 'Needs review'
                      }
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {unresolved > 0 && (
          <Alert severity="info">
            You can continue without resolving every representative here. Any customers still without an assigned Elite Core user will be clearly identified on the next screen.
          </Alert>
        )}
        <Button
          variant="contained"
          onClick={() => setRepresentativeReviewComplete(true)}
          sx={{ justifySelf: 'start', textTransform: 'none' }}
        >
          Continue to Assign &amp; Review
        </Button>
      </Paper>
    );
  };

  const friendlyMergeField = (field = '') => ({
    emailAddress: 'Company Email',
    phoneNumber: 'Company Phone',
    industry: 'Industry',
    websites: 'Website',
  }[field] || String(field).replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (value) => value.toUpperCase()));

  const displayMergeValue = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean).join(', ');
    if (value && typeof value === 'object') return JSON.stringify(value);
    return String(value ?? '');
  };

  const contactDisplayName = (contact = {}) =>
    [contact.firstName, contact.lastName].filter(Boolean).join(' ')
    || contact.email
    || contact.phone
    || 'Existing contact';

  const contactSecondaryDetails = (contact = {}) =>
    [
      contact.email && contact.email !== contactDisplayName(contact) ? contact.email : null,
      contact.phone,
      contact.title,
    ].filter(Boolean).join(' · ');

  const addressDisplayLine = (address = {}) =>
    [
      address.addressName,
      address.streetAddress,
      address.city,
      address.stateProvince,
      address.postalZipCode,
      address.country,
    ].filter(Boolean).join(' · ');

  const actualMergeFields = (record) =>
    (record?.plannedChanges?.fields || []).filter((field) => field.action !== 'preserve_existing');

  const preservedMergeFields = (record) =>
    (record?.plannedChanges?.fields || []).filter((field) => field.action === 'preserve_existing');

  const roleLabel = (role = '') => String(role || 'User').replaceAll('-', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

  const hydrateAssignmentUser = (assignment, users = []) => {
    const authoritative = users.find((user) => String(user.id) === String(assignment?.id));
    return authoritative ? { ...assignment, ...authoritative, role: authoritative.role || assignment?.role } : assignment;
  };

  const existingAssignmentsForRecord = (record, users = []) => {
    const byId = new Map();
    (record?.existingCustomer?.assignedReps || []).forEach((assignment) => {
      if (!assignment?.id) return;
      const hydrated = hydrateAssignmentUser(assignment, users);
      byId.set(String(assignment.id), hydrated);
    });
    return [...byId.values()];
  };

  const effectiveAssignmentsForRecord = (record, users = []) => {
    const ids = assignedUserIds(record);
    const byId = new Map();
    ids.forEach((id) => {
      const user = users.find((candidate) => String(candidate.id) === String(id))
        || (record.assignedReps || []).find((candidate) => String(candidate.id) === String(id));
      if (user?.id) byId.set(String(user.id), hydrateAssignmentUser(user, users));
    });
    return [...byId.values()];
  };

  const newAssignmentsForRecord = (record, users = []) => {
    const existingIds = new Set(existingAssignmentsForRecord(record, users).map((user) => String(user.id)));
    return effectiveAssignmentsForRecord(record, users).filter((user) => !existingIds.has(String(user.id)));
  };

  const importedFieldDetails = (record) => [
    ['Business name', record?.businessName],
    ['Email', record?.primaryEmail || record?.companyEmail],
    ['Phone', record?.phone],
    ['Website', record?.website],
    ['Currency', record?.currency],
    ['Industry', record?.industry?.mapped],
  ].filter(([, value]) => value != null && String(value).trim() !== '');

  const contactSummaryLine = (contact = {}) => {
    const primary = contactDisplayName(contact);
    const secondary = contactSecondaryDetails(contact);
    return secondary ? `${primary} — ${secondary}` : primary;
  };

  const assignmentSummaryLine = (user = {}) =>
    `${userLabel(user)}${user?.role ? ` — ${roleLabel(user.role)}` : ''}`;

  const recordImportSummaryItems = (record, users = preview?.instanceUsers || []) => {
    const changes = record?.plannedChanges || {};
    const isMerge = record?.plannedAction === 'merge';
    const fields = isMerge
      ? actualMergeFields(record).map((field) => (
        `${friendlyMergeField(field.field)}: ${displayMergeValue(field.incomingValue)}`
      ))
      : importedFieldDetails(record).map(([label, value]) => `${label}: ${displayMergeValue(value)}`);

    const contactsAdded = isMerge
      ? (changes.contactsToAdd || []).map(contactSummaryLine)
      : (record?.contacts || []).map(contactSummaryLine);

    const contactsEnriched = isMerge
      ? (changes.contactsToEnrich || []).map((contact) => {
        const target = contactDisplayName(contact.existingContact || {});
        const details = (contact.fields || [])
          .map((field) => `${friendlyMergeField(field.field)} → ${displayMergeValue(field.incomingValue)}`)
          .join(', ');
        return details ? `${target}: ${details}` : target;
      })
      : [];

    const addresses = isMerge
      ? (changes.addressesToAdd || []).map(addressDisplayLine).filter(Boolean)
      : (record?.addresses || []).map(addressDisplayLine).filter(Boolean);

    const repsAdded = isMerge
      ? newAssignmentsForRecord(record, users).map(assignmentSummaryLine)
      : effectiveAssignmentsForRecord(record, users).map(assignmentSummaryLine);

    const repsRemoved = isMerge && record?.replaceExistingAssignedReps
      ? existingAssignmentsForRecord(record, users)
        .filter((existing) => !effectiveAssignmentsForRecord(record, users).some((user) => String(user.id) === String(existing.id)))
        .map(assignmentSummaryLine)
      : [];

    const items = [];
    if (fields.length) items.push({ key: 'fields', label: isMerge ? 'Fields added' : 'Imported fields', count: fields.length, details: fields, icon: <DescriptionOutlinedIcon fontSize="small" /> });
    if (contactsAdded.length) items.push({ key: 'contacts-added', label: isMerge ? 'Contacts added' : 'Contacts', count: contactsAdded.length, details: contactsAdded, icon: <PersonOutlineOutlinedIcon fontSize="small" /> });
    if (contactsEnriched.length) items.push({ key: 'contacts-enriched', label: 'Contacts enriched', count: contactsEnriched.length, details: contactsEnriched, icon: <PersonOutlineOutlinedIcon fontSize="small" /> });
    if (addresses.length) items.push({ key: 'addresses', label: isMerge ? 'Addresses added' : 'Addresses', count: addresses.length, details: addresses, icon: <LocationOnOutlinedIcon fontSize="small" /> });
    if (repsAdded.length) items.push({ key: 'reps-added', label: isMerge ? 'Reps added' : 'Assigned reps', count: repsAdded.length, details: repsAdded, icon: <PeopleOutlineOutlinedIcon fontSize="small" /> });
    if (repsRemoved.length) items.push({ key: 'reps-removed', label: 'Reps removed', count: repsRemoved.length, details: repsRemoved, icon: <PeopleOutlineOutlinedIcon fontSize="small" /> });
    return items;
  };

  const renderRecordImportSummary = (record, users = preview?.instanceUsers || []) => {
    const items = recordImportSummaryItems(record, users);
    if (!items.length) {
      return <Typography variant="caption" color="text.secondary">No additional imported details</Typography>;
    }
    return (
      <Box sx={{ display: 'flex', gap: .5, flexWrap: 'wrap', alignItems: 'center' }}>
        {items.map((item) => (
          <Tooltip
            key={item.key}
            arrow
            placement="top"
            title={(
              <Box sx={{ py: .25, maxWidth: 360 }}>
                <Typography variant="caption" fontWeight={700} display="block">{item.label}</Typography>
                {item.details.map((detail, index) => (
                  <Typography key={`${item.key}-${index}`} variant="caption" display="block" sx={{ mt: index ? .25 : .4 }}>
                    • {detail}
                  </Typography>
                ))}
              </Box>
            )}
          >
            <Chip
              size="small"
              variant="outlined"
              icon={item.icon}
              label={item.count}
              aria-label={`${item.label}: ${item.count}`}
              sx={{ cursor: 'help' }}
            />
          </Tooltip>
        ))}
      </Box>
    );
  };

  const sourceRepresentativeValues = (record) => [...new Set(
    (record?.representativeResolutions || [])
      .map((resolution) => String(resolution?.sourceValue || '').trim())
      .filter((value) => value && value !== 'Manual assignment')
  )];

  const plannedChangeLabel = (record, users = preview?.instanceUsers || []) => {
    if (record.plannedAction === 'create') return 'Will be created';
    if (record.plannedAction === 'skip') return 'Skipped by import mode';
    if (record.plannedAction === 'needs_review' && record.defaultMatchDecision === 'create_new') return 'Will be created as a new customer by default';
    if (record.plannedAction === 'needs_review') return 'Review required';
    const changes = record.plannedChanges || {};
    const fields = actualMergeFields(record);
    const newAssignments = newAssignmentsForRecord(record, users);
    const parts = [];
    if (fields.length) parts.push(`+${fields.length} field${fields.length === 1 ? '' : 's'}`);
    if (changes.contactsToAdd?.length) parts.push(`+${changes.contactsToAdd.length} contact${changes.contactsToAdd.length === 1 ? '' : 's'}`);
    if (changes.contactsToEnrich?.length) parts.push(`${changes.contactsToEnrich.length} contact enriched`);
    if (changes.addressesToAdd?.length) parts.push(`+${changes.addressesToAdd.length} address${changes.addressesToAdd.length === 1 ? '' : 'es'}`);
    if (newAssignments.length) parts.push(`+${newAssignments.length} rep${newAssignments.length === 1 ? '' : 's'}`);
    return parts.length ? `Merge: ${parts.join(' · ')}` : 'No new information';
  };

  const openMergeDetails = (event, record) => {
    event.stopPropagation();
    setMergeDetailsAnchor(event.currentTarget);
    setMergeDetailsRecord(record);
  };

  const closeMergeDetails = () => {
    setMergeDetailsAnchor(null);
    setMergeDetailsRecord(null);
  };
  const customerMatchReason = (matchType = '') => ({
    exact_external_id: 'Matched by source-system customer ID',
    exact_company_email: 'Matched by company email',
    exact_contact_email: 'Matched by contact email',
    exact_contact_email_name: 'Matched by contact email + business name',
    exact_name_postal: 'Matched by business name + postal code',
    exact_name_phone: 'Matched by business name + phone',
    exact_normalized_name: 'Matched by normalized business name',
    reviewer_selected: 'Selected by reviewer',
  }[matchType] || String(matchType).replaceAll('_', ' '));

  const renderPreviewStep = () => {
    if (!livePreviewEnabled && isProgressivePreview) {
      const ready = Number(previewProgress?.readyRecords || 0);
      const total = Number(previewProgress?.totalRecords || preview?.summary?.proposedCustomers || 0);
      const phase = previewProgress?.phase || previewProgress?.status || 'processing';
      return (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>Assign &amp; Review</Typography>
            <Typography variant="body2" color="text.secondary">
              Live Preview is off to keep large imports responsive.
            </Typography>
          </Box>
          <Paper variant="outlined" sx={{ p: 3, minHeight: 220, display: 'grid', placeItems: 'center', textAlign: 'center' }}>
            <Box>
              <Typography variant="overline" color="text.secondary">
                {progressivePhaseLabel(phase)}
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ mt: .5 }}>
                {ready.toLocaleString()} / {total.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: .75 }}>
                customers prepared
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2, maxWidth: 520 }}>
                Filters, representative cards, customer results, avatars, and live review totals are paused. Processing continues on the server. Turn Live Preview back on whenever you want to inspect or edit the ready customers.
              </Typography>
            </Box>
          </Paper>
        </Box>
      );
    }
    if (representativeMappings.length > 0 && !representativeReviewComplete) return renderRepresentativeMappingStage();
    const users = preview?.instanceUsers || [];
    const assignmentCounts = new Map(
      (preview?.assignmentSummary?.byUser || []).map((item) => [String(item.userId), Number(item.customerCount || 0)])
    );
    if (!assignmentCounts.size) {
      assignedUserIdsByRecord.forEach((userIds) => {
        userIds.forEach((userId) => {
          assignmentCounts.set(String(userId), Number(assignmentCounts.get(String(userId)) || 0) + 1);
        });
      });
    }
    const customerCountForUser = (userId) => Number(assignmentCounts.get(String(userId)) || 0);
    const usersByCustomerCount = [...users].sort((a, b) => {
      const countDifference = customerCountForUser(b.id) - customerCountForUser(a.id);
      if (countDifference !== 0) return countDifference;
      return userLabel(a).localeCompare(userLabel(b));
    });
    const representativeUsersToShow = usersByCustomerCount.filter((user) => customerCountForUser(user.id) > 0);
    const authoritativeUnassignedCount = representativeFilterActivated && representativeFilter === 'unassigned' && previewFilteredReadyCount != null
      ? previewFilteredReadyCount
      : (preview?.assignmentSummary?.unassignedCount ?? [...assignedUserIdsByRecord.values()].filter((ids) => ids.length === 0).length);
    const authoritativeNeedsReviewCount = representativeFilterActivated && representativeFilter === 'needs_review' && previewFilteredReadyCount != null
      ? previewFilteredReadyCount
      : (preview?.summary?.conflictCount ?? records.filter((record) => record.plannedAction === 'needs_review').length);
    const modeOptions = [
      ['keep_existing', 'Keep Existing Data', 'Create new customers. Existing Elite Core customers will be skipped.', false],
      ['merge_missing', 'Merge Missing Data', 'Create new customers and add missing fields, contacts and addresses to existing customers. Existing populated values are preserved.', false],
      ['overwrite_existing', 'Overwrite Existing Data', 'Replace existing customer data with imported data.', true],
    ];
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Assign &amp; Review</Typography>
          <Typography variant="body2" color="text.secondary">Review each representative&apos;s book of customers before importing.</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 1.5 }}>
          {modeOptions.map(([value, title, description, disabled]) => (
            <Card
              key={value}
              variant="outlined"
              onClick={() => !disabled && changeImportMode(value)}
              sx={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.62 : 1, borderColor: importMode === value ? 'primary.main' : 'divider' }}
            >
              <CardContent sx={{ display: 'flex', gap: 1, p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Radio checked={importMode === value} disabled={disabled} value={value} inputProps={{ 'aria-label': title }} />
                <Box>
                  <Typography fontWeight={700}>{title}</Typography>
                  <Typography variant="body2" color="text.secondary">{description}</Typography>
                  {disabled && <Chip size="small" label="Coming later" sx={{ mt: 1 }} />}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Box sx={{ display: 'grid', gap: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(160px, 220px))', gap: 1 }}>
            {[
              ['all', 'All Customers', preview?.assignmentSummary?.totalRecords ?? totalPreviewRecordCount],
              ['unassigned', 'Unassigned', authoritativeUnassignedCount],
              ['needs_review', 'Review Matches', authoritativeNeedsReviewCount],
            ].map(([key, label, count]) => (
              <Card key={key} variant="outlined" onClick={() => applyRepresentativeFilter(key)} sx={{ cursor: 'pointer', borderColor: representativeFilter === key ? 'primary.main' : 'divider' }}>
                <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                  <Typography fontWeight={700}>{label}</Typography>
                  <Typography variant="caption">{Number(count || 0).toLocaleString()} customers</Typography>
                  {key === 'needs_review' && Number(count || 0) > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Possible matches create new customers by default
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>

          <Box sx={{ mt: .5 }}>
            <Typography variant="subtitle2" fontWeight={700}>Representatives in this import</Typography>
            <Typography variant="caption" color="text.secondary">Counts and filters reflect the staged import assignments. Representatives with no customers are removed from this filter list automatically. Click any active filter again to refresh the ready customer list without stopping background processing.</Typography>
          </Box>

          {representativeUsersToShow.length > 0 ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 1 }}>
              {representativeUsersToShow.map((user) => (
                <Card
                  key={user.id}
                  variant="outlined"
                  onClick={() => applyRepresentativeFilter(String(user.id))}
                  sx={{
                    cursor: 'pointer',
                    borderColor: String(representativeFilter) === String(user.id) ? 'primary.main' : 'divider',
                    bgcolor: String(representativeFilter) === String(user.id) ? 'action.selected' : 'background.paper',
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.1, '&:last-child': { pb: 1.1 } }}>
                    <Avatar src={profilePictureUrl(user.profilePicture)} alt={userLabel(user)} sx={{ width: 34, height: 34, fontSize: 14 }}>{initialsFor(user)}</Avatar>
                    <Box minWidth={0} flex={1}>
                      <Typography variant="body2" fontWeight={700} noWrap>{userLabel(user)}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block" noWrap>{roleLabel(user.role)}</Typography>
                      <Typography variant="caption" fontWeight={600}>{customerCountForUser(user.id).toLocaleString()} in this import</Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">No representatives are currently assigned in this import.</Typography>
          )}

          {!['all', 'unassigned', 'needs_review'].includes(representativeFilter) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                Showing customers assigned to <strong>{userLabel(users.find((user) => String(user.id) === String(representativeFilter)) || {})}</strong>
              </Typography>
              <Button size="small" onClick={() => applyRepresentativeFilter('all')} sx={{ textTransform: 'none' }}>Clear filter</Button>
            </Box>
          )}
        </Box>

        <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="body2">
              {!representativeFilterActivated && isProgressivePreview
                ? `Showing ${visiblePreviewRecords.length ? previewStartIndex + 1 : 0}-${previewEndIndex} of ${filteredRecords.length.toLocaleString()} loaded · ${readyRecordCount.toLocaleString()} ready so far`
                : `Showing ${visiblePreviewRecords.length ? previewStartIndex + 1 : 0}-${previewEndIndex} of ${filteredReadyRecordCount.toLocaleString()} ready customers`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {!representativeFilterActivated && isProgressivePreview
                ? `Newly processed customers are added automatically. Only ${CUSTOMER_PREVIEW_PAGE_SIZE} cards are rendered at once for performance.`
                : `Filtered preview pages are limited to ${CUSTOMER_PREVIEW_PAGE_SIZE} customers.`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" disabled={previewPageLoading} onClick={() => visiblePreviewRecords.forEach((record) => setRecordAction(record.id, record.plannedAction === 'merge' ? 'update' : 'import'))}>Select all visible</Button>
            <Button size="small" disabled={previewPageLoading} onClick={() => visiblePreviewRecords.forEach((record) => setRecordAction(record.id, 'skip'))}>Clear visible selection</Button>
            <Button
              size="small"
              variant="outlined"
              disabled={previewPageLoading || bulkAssigning || filteredReadyRecordCount === 0}
              onClick={openBulkAssignment}
              sx={{ textTransform: 'none' }}
            >
              Actions
            </Button>
            {previewTotalPages > 1 && (
              <>
                <Button size="small" variant="outlined" disabled={previewPageLoading || visiblePreviewPage <= 1} onClick={() => goToPreviewPage(visiblePreviewPage - 1)}>Previous</Button>
                <Button size="small" variant="outlined" disabled={previewPageLoading || visiblePreviewPage >= previewTotalPages} onClick={() => goToPreviewPage(visiblePreviewPage + 1)}>Next</Button>
              </>
            )}
          </Box>
        </Paper>
        {previewPageLoading ? (
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              minHeight: 280,
              display: 'grid',
              alignContent: 'start',
              gap: 1.5,
              backgroundColor: '#fff',
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Loading {previewPageLoadingLabel || 'matching customers'}…
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Preparing this filtered page can take a few seconds on a large import. The file keeps processing in the background, and previously loaded records remain available again as soon as this filter is ready.
              </Typography>
            </Box>
            <LinearProgress />
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1.5, '@media (max-width: 1200px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }, '@media (max-width: 700px)': { gridTemplateColumns: '1fr' } }}>
              {Array.from({ length: 8 }).map((_item, index) => (
                <Paper key={`preview-skeleton-${index}`} variant="outlined" sx={{ p: 1.5, minHeight: 150, opacity: 0.7 }}>
                  <Box sx={{ height: 18, width: '68%', bgcolor: 'grey.200', borderRadius: 1, mb: 1 }} />
                  <Box sx={{ height: 22, width: 90, bgcolor: 'grey.100', borderRadius: 4, mb: 2 }} />
                  <Box sx={{ height: 12, width: '90%', bgcolor: 'grey.100', borderRadius: 1, mb: .75 }} />
                  <Box sx={{ height: 12, width: '75%', bgcolor: 'grey.100', borderRadius: 1, mb: .75 }} />
                  <Box sx={{ height: 30, width: '45%', bgcolor: 'grey.100', borderRadius: 1, mt: 2 }} />
                </Paper>
              ))}
            </Box>
          </Paper>
        ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1.5, '@media (max-width: 1200px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }, '@media (max-width: 700px)': { gridTemplateColumns: '1fr' } }}>
          {visiblePreviewRecords.map((record) => {
            const selected = selectedForImport(record);
            const assignedIds = assignedUserIds(record);
            const assignedUsers = users.filter((user) => assignedIds.includes(String(user.id)));
            const defaultsToCreate = record.plannedAction === 'needs_review' && record.defaultMatchDecision === 'create_new';
            const explicitMatchDecision = matchDecisions[record.id] || null;
            const selectedMatchAction = explicitMatchDecision?.action || record.matchDecision || record.defaultMatchDecision || null;
            const selectedMatchCustomerId = explicitMatchDecision?.customerId ? String(explicitMatchDecision.customerId) : null;
            const status = defaultsToCreate
              ? 'New Customer · Possible Match'
              : record.plannedAction === 'needs_review'
                ? 'Possible Match'
                : record.existingCustomer ? 'Existing Customer' : 'New Customer';
            return (
              <Card
                key={record.id}
                variant="outlined"
                sx={{
                  borderColor: selected && !assignedIds.length ? 'error.main' : record.plannedAction === 'needs_review' ? 'warning.main' : 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  alignContent: 'flex-start',
                }}
              >
                <CardContent sx={{ display: 'grid', gap: 1, flex: 1, width: '100%', alignItems: 'flex-start', alignContent: 'flex-start' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: .5 }}><Checkbox checked={selected} onChange={(event) => setRecordAction(record.id, event.target.checked ? (record.plannedAction === 'merge' ? 'update' : 'import') : 'skip')} inputProps={{ 'aria-label': `Select ${record.businessName}` }} /><Box minWidth={0}><Typography fontWeight={700}>{record.businessName || 'Unnamed customer'}</Typography><Chip size="small" color={record.plannedAction === 'needs_review' ? 'warning' : record.existingCustomer ? 'info' : 'success'} label={status} /></Box></Box>
                  {record.plannedAction === 'merge' ? (
                    <Box sx={{ display: 'grid', gap: .6, width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight={600}>Merge changes</Typography>
                        <Button
                          size="small"
                          startIcon={<InfoOutlinedIcon sx={{ fontSize: 15 }} />}
                          onClick={(event) => openMergeDetails(event, record)}
                          sx={{ minWidth: 0, px: 0.75, py: 0.25, textTransform: 'none', fontSize: '0.72rem' }}
                        >
                          What will change?
                        </Button>
                      </Box>
                      {renderRecordImportSummary(record, users)}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'grid', gap: .6, width: '100%' }}>
                      <Typography variant="body2" fontWeight={600}>{plannedChangeLabel(record)}</Typography>
                      {renderRecordImportSummary(record, users)}
                    </Box>
                  )}
                  {record.existingCustomer && <Typography variant="caption" color="text.secondary">{customerMatchReason(record.existingCustomer.matchType)} · {record.existingCustomer.confidence || 'unknown'} confidence</Typography>}
                  {record.plannedAction === 'needs_review' && (
                    <Box sx={{ display: 'grid', gap: 1, p: 1.25, bgcolor: 'warning.50', borderRadius: 1, alignItems: 'flex-start', alignContent: 'flex-start', width: '100%' }}>
                      <Box>
                        <Typography variant="body2" fontWeight={700}>Possible existing matches found</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: .25, lineHeight: 1.35 }}>
                          This customer will be created as a separate new customer by default. Nothing below will be changed unless you explicitly choose to merge into one of the possible matches.
                        </Typography>
                        {record.defaultMatchDecision === 'create_new' && (
                          <Chip
                            size="small"
                            color="success"
                            label={`Default: Create ${record.businessName || 'this customer'} as new`}
                            sx={{ mt: .75 }}
                          />
                        )}
                      </Box>

                      <Box sx={{ display: 'grid', gap: .75, width: '100%' }}>
                        {(record.matchCandidates || []).map((candidate) => {
                          const matchedEmails = candidate.matchedContactEmails || (candidate.evidence || [])
                            .map((evidence) => String(evidence).replace(/^Matched imported contact email:\s*/i, '').trim())
                            .filter((value) => value && value !== 'Same contact email');
                          return (
                            <Paper
                              key={candidate.id}
                              variant="outlined"
                              sx={{ p: 1, width: '100%', bgcolor: 'background.paper', display: 'grid', gap: .5, alignContent: 'flex-start' }}
                            >
                              <Box>
                                <Typography variant="body2" fontWeight={700}>{candidate.businessName}</Typography>
                                {[candidate.companyEmail, candidate.phone, candidate.city, candidate.postalCode].filter(Boolean).length > 0 && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {[candidate.companyEmail, candidate.phone, candidate.city, candidate.postalCode].filter(Boolean).join(' · ')}
                                  </Typography>
                                )}
                              </Box>

                              {matchedEmails.length > 0 && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Matched contact</Typography>
                                  {matchedEmails.map((email) => (
                                    <Typography key={email} variant="caption" display="block">{email}</Typography>
                                  ))}
                                </Box>
                              )}

                              <Box sx={{ display: 'grid', gap: .35, justifyItems: 'start' }}>
                                <Typography variant="caption" color="text.secondary">
                                  Choose this if <strong>{record.businessName || 'this imported customer'}</strong> and <strong>{candidate.businessName || 'this customer'}</strong> are the same customer.
                                </Typography>
                                <Button
                                  size="small"
                                  variant={selectedMatchAction === 'use_existing' && selectedMatchCustomerId === String(candidate.id) ? 'contained' : 'outlined'}
                                  startIcon={selectedMatchAction === 'use_existing' && selectedMatchCustomerId === String(candidate.id) ? <CheckCircleOutlineIcon /> : null}
                                  aria-pressed={selectedMatchAction === 'use_existing' && selectedMatchCustomerId === String(candidate.id)}
                                  onClick={() => chooseCustomerMatch(record, 'use_existing', candidate)}
                                  sx={{ justifySelf: 'flex-start', textTransform: 'none' }}
                                >
                                  {selectedMatchAction === 'use_existing' && selectedMatchCustomerId === String(candidate.id) ? 'Selected · ' : ''}
                                  Merge into {candidate.businessName || 'this customer'}
                                </Button>
                              </Box>
                            </Paper>
                          );
                        })}
                      </Box>

                      <Box sx={{ width: '100%', pt: .25 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700}>No merge needed?</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: .25 }}>
                          Creating <strong>{record.businessName || 'this customer'}</strong> as a separate new customer is selected by default. The possible matches stay unchanged unless you choose a merge option above.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: .5, flexWrap: 'wrap', mt: .5 }}>
                          <Button
                            size="small"
                            variant={selectedMatchAction === 'create_new' ? 'contained' : 'outlined'}
                            color="success"
                            startIcon={selectedMatchAction === 'create_new' ? <CheckCircleOutlineIcon /> : null}
                            aria-pressed={selectedMatchAction === 'create_new'}
                            onClick={() => chooseCustomerMatch(record, 'create_new')}
                            sx={{ textTransform: 'none' }}
                          >
                            {selectedMatchAction === 'create_new' ? 'Selected · ' : ''}
                            Create {record.businessName || 'customer'} as new
                          </Button>
                          <Button
                            size="small"
                            variant={selectedMatchAction === 'skip' ? 'contained' : 'outlined'}
                            color={selectedMatchAction === 'skip' ? 'error' : 'inherit'}
                            startIcon={selectedMatchAction === 'skip' ? <CheckCircleOutlineIcon /> : null}
                            aria-pressed={selectedMatchAction === 'skip'}
                            onClick={() => chooseCustomerMatch(record, 'skip')}
                            sx={{ textTransform: 'none' }}
                          >
                            {selectedMatchAction === 'skip' ? 'Selected · ' : ''}
                            Do not import this customer
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  )}
                  {record.existingCustomer ? (() => {
                    const currentAssignments = existingAssignmentsForRecord(record, users)
                      .filter((user) => assignedIds.includes(String(user.id)));
                    const additions = newAssignmentsForRecord(record, users);
                    return (
                      <Box sx={{ display: 'grid', gap: .75 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={700}>Currently assigned</Typography>
                          <Box sx={{ display: 'flex', gap: .5, flexWrap: 'wrap', mt: .25 }}>
                            {currentAssignments.length
                              ? currentAssignments.map((user) => (
                                <Chip
                                  key={user.id}
                                  size="small"
                                  variant="outlined"
                                  avatar={<Avatar src={profilePictureUrl(user.profilePicture)}>{initialsFor(user)}</Avatar>}
                                  label={`${userLabel(user)} · ${roleLabel(user.role)}`}
                                  onDelete={() => setRecordAssignmentRemoved(record, user)}
                                  deleteIcon={<CloseIcon fontSize="small" />}
                                  sx={{ '& .MuiChip-deleteIcon': { opacity: 0 }, '&:hover .MuiChip-deleteIcon': { opacity: 1 } }}
                                />
                              ))
                              : <Typography variant="caption" color="text.secondary">None</Typography>}
                          </Box>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={700}>Will add</Typography>
                          <Box sx={{ display: 'flex', gap: .5, flexWrap: 'wrap', mt: .25 }}>
                            {additions.length
                              ? additions.map((user) => (
                                <Chip
                                  key={user.id}
                                  size="small"
                                  color="primary"
                                  avatar={<Avatar src={profilePictureUrl(user.profilePicture)}>{initialsFor(user)}</Avatar>}
                                  label={`${userLabel(user)} · ${roleLabel(user.role)}`}
                                  onDelete={() => setRecordAssignmentRemoved(record, user)}
                                  deleteIcon={<CloseIcon fontSize="small" />}
                                  sx={{ '& .MuiChip-deleteIcon': { opacity: 0 }, '&:hover .MuiChip-deleteIcon': { opacity: 1 } }}
                                />
                              ))
                              : <Typography variant="caption" color="text.secondary">No representative changes</Typography>}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })() : (
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>Will assign</Typography>
                      <Box sx={{ display: 'flex', gap: .5, flexWrap: 'wrap', mt: .25 }}>
                        {assignedUsers.length
                          ? assignedUsers.map((user) => (
                            <Chip
                              key={user.id}
                              size="small"
                              avatar={<Avatar src={profilePictureUrl(user.profilePicture)}>{initialsFor(user)}</Avatar>}
                              label={`${userLabel(user)} · ${roleLabel(user.role)}`}
                              onDelete={() => setRecordAssignmentRemoved(record, user)}
                              deleteIcon={<CloseIcon fontSize="small" />}
                              sx={{ '& .MuiChip-deleteIcon': { opacity: 0 }, '&:hover .MuiChip-deleteIcon': { opacity: 1 } }}
                            />
                          ))
                          : <Chip size="small" color="error" label="Unassigned" />}
                      </Box>
                    </Box>
                  )}
                  <Button size="small" variant="outlined" onClick={() => setReviewCustomer(record)}>Edit assignments</Button>
                </CardContent>
              </Card>
            );
          })}
        </Box>
        )}

        <Popover
          open={Boolean(mergeDetailsAnchor && mergeDetailsRecord)}
          anchorEl={mergeDetailsAnchor}
          onClose={closeMergeDetails}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { width: 390, maxWidth: 'calc(100vw - 32px)', p: 2 } } }}
        >
          {mergeDetailsRecord && (() => {
            const changes = mergeDetailsRecord.plannedChanges || {};
            const fieldsToFill = actualMergeFields(mergeDetailsRecord);
            const fieldsKept = preservedMergeFields(mergeDetailsRecord);
            const users = preview?.instanceUsers || [];
            const repsToAdd = newAssignmentsForRecord(mergeDetailsRecord, users);
            const sourceValues = sourceRepresentativeValues(mergeDetailsRecord);
            const hasChanges = fieldsToFill.length
              || changes.contactsToAdd?.length
              || changes.contactsToEnrich?.length
              || changes.addressesToAdd?.length
              || repsToAdd.length;
            return (
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>What will be merged?</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Elite Core only fills missing information. Existing populated values are not overwritten.
                  </Typography>
                </Box>

                {!hasChanges && (
                  <Alert severity="info">No new information will be added to this customer.</Alert>
                )}

                {fieldsToFill.length > 0 && (
                  <Box>
                    <Typography variant="caption" fontWeight={700}>Fields to add</Typography>
                    {fieldsToFill.map((field, index) => (
                      <Typography key={`${field.field}-${index}`} variant="body2">
                        • {friendlyMergeField(field.field)}: {displayMergeValue(field.incomingValue)}
                      </Typography>
                    ))}
                  </Box>
                )}

                {(changes.contactsToAdd || []).length > 0 && (
                  <Box>
                    <Typography variant="caption" fontWeight={700}>New contacts</Typography>
                    {changes.contactsToAdd.map((contact, index) => {
                      const primary = contactDisplayName(contact);
                      const secondary = contactSecondaryDetails(contact);
                      return (
                        <Box key={`contact-${index}`} sx={{ mb: 0.5 }}>
                          <Typography variant="body2">• {primary}</Typography>
                          {secondary && <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 1.5 }}>{secondary}</Typography>}
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {(changes.contactsToEnrich || []).length > 0 && (
                  <Box>
                    <Typography variant="caption" fontWeight={700}>Existing contacts to enrich</Typography>
                    {changes.contactsToEnrich.map((contact, index) => {
                      const existingContact = contact.existingContact || {};
                      const name = contactDisplayName(existingContact);
                      const secondary = contactSecondaryDetails(existingContact);
                      return (
                        <Box key={`enrich-${index}`} sx={{ mb: 0.75 }}>
                          <Typography variant="body2">• {name}</Typography>
                          {secondary && <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 1.5 }}>{secondary}</Typography>}
                          {contact.match?.label && (
                            <Typography variant="caption" color="success.main" display="block" sx={{ pl: 1.5 }}>
                              Matched by {contact.match.label.toLowerCase()}{contact.match.value ? `: ${contact.match.value}` : ''}
                            </Typography>
                          )}
                          {(contact.fields || []).map((field) => (
                            <Typography key={field.field} variant="caption" display="block" sx={{ pl: 1.5 }}>
                              Add {friendlyMergeField(field.field)}: “{displayMergeValue(field.incomingValue)}”
                            </Typography>
                          ))}
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {(changes.addressesToAdd || []).some((address) => addressDisplayLine(address)) && (
                  <Box>
                    <Typography variant="caption" fontWeight={700}>New addresses</Typography>
                    {changes.addressesToAdd
                      .map((address) => addressDisplayLine(address))
                      .filter(Boolean)
                      .map((line, index) => (
                        <Typography key={`address-${index}`} variant="body2">• {line}</Typography>
                      ))}
                  </Box>
                )}

                {repsToAdd.length > 0 && (
                  <Box>
                    <Typography variant="caption" fontWeight={700}>Representatives to add</Typography>
                    {repsToAdd.map((rep) => (
                      <Typography key={`rep-${rep.id}`} variant="body2">
                        • {userLabel(rep)} · {roleLabel(rep.role)}
                      </Typography>
                    ))}
                  </Box>
                )}

                {repsToAdd.length > 0 && sourceValues.length > 0 && (
                  <Box>
                    <Typography variant="caption" fontWeight={700} color="text.secondary">Representative source values</Typography>
                    {sourceValues.map((value) => (
                      <Typography key={value} variant="caption" color="text.secondary" display="block">• {value}</Typography>
                    ))}
                  </Box>
                )}

                {fieldsKept.length > 0 && (
                  <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary">Existing values kept</Typography>
                    {fieldsKept.map((field, index) => (
                      <Typography key={`kept-${field.field}-${index}`} variant="caption" color="text.secondary" display="block">
                        • {friendlyMergeField(field.field)} stays “{displayMergeValue(field.currentValue)}”
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })()}
        </Popover>

        <Paper elevation={3} sx={{ position: 'sticky', bottom: 0, p: 1.5, zIndex: 2, display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={`${footerSelectedCount.toLocaleString()} selected`} />
            <Chip color="success" label={`${footerNewCount.toLocaleString()} new`} />
            <Chip color="info" label={`${footerMergeCount.toLocaleString()} merge`} />
            <Chip color={needsReviewCount ? 'warning' : 'default'} label={`${needsReviewCount.toLocaleString()} need review`} />
            <Chip color={footerUnassignedCount ? 'error' : 'default'} label={`${footerUnassignedCount.toLocaleString()} unassigned`} />
          </Box>
          {(footerUnassignedCount > 0 || needsReviewCount > 0) && (
            <Typography variant="body2" color="warning.main">
              {[
                footerUnassignedCount > 0 && (
                  allowUnassignedImport
                    ? `${footerUnassignedCount.toLocaleString()} unassigned customer${footerUnassignedCount === 1 ? '' : 's'} will be imported without an assigned user`
                    : `${footerUnassignedCount.toLocaleString()} unassigned customer${footerUnassignedCount === 1 ? '' : 's'} require confirmation to continue`
                ),
                needsReviewCount > 0 && (
                  skipUnresolvedMatches
                    ? `${needsReviewCount.toLocaleString()} unresolved customer match${needsReviewCount === 1 ? '' : 'es'} will be skipped`
                    : `${needsReviewCount.toLocaleString()} customer match${needsReviewCount === 1 ? '' : 'es'} need review`
                ),
              ].filter(Boolean).join(' · ')}
            </Typography>
          )}
        </Paper>
      </Box>
    );
  };

  const renderEnrichmentStep = () => {
    const safeSuggestions = suggestions.filter((suggestion) => ['auto_accepted', 'accepted'].includes(suggestion.status) && !suggestion.type?.startsWith('conflicting_'));
    const pendingSafe = suggestions.filter((suggestion) => suggestion.status === 'pending' && !suggestion.type?.startsWith('conflicting_'));
    const conflicts = suggestions.filter((suggestion) => suggestion.type?.startsWith('conflicting_'));
    const needsReview = preview?.unmatchedRows || [];
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip label={`${sources.length || 1} sources`} />
          <Chip label={`${Number(totalPreviewRecordCount || records.length || 0).toLocaleString()} candidate customers`} />
          <Chip color="success" label={`We can safely add ${safeSuggestions.length} missing details`} />
          <Chip color="info" label={`${pendingSafe.length} additions waiting for review`} />
          <Chip color="warning" label={`${conflicts.length} differences to review`} />
          <Chip color={needsReview.length ? 'warning' : 'default'} label={`${needsReview.length} rows need a match decision`} />
        </Box>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={600}>Review useful customer details</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Safe additions fill blank fields or add distinct contacts/addresses. Conflicts preserve the current candidate value unless accepted later.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
            <Button
              variant="outlined"
              disabled={!pendingSafe.length || loading}
              onClick={() => updateSuggestionDecisions(Object.fromEntries(pendingSafe.map((suggestion) => [suggestion.id, 'accept'])))}
              sx={{ textTransform: 'none' }}
            >
              Accept All Safe Additions
            </Button>
            <Button
              variant="outlined"
              disabled={!conflicts.length || loading}
              onClick={() => updateSuggestionDecisions(Object.fromEntries(conflicts.map((suggestion) => [suggestion.id, 'reject'])))}
              sx={{ textTransform: 'none' }}
            >
              Reject All Conflicts
            </Button>
          </Box>
        </Paper>
        {records.map((record) => {
          const rowSuggestions = suggestions.filter((suggestion) => suggestion.candidateCustomerId === record.id);
          if (!rowSuggestions.length) return null;
          return (
            <Paper key={record.id} variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700}>{record.businessName || 'Unnamed customer'}</Typography>
              <Typography variant="caption" color="text.secondary">Information assembled from {(record.sourceNames || []).join(', ')}</Typography>
              <Box sx={{ display: 'grid', gap: 1.25, mt: 1.5 }}>
                {rowSuggestions.map((suggestion) => {
                  const conflict = suggestion.type?.startsWith('conflicting_');
                  const safe = !conflict;
                  return (
                    <Paper key={suggestion.id} variant="outlined" sx={{ p: 1.5, backgroundColor: safe ? '#f6fbf7' : '#fffaf2' }}>
                      <Typography variant="body2" fontWeight={700}>{suggestion.field.replaceAll('.', ' ')}</Typography>
                      {conflict && <Typography variant="body2">Current: {String(suggestion.currentValue || '—')}</Typography>}
                      <Typography variant="body2">{suggestion.sourceName}: {suggestionValue(suggestion)}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        From {suggestion.sourceName}{suggestion.sourceRows?.length ? `, row ${suggestion.sourceRows.join(', ')}` : ''}
                      </Typography>
                      {safe ? (
                        <Typography variant="caption" color="success.main">✓ Safe addition</Typography>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Button size="small" variant={suggestion.status === 'rejected' ? 'contained' : 'outlined'} onClick={() => updateSuggestionDecisions({ [suggestion.id]: 'reject' })}>Keep current</Button>
                          <Button size="small" variant={suggestion.status === 'accepted' ? 'contained' : 'outlined'} onClick={() => updateSuggestionDecisions({ [suggestion.id]: 'accept' })}>Use {suggestion.sourceName}</Button>
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            </Paper>
          );
        })}
        {[...(preview?.unmatchedRows || []), ...(preview?.identityConflicts || [])].map((item) => {
          const possibleMatches = item.possibleMatches || (item.suggestedMatch ? [item.suggestedMatch] : []);
          return (
            <Paper key={`${item.sourceId}:${item.record.id}`} variant="outlined" sx={{ p: 2, borderColor: 'warning.main' }}>
              <Typography variant="overline" color="warning.main">Needs your decision</Typography>
              <Typography variant="subtitle1" fontWeight={700}>Source row: {item.record.businessName || 'Unnamed customer'}</Typography>
              {possibleMatches.map((match) => {
                const possible = records.find((record) => record.id === match.candidateId);
                return (
                  <Box key={match.candidateId} sx={{ mt: 1.5, p: 1.5, backgroundColor: '#fffaf2', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight={700}>Possible customer: {possible?.businessName || match.candidateId}</Typography>
                    {(match.evidence || []).map((evidence) => (
                      <Typography key={evidence.type} variant="caption" display="block">✓ {evidenceLabel(evidence.type)}</Typography>
                    ))}
                    <Button size="small" variant="contained" sx={{ mt: 1 }} onClick={() => resolveIdentity(item, 'match', match.candidateId)}>Match</Button>
                  </Box>
                );
              })}
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                <Button size="small" variant="outlined" onClick={() => resolveIdentity(item, 'create')}>Create separate customer</Button>
                <Button size="small" color="inherit" onClick={() => resolveIdentity(item, 'skip')}>Skip</Button>
              </Box>
            </Paper>
          );
        })}
      </Box>
    );
  };

  const copyDryRunJson = async (value, label = 'JSON') => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(value ?? null, null, 2));
      setDryRunCopyStatus(`${label} copied`);
      window.setTimeout(() => setDryRunCopyStatus(''), 1800);
    } catch (_error) {
      setDryRunCopyStatus('Unable to copy automatically — select the JSON text manually.');
    }
  };

  const dryRunOutcomeColor = (action) => {
    if (action === 'blocked') return 'error';
    if (action === 'create') return 'success';
    if (action === 'merge') return 'info';
    if (action === 'skip_needs_review') return 'warning';
    return 'default';
  };

  const renderImportStep = () => {
    const drySummary = dryRunResult?.summary || {};
    const simulatedRecords = dryRunResult?.records || [];
    const dryRunPageSize = 50;
    const dryRunFilterOptions = [
      { key: 'all', label: 'All', count: simulatedRecords.length },
      { key: 'blocked', label: 'Blocked', count: simulatedRecords.filter((row) => row.action === 'blocked').length },
      { key: 'merge', label: 'Would merge', count: simulatedRecords.filter((row) => row.action === 'merge').length },
      { key: 'create', label: 'Would create', count: simulatedRecords.filter((row) => row.action === 'create').length },
      { key: 'no_changes', label: 'No changes', count: simulatedRecords.filter((row) => row.action === 'no_changes').length },
      { key: 'skip_needs_review', label: 'Skipped — Needs Review', count: simulatedRecords.filter((row) => row.action === 'skip_needs_review').length },
      { key: 'skip', label: 'Skipped', count: simulatedRecords.filter((row) => row.action === 'skip').length },
    ];
    const filteredSimulatedRecords = dryRunResultFilter === 'all'
      ? simulatedRecords
      : simulatedRecords.filter((row) => row.action === dryRunResultFilter);
    const dryRunResultTotalPages = Math.max(1, Math.ceil(filteredSimulatedRecords.length / dryRunPageSize));
    const safeDryRunResultPage = Math.min(dryRunResultPage, dryRunResultTotalPages);
    const dryRunResultStart = filteredSimulatedRecords.length
      ? (safeDryRunResultPage - 1) * dryRunPageSize
      : 0;
    const displayedSimulatedRecords = filteredSimulatedRecords.slice(
      dryRunResultStart,
      dryRunResultStart + dryRunPageSize,
    );
    const dryRunResultEnd = dryRunResultStart + displayedSimulatedRecords.length;
    const waitingPhase = previewProgress?.phase || previewProgress?.status || preview?.currentPhase || 'processing';
    const waitingReady = Number(previewProgress?.readyRecords || 0);
    const waitingTotal = Number(previewProgress?.totalRecords || preview?.summary?.proposedCustomers || 0);
    const waitingPercent = waitingTotal
      ? Math.min(100, Math.round((waitingReady / waitingTotal) * 100))
      : 0;
    const waitingMessage = ({
      queued: 'Waiting for the preview worker to start.',
      parsing: 'Reading and grouping rows from the spreadsheet.',
      matching: 'Matching imported customers against existing Elite Core customers.',
      processing: 'Building and saving the review-ready customer batches.',
      previewing: 'Finalizing the customer preview.',
    }[waitingPhase] || 'Finalizing the customer preview.');

    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Alert severity={dryRunIsCurrent ? 'success' : dryRunResult && !dryRunResult.valid ? 'error' : 'info'}>
          {dryRunIsCurrent
            ? 'Dry Run passed. Simulation only — no database writes were performed. Review the results below before importing.'
            : dryRunResult && !dryRunResult.valid
              ? 'Dry Run found blocking issues. Resolve them and run the simulation again.'
              : 'Run a Dry Run before importing. Elite Core will calculate the exact planned database changes without writing customer data.'}
        </Alert>

        {skipUnresolvedMatches && needsReviewCount > 0 && (
          <Alert severity="warning">
            {needsReviewCount.toLocaleString()} genuinely unresolved blocking customer match{needsReviewCount === 1 ? '' : 'es'} will be skipped. Possible matches that kept the default Create as new selection will still be imported as new customers.
          </Alert>
        )}
        {allowUnassignedImport && unassignedCount > 0 && (
          <Alert severity="info">
            {unassignedCount.toLocaleString()} customer{unassignedCount === 1 ? '' : 's'} will be allowed to import without an assigned Elite Core user. Dry Run will still validate the rest of each customer record normally.
          </Alert>
        )}

        {!previewCommitReady && (
          <Paper variant="outlined" sx={{ p: 2, display: 'grid', gap: 1.25, bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>Finishing customer preview…</Typography>
                <Typography variant="body2" color="text.secondary">
                  {waitingMessage}
                </Typography>
              </Box>
              <Chip
                size="small"
                color="info"
                label={progressivePhaseLabel(waitingPhase)}
              />
            </Box>

            <LinearProgress
              variant={waitingTotal ? 'determinate' : 'indeterminate'}
              value={waitingTotal ? waitingPercent : undefined}
            />

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              {waitingTotal > 0 && (
                <Typography variant="body2" fontWeight={600}>
                  {waitingReady.toLocaleString()} / {waitingTotal.toLocaleString()} customers ready
                </Typography>
              )}
              {waitingTotal > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {waitingPercent}% prepared
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                Dry Run stays disabled until the server finishes the complete preview so it can validate the exact final dataset.
              </Typography>
            </Box>
          </Paper>
        )}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" fontWeight={600}>Import simulation</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            The simulation uses the same create/merge planning logic as the real import.
          </Typography>
          {dryRunResult ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 1 }}>
              <Typography variant="body2"><strong>Evaluated:</strong> {(drySummary.evaluated || 0).toLocaleString()}</Typography>
              <Typography variant="body2"><strong>Would create:</strong> {(drySummary.creates || 0).toLocaleString()}</Typography>
              <Typography variant="body2"><strong>Would merge:</strong> {(drySummary.merges || 0).toLocaleString()}</Typography>
              <Typography variant="body2"><strong>No changes:</strong> {(drySummary.noChanges || 0).toLocaleString()}</Typography>
              <Typography variant="body2"><strong>Skipped:</strong> {(drySummary.skipped || 0).toLocaleString()}</Typography>
              <Typography variant="body2"><strong>Skipped — Needs Review:</strong> {(drySummary.skippedNeedsReview || 0).toLocaleString()}</Typography>
              <Typography variant="body2"><strong>Blocked:</strong> {(drySummary.blocked || 0).toLocaleString()}</Typography>
              <Typography variant="body2"><strong>Addresses added:</strong> {(drySummary.addressCreates || 0).toLocaleString()}</Typography>
              <Typography variant="body2"><strong>Contacts added:</strong> {(drySummary.contactAdds || 0).toLocaleString()}</Typography>
              <Typography variant="body2"><strong>Contacts enriched:</strong> {(drySummary.contactEnrichments || 0).toLocaleString()}</Typography>
              <Typography variant="body2"><strong>Rep assignments added:</strong> {(drySummary.assignmentAdds || 0).toLocaleString()}</Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">No simulation has been run for the current import decisions.</Typography>
          )}
        </Paper>

        {dryRunResult && !dryRunIsCurrent && (
          <Alert severity="warning">
            Import decisions changed after this Dry Run. Run Dry Run again before importing.
          </Alert>
        )}

        {simulatedRecords.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'grid', gap: 1.5, mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>Simulated database operations</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Every simulated record is available. Blocked records are selected first when present so all blocking issues can be reviewed before import.
                  </Typography>
                </Box>
                <Chip size="small" label={`${simulatedRecords.length.toLocaleString()} total simulated`} />
              </Box>

              <Box sx={{ display: 'flex', gap: .75, flexWrap: 'wrap' }}>
                {dryRunFilterOptions.filter((option) => option.count > 0 || option.key === 'all').map((option) => (
                  <Chip
                    key={option.key}
                    clickable
                    color={dryRunResultFilter === option.key ? 'primary' : 'default'}
                    variant={dryRunResultFilter === option.key ? 'filled' : 'outlined'}
                    label={`${option.label} · ${option.count.toLocaleString()}`}
                    onClick={() => {
                      setDryRunResultFilter(option.key);
                      setDryRunResultPage(1);
                    }}
                  />
                ))}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">
                  Showing {displayedSimulatedRecords.length ? dryRunResultStart + 1 : 0}-{dryRunResultEnd} of {filteredSimulatedRecords.length.toLocaleString()} {dryRunResultFilter === 'all' ? 'records' : dryRunFilterOptions.find((option) => option.key === dryRunResultFilter)?.label.toLowerCase() || 'records'}.
                </Typography>
                {dryRunResultTotalPages > 1 && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={safeDryRunResultPage <= 1}
                      onClick={() => setDryRunResultPage((page) => Math.max(1, page - 1))}
                    >
                      Previous
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      Page {safeDryRunResultPage.toLocaleString()} of {dryRunResultTotalPages.toLocaleString()}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={safeDryRunResultPage >= dryRunResultTotalPages}
                      onClick={() => setDryRunResultPage((page) => Math.min(dryRunResultTotalPages, page + 1))}
                    >
                      Next
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell>Outcome</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Database operations</TableCell>
                  <TableCell>Warnings</TableCell>
                  <TableCell align="right">Inspect</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedSimulatedRecords.map((row) => (
                  <TableRow key={row.recordId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{row.businessName || 'Unnamed customer'}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.recordId}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={String(row.action || '').replaceAll('_', ' ')}
                        color={dryRunOutcomeColor(row.action)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {row.targetCustomerId ? `Customer #${row.targetCustomerId}` : row.action === 'create' ? 'New customer' : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {(row.operations || []).length
                        ? (row.operations || []).map((operation, index) => (
                            <Typography key={`${row.recordId}-op-${index}`} variant="caption" display="block">
                              {operation.operation} {operation.table}{operation.id ? ` #${operation.id}` : ''}
                            </Typography>
                          ))
                        : <Typography variant="caption" color="text.secondary">No database operation</Typography>}
                    </TableCell>
                    <TableCell>
                      {(row.warnings || []).length
                        ? (row.warnings || []).map((warning) => (
                            <Typography key={warning} variant="caption" color={row.action === 'blocked' ? 'error.main' : 'text.secondary'} display="block">
                              {warning}
                            </Typography>
                          ))
                        : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setDryRunInspectRecord(row);
                          setDryRunCopyStatus('');
                        }}
                        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                      >
                        Inspect JSON
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {dryRunResultTotalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center', mt: 1.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={safeDryRunResultPage <= 1}
                  onClick={() => setDryRunResultPage((page) => Math.max(1, page - 1))}
                >
                  Previous
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Page {safeDryRunResultPage.toLocaleString()} of {dryRunResultTotalPages.toLocaleString()}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={safeDryRunResultPage >= dryRunResultTotalPages}
                  onClick={() => setDryRunResultPage((page) => Math.min(dryRunResultTotalPages, page + 1))}
                >
                  Next
                </Button>
              </Box>
            )}
          </Paper>
        )}

        {dryRunResult && (
          <Paper variant="outlined" sx={{ p: 2, display: 'grid', gap: 1.5 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>Optional verification — Test Import</Typography>
              <Typography variant="body2" color="text.secondary">
                Run a small successful sample through Elite Core&apos;s real create/merge persistence code inside a database transaction. Every write is rolled back when the test finishes.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Sample size</InputLabel>
                <Select
                  label="Sample size"
                  value={testImportSize}
                  onChange={(event) => setTestImportSize(Number(event.target.value))}
                >
                  {[1, 5, 10, 25].map((size) => (
                    <MenuItem key={size} value={size}>{size} records</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Sample from</InputLabel>
                <Select
                  label="Sample from"
                  value={testImportMode}
                  onChange={(event) => setTestImportMode(event.target.value)}
                >
                  <MenuItem value="mixed">Successful creates + merges</MenuItem>
                  <MenuItem value="create">Creates only</MenuItem>
                  <MenuItem value="merge">Merges only</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                onClick={runTestImport}
                disabled={testImportRunning || dryRunning || importing || !dryRunIsCurrent}
                sx={{ textTransform: 'none' }}
              >
                {testImportRunning ? 'Validating sample & running…' : !dryRunIsCurrent ? 'Run Dry Run Again First' : 'Run Test Import'}
              </Button>
            </Box>

            <Alert severity="info">
              No customer changes are retained. Test Import always rolls back the transaction after exercising the real persistence path.
            </Alert>

            {testImportRunning && <LinearProgress />}

            {testImportResult && (
              <Box sx={{ display: 'grid', gap: 1.25 }}>
                <Alert severity={testImportResult.failed ? 'warning' : 'success'}>
                  Tested {Number(testImportResult.sampleSize || 0).toLocaleString()} records · {Number(testImportResult.succeeded || 0).toLocaleString()} succeeded · {Number(testImportResult.failed || 0).toLocaleString()} failed · all database changes rolled back.
                </Alert>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Chip size="small" label={`${Number(testImportResult.creates || 0).toLocaleString()} creates tested`} />
                  <Chip size="small" label={`${Number(testImportResult.merges || 0).toLocaleString()} merges tested`} />
                  <Chip size="small" color="success" label="0 retained writes" />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => {
                      setTestImportInspectIndex(0);
                      setTestImportInspectOpen(true);
                    }}
                    disabled={!(testImportResult.results || []).length}
                    sx={{ textTransform: 'none' }}
                  >
                    Compare Before / After
                  </Button>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell>Path</TableCell>
                      <TableCell>Result</TableCell>
                      <TableCell>Customer ID</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(testImportResult.results || []).map((row) => (
                      <TableRow key={row.recordId}>
                        <TableCell>{row.businessName || 'Unnamed customer'}</TableCell>
                        <TableCell>{row.sourceAction === 'update' ? 'Merge existing' : 'Create new'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={row.success ? 'success' : 'error'}
                            label={row.success ? row.action : 'failed'}
                          />
                        </TableCell>
                        <TableCell>{row.customerId || '—'}</TableCell>
                        <TableCell>
                          {row.success ? (
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                const index = (testImportResult.results || []).findIndex((result) => result.recordId === row.recordId);
                                setTestImportInspectIndex(Math.max(0, index));
                                setTestImportInspectOpen(true);
                              }}
                              sx={{ textTransform: 'none' }}
                            >
                              View Before / After
                            </Button>
                          ) : (
                            <Typography variant="caption" color="error.main">{row.error || 'Test failed'}</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>
        )}

        {dryRunIsCurrent && Number(dryRunResult?.summary?.blocked || 0) > 0 && (
          <Alert severity="warning">
            Dry Run completed with {Number(dryRunResult.summary.blocked).toLocaleString()} blocked record{Number(dryRunResult.summary.blocked) === 1 ? '' : 's'}. You can still import the current approved dataset; blocked records may fail individually and will be reported in the import results.
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant={dryRunIsCurrent ? 'outlined' : 'contained'}
            onClick={runDryRun}
            disabled={loading || importing || dryRunning || !previewCommitReady}
            sx={{ textTransform: 'none' }}
          >
            {dryRunning
              ? 'Running Dry Run…'
              : !previewCommitReady
                ? 'Waiting for Preview…'
                : dryRunResult
                  ? 'Run Dry Run Again'
                  : 'Run Dry Run'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={commitImport}
            disabled={loading || importing || dryRunning || !previewCommitReady || !dryRunIsCurrent}
            sx={{ textTransform: 'none' }}
          >
            {destination === 'leads' ? 'Import Leads' : 'Import Customers'}
          </Button>
        </Box>

        {importing && (
          <Alert severity="info">
            Importing {destination}... this modal cannot be closed until the operation finishes.
          </Alert>
        )}
      </Box>
    );
  };

  const renderResultsStep = () => (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Alert severity={importSession?.failedCount ? 'warning' : 'success'}>
        Import complete. Created: {importSession?.results?.summary?.created || 0}. Merged: {importSession?.results?.summary?.merged || 0}.
        Skipped existing: {importSession?.results?.summary?.skippedExisting || 0}. Skipped manually: {importSession?.results?.summary?.skippedManually || 0}.
        No changes: {importSession?.results?.summary?.noChanges || 0}. Failed: {importSession?.results?.summary?.failed || 0}.
      </Alert>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button
          variant="outlined"
          href={`/instance/${instanceUniqueId}/sales/customers${destination === 'leads' ? '/leads' : ''}`}
          sx={{ textTransform: 'none' }}
        >
          {destination === 'leads' ? 'View Leads' : 'View Customers'}
        </Button>
        <Button variant="outlined" onClick={resetWorkflow} sx={{ textTransform: 'none' }}>
          Start Another Import
        </Button>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Business</TableCell>
            <TableCell>Result</TableCell>
            <TableCell>Reason</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(importSession?.results?.records || []).filter((row) => row.action === 'failed' || row.action === 'skipped').map((row) => (
            <TableRow key={row.recordId}>
              <TableCell>{row.businessName || '-'}</TableCell>
              <TableCell>{row.action}</TableCell>
              <TableCell>{row.reason || '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );

  const renderCustomSupplierStep = () => {
    if (activeStep === 0) {
      return (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Import Custom Suppliers</Typography>
            <Typography variant="body2" color="text.secondary">
              Upload supplier data for this instance. New custom suppliers are created as standalone Core Suppliers; existing ASI suppliers are recognized without being re-imported.
            </Typography>
          </Box>

          <Alert severity="info">
            Some ASI suppliers have products that are missing from the ASI catalogue. If that happens, you can create a separate, linked custom supplier to hold your own products. The original ASI supplier remains intact. This spreadsheet imports suppliers and their contacts/addresses, not products.
          </Alert>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={() => downloadCustomSupplierTemplate('flat')} sx={{ textTransform: 'none' }}>
              Download Flat CSV Example
            </Button>
            <Button variant="outlined" onClick={() => downloadCustomSupplierTemplate('related')} sx={{ textTransform: 'none' }}>
              Download Supplier / Contact / Address CSV Example
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Flat CSV: supplier details with contact and address fields on the same row.
            Related rows CSV: separate SUPPLIER, CONTACT and ADDRESS rows connected using the same Company Name.
            Both examples contain fictional data and can be uploaded directly.
          </Typography>

          <Box
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              previewCustomSupplierFile(event.dataTransfer.files?.[0]);
            }}
            sx={{ border: '1px dashed #9aa7b1', borderRadius: 1, p: 4, backgroundColor: '#fff', textAlign: 'center' }}
          >
            <StorefrontOutlinedIcon color="primary" sx={{ fontSize: 44, mb: 1 }} />
            <Typography variant="subtitle1" fontWeight={700}>Drop supplier spreadsheet here</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              CSV, XLS and XLSX supported. Supplier Name is required; common supplier/contact/address headings are detected automatically.
            </Typography>
            <Button variant="contained" onClick={() => inputRef.current?.click()} sx={{ textTransform: 'none' }}>
              Choose Supplier File
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,.xls,.xlsx"
              hidden
              onChange={(event) => {
                const selected = event.target.files?.[0];
                event.target.value = '';
                previewCustomSupplierFile(selected);
              }}
            />
          </Box>

          {customSupplierFile && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700}>{customSupplierFile.name}</Typography>
              <Typography variant="caption" color="text.secondary">{formatBytes(customSupplierFile.size)}</Typography>
            </Paper>
          )}
        </Box>
      );
    }

    if (activeStep === 1) {
      const summary = customSupplierPreview?.summary || {};
      const allRows = customSupplierPreview?.records || [];
      const query = customSupplierSearch.trim().toLowerCase();
      const rows = allRows.filter((row) => {
        if (customSupplierDecisionFilter !== 'all'
            && !(customSupplierDecisionFilter === 'create_custom' && row.action === 'create_asi_custom')
            && row.action !== customSupplierDecisionFilter) return false;
        if (!query) return true;
        return [row.name, row.email, row.existingSupplier?.Name, row.existingSupplier?.Id, row.reason, row.matchType]
          .some((value) => String(value || '').toLowerCase().includes(query));
      });
      const eligibleIds = new Set(allRows.filter(row => !['blocked', 'skip', 'asi_existing'].includes(row.action)).map(row => row.id));
      const selectedEligibleIds = selectedCustomSupplierIds.filter(id => eligibleIds.has(id));
      const pageSize = 100;
      const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
      const safePage = Math.min(customSupplierPage, totalPages);
      const start = rows.length ? (safePage - 1) * pageSize : 0;
      const visibleRows = rows.slice(start, start + pageSize);
      const actionLabel = (action) => ({
        create_custom: 'Create custom',
        create_asi_custom: 'Create custom (ASI linked)',
        update_instance: 'Enrich existing custom',
        connect_existing: 'Attach locally',
        asi_existing: 'Already in ASI',
        blocked: 'Blocked',
        skip: 'Skip',
      }[action] || action);

      return (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Preview supplier import</Typography>
            <Typography variant="body2" color="text.secondary">
              Nothing has been written yet. Imported supplier spreadsheets create or enrich your own instance-owned custom suppliers by default.
              Existing ASI catalogues remain unchanged; uniquely matched ASI suppliers are linked to the new custom record.
              Existing supplier values are preserved; contacts and addresses merge without replacing saved details. Select 1 or 5 suppliers with the checkboxes.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip label={`${Number(summary.supplierCandidates || 0).toLocaleString()} suppliers`} />
            <Chip color="success" label={`${Number(summary.createCustom || 0).toLocaleString()} create custom`} />
            {Number(summary.linkExistingAsi || 0) > 0 && <Chip color="success" label={`${summary.linkExistingAsi} existing custom + ASI linked`} />}
            {Number(summary.asiCustomCompanions || 0) > 0 && (
              <Chip label={`${Number(summary.asiCustomCompanions).toLocaleString()} linked to ASI`} />
            )}
            <Chip color="info" label={`${Number(summary.connectExisting || 0).toLocaleString()} connect existing`} />
            {Number(summary.alreadyInAsi || 0) > 0 && <Chip label={`${Number(summary.alreadyInAsi).toLocaleString()} already in ASI · no import`} />}
            {allRows.some(row => row.matchType === 'asi_custom_default') && (
              <Chip color="success" label={`${allRows.filter(row => row.matchType === 'asi_custom_default').length} linked custom suppliers selected by default`} />
            )}
            {allRows.some(row => row.matchType === 'asi_local_default') && (
              <Chip color="success" label={`${allRows.filter(row => row.matchType === 'asi_local_default').length} ASI contact imports selected by default`} />
            )}
            <Chip label={`${Number(summary.updateInstance || 0).toLocaleString()} custom suppliers to enrich`} />
            {Number(summary.blocked || 0) > 0 && <Chip color="error" label={`${Number(summary.blocked).toLocaleString()} blocked`} />}
          </Box>

          {asiBatchCandidates.length > 0 && (
            <Alert severity="info" action={
              <Button size="small" color="inherit" onClick={() => {
                setAsiBatchSelectedIds(asiBatchCandidates.map(({ record }) => record.id));
                setAsiBatchReviewOpen(true);
              }} disabled={loading}>
                Review ASI matches ({asiBatchCandidates.length})
              </Button>
            }>
              Some ASI identities need review. Confirm each suggested relationship before importing; uncertain associations are never linked automatically.
            </Alert>
          )}

          {Number(summary.blocked || 0) > 0 && (
            <Alert severity="error">
              Review blocked supplier matches to select an existing Core Supplier or skip the record. Nothing is changed until import.
            </Alert>
          )}

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
              <TextField
                size="small"
                label="Search suppliers"
                placeholder="Supplier name, email, Core Supplier ID..."
                value={customSupplierSearch}
                onChange={(event) => {
                  setCustomSupplierSearch(event.target.value);
                  setCustomSupplierPage(1);
                }}
                sx={{ flex: '1 1 290px', maxWidth: 480 }}
                inputProps={{ 'aria-label': 'Search supplier import candidates' }}
              />
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <InputLabel id="custom-supplier-decision-filter-label">Decision</InputLabel>
                <Select
                  labelId="custom-supplier-decision-filter-label"
                  label="Decision"
                  value={customSupplierDecisionFilter}
                  onChange={(event) => {
                    setCustomSupplierDecisionFilter(event.target.value);
                    setCustomSupplierPage(1);
                  }}
                >
                  <MenuItem value="all">All decisions</MenuItem>
                  <MenuItem value="create_custom">Create custom</MenuItem>
                  <MenuItem value="connect_existing">Connect existing</MenuItem>
                  <MenuItem value="asi_existing">Already in ASI</MenuItem>
                  <MenuItem value="update_instance">Enrich existing custom</MenuItem>
                  <MenuItem value="blocked">Blocked</MenuItem>
                  <MenuItem value="skip">Skipped</MenuItem>
                </Select>
              </FormControl>
              {(customSupplierSearch || customSupplierDecisionFilter !== 'all') && (
                <Button size="small" onClick={() => {
                  setCustomSupplierSearch('');
                  setCustomSupplierDecisionFilter('all');
                  setCustomSupplierPage(1);
                }}>Clear filters</Button>
              )}
            </Box>
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>Detected field mapping</Typography>
              <Typography variant="caption" color="text.secondary">
                {Object.entries(customSupplierPreview?.mapping || {}).map(([field, header]) => `${field} → ${header}`).join(' · ') || 'No fields detected'}
              </Typography>
            </Box>

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Showing {visibleRows.length ? start + 1 : 0}-{start + visibleRows.length} of {rows.length.toLocaleString()} matching suppliers ({allRows.length.toLocaleString()} total).
            </Typography>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Test</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Decision</TableCell>
                  <TableCell>Matched Core Supplier</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" disabled={!eligibleIds.has(row.id) || customSupplierImporting}
                        checked={selectedEligibleIds.includes(row.id)}
                        inputProps={{ 'aria-label': `Select ${row.name} for test import` }}
                        onChange={(event) => setSelectedCustomSupplierIds(previous => event.target.checked
                          ? [...previous.filter(id => eligibleIds.has(id)), row.id]
                          : previous.filter(id => id !== row.id))} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{row.name || 'Unnamed supplier'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(row.sourceRows || []).length.toLocaleString()} source row{row.sourceRows?.length === 1 ? '' : 's'} · {(row.contacts || []).length} contact{row.contacts?.length === 1 ? '' : 's'} · {(row.addresses || []).length} address{row.addresses?.length === 1 ? '' : 'es'}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.email || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={row.action === 'blocked' ? 'error' : ['create_custom', 'create_asi_custom'].includes(row.action) ? 'success' : row.action === 'asi_existing' ? 'default' : 'info'}
                        label={row.matchType === 'asi_local_default' ? 'Attach locally · Default selected' : actionLabel(row.action)}
                      />
                    </TableCell>
                    <TableCell>
                      {row.existingSupplier
                        ? `#${row.existingSupplier.Id} · ${row.existingSupplier.Name}`
                        : '—'}
                      {hasAsiSupplierReference(row.existingSupplier) && (
                        <Typography variant="caption" color="text.secondary" display="block">ASI catalogue record</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.reason || row.matchType?.replaceAll('_', ' ') || '—'}</Typography>
                      {row.poEmailSuggestion && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
                          ASI PO: {row.poEmailSuggestion.mode === 'contact'
                            ? `Orders contact suggested — ${row.poEmailSuggestion.contactEmail} (if no saved preference)`
                            : 'Supplier.Email default'}
                        </Typography>
                      )}
                      <Button size="small" onClick={() => setCustomSupplierDetailsId(row.id)} sx={{ textTransform: 'none', pl: 0 }}>
                        {row.action === 'blocked' ? 'Resolve match' : ['asi_existing', 'asi_local_default', 'asi_local_opt_in'].includes(row.matchType) || row.action === 'asi_existing' ? 'ASI options' : 'View details'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        No suppliers match your search or decision filter.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center', mt: 1.5 }}>
                <Button size="small" variant="outlined" disabled={safePage <= 1} onClick={() => setCustomSupplierPage((page) => Math.max(1, page - 1))}>
                  Previous
                </Button>
                <Typography variant="caption" color="text.secondary">Page {safePage} of {totalPages}</Typography>
                <Button size="small" variant="outlined" disabled={safePage >= totalPages} onClick={() => setCustomSupplierPage((page) => Math.min(totalPages, page + 1))}>
                  Next
                </Button>
              </Box>
            )}
          </Paper>

          <Alert severity="warning">
            Test imports write real records. Select exactly 1 or 5 eligible suppliers using the checkboxes. Search and filters only affect display. Import All is unchanged.
          </Alert>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={() => setWorkflowStep(0)} sx={{ textTransform: 'none' }}>
              Choose Different File
            </Button>
            <Button
              variant="outlined"
              disabled={customSupplierImporting || selectedEligibleIds.length !== 1}
              onClick={() => commitCustomSupplierImport(1)}
              sx={{ textTransform: 'none' }}
            >
              Import 1 (test)
            </Button>
            <Button
              variant="outlined"
              disabled={customSupplierImporting || selectedEligibleIds.length !== 5}
              onClick={() => commitCustomSupplierImport(5)}
              sx={{ textTransform: 'none' }}
            >
              Import 5 (test)
            </Button>
            <Button
              variant="contained"
              disabled={!customSupplierPreview?.commitEnabled || customSupplierImporting}
              onClick={() => commitCustomSupplierImport(null)}
              sx={{ textTransform: 'none' }}
            >
              {customSupplierImporting ? 'Importing Suppliers…' : 'Import All Suppliers'}
            </Button>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Alert severity="success">
          {customSupplierTestCount ? `Test import complete: ${customSupplierTestCount} supplier(s) processed.` : 'Custom supplier import complete.'}
        </Alert>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip color="success" label={`${Number(customSupplierResult?.created || 0).toLocaleString()} custom suppliers created`} />
          <Chip color="info" label={`${Number(customSupplierResult?.connected || 0).toLocaleString()} existing Core Suppliers connected`} />
          <Chip label={`${Number(customSupplierResult?.updated || 0).toLocaleString()} instance suppliers updated`} />
          <Chip label={`${Number(customSupplierResult?.connectionsCreated || 0).toLocaleString()} instance links created`} />
          {Number(customSupplierResult?.poEmailPreferencesCreated || 0) > 0 && (
            <Chip color="success" label={`${Number(customSupplierResult.poEmailPreferencesCreated).toLocaleString()} ASI PO email defaults set`} />
          )}
          {Number(customSupplierResult?.skipped || 0) > 0 && <Chip label={`${customSupplierResult.skipped} skipped`} />}
        </Box>
        {(customSupplierResult?.records || []).length > 0 && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Processed suppliers</Typography>
            {(customSupplierResult.records || []).map((item, index) => (
              <Typography key={`${item.supplierId || index}`} variant="body2" sx={{ py: 0.5 }}>
                #{item.supplierId} · {item.name} · {(item.action || '').replaceAll('_', ' ')}
                {item.poEmailDecision === 'orders-contact-selected' ? ' · Orders PO recipient selected'
                  : item.poEmailDecision === 'existing-preference-kept' ? ' · Existing PO recipient preference preserved'
                    : item.poEmailDecision === 'supplier-email-fallback' ? ' · PO recipient uses Supplier.Email'
                      : item.poEmailDecision === 'migration-required' ? ' · PO preference unavailable: migration required'
                        : ''}
              </Typography>
            ))}
          </Paper>
        )}
        <Alert severity="info">
          New custom suppliers remain complete standalone CoreSupplier records. For matched shared suppliers, only the instance-specific ConnectedSupplier relationship receives imported overrides, contacts and addresses.
        </Alert>
        <Button variant="outlined" onClick={resetWorkflow} sx={{ justifySelf: 'start', textTransform: 'none' }}>
          Start Another Import
        </Button>
      </Box>
    );
  };

  const renderDecoratorMatrixStep = () => {
    if (activeStep === 0) {
      return (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Import Decoration Matrix</Typography>
            <Typography variant="body2" color="text.secondary">
              Upload the .xlsx workbook created by Company Settings → Decorator Matrix → Export.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Import reads the Raw sheets so pricing tables, charge prices, material rules and production routing can be rebuilt exactly. Matrix Review notes and Action columns are not imported.
            </Typography>
          </Box>
          <Alert severity="warning">
            This import replaces the active decoration charge matrix. Existing active charges are archived first so historical orders retain their original charge records.
          </Alert>
          <Box
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              previewDecoratorMatrixFile(event.dataTransfer.files?.[0]);
            }}
            sx={{ border: '1px dashed #9aa7b1', borderRadius: 1, p: 4, backgroundColor: '#fff', textAlign: 'center' }}
          >
            <FileUploadOutlinedIcon color="primary" sx={{ fontSize: 44, mb: 1 }} />
            <Typography variant="subtitle1" fontWeight={700}>Drop decorator matrix workbook here</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Elite Core validates the workbook and shows exactly what will replace the current active matrix before anything is written.
            </Typography>
            <Button variant="contained" onClick={() => inputRef.current?.click()} sx={{ textTransform: 'none' }}>
              Choose Matrix Workbook
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx"
              hidden
              onChange={(event) => {
                const selected = event.target.files?.[0];
                event.target.value = '';
                previewDecoratorMatrixFile(selected);
              }}
            />
          </Box>
          {decoratorMatrixFile && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700}>{decoratorMatrixFile.name}</Typography>
              <Typography variant="caption" color="text.secondary">{formatBytes(decoratorMatrixFile.size)}</Typography>
            </Paper>
          )}
        </Box>
      );
    }

    if (activeStep === 1) {
      const summary = decoratorMatrixPreview?.summary || {};
      const rows = decoratorMatrixPreview?.rows || [];
      const pageSize = 100;
      const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
      const safePage = Math.min(decoratorMatrixPage, totalPages);
      const start = rows.length ? (safePage - 1) * pageSize : 0;
      const visibleRows = rows.slice(start, start + pageSize);
      return (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Preview replacement</Typography>
            <Typography variant="body2" color="text.secondary">
              Review the incoming matrix. Matching charge IDs are updated in place, genuinely new charges are created, and charges missing from the incoming matrix can be archived.
            </Typography>
          </Box>

          <Alert severity={decoratorMatrixPreview?.commitEnabled ? 'warning' : 'error'}>
            {decoratorMatrixPreview?.commitEnabled
              ? decoratorMatrixArchiveExisting
                ? `${Number(summary.matchedExistingChargesToUpdate || 0).toLocaleString()} existing charges will be updated (including ${Number(summary.matchedBlankIdChargesByName || 0).toLocaleString()} blank-ID rows matched by decorator + charge name), ${Number(summary.restoredExistingCharges || 0).toLocaleString()} archived charges will be restored in place, ${Number(summary.newChargesToCreate || 0).toLocaleString()} genuinely new charges will be created, and ${Number(summary.currentActiveChargesToArchive || 0).toLocaleString()} active charges not represented by the incoming active plan will be archived.`
                : `${Number(summary.matchedExistingChargesToUpdate || 0).toLocaleString()} existing charges will be updated (including ${Number(summary.matchedBlankIdChargesByName || 0).toLocaleString()} blank-ID rows matched by decorator + charge name), ${Number(summary.restoredExistingCharges || 0).toLocaleString()} archived charges will be restored in place, and ${Number(summary.newChargesToCreate || 0).toLocaleString()} genuinely new charges will be created. Explicit Matrix Review retirements still archive; other missing active charges remain active.`
              : 'This workbook has blocking issues and cannot be imported yet.'}
          </Alert>

          <FormControlLabel
            control={
              <Checkbox
                checked={decoratorMatrixArchiveExisting}
                onChange={(event) => setDecoratorMatrixArchiveExisting(event.target.checked)}
              />
            }
            label="Archive current charges that are missing from this import"
          />

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip label={`${Number(summary.decoratorsInFile || 0).toLocaleString()} decorators in file`} />
            <Chip color="success" label={`${Number(summary.decoratorsMatched || 0).toLocaleString()} decorators matched`} />
            <Chip color="primary" label={`${Number(summary.matchedExistingChargesToUpdate || 0).toLocaleString()} matching charges to update`} />
            <Chip color="success" label={`${Number(summary.newChargesToCreate || 0).toLocaleString()} new charges to create`} />
            <Chip label={`${Number(summary.chargesInFile || 0).toLocaleString()} total named charges in import`} />
            {Number(summary.skippedUnnamedCharges || 0) > 0 && (
              <Chip color="warning" label={`${Number(summary.skippedUnnamedCharges).toLocaleString()} unnamed charges skipped`} />
            )}
            <Chip label={`${Number(summary.rangeTableEntries || 0).toLocaleString()} range rows`} />
            <Chip label={`${Number(summary.chargePrices || 0).toLocaleString()} charge prices`} />
            <Chip label={`${Number(summary.materialRules || 0).toLocaleString()} material rules`} />
            <Chip label={`${Number(summary.productionRouting || 0).toLocaleString()} routing rows`} />
            {Number(summary.blocked || 0) > 0 && <Chip color="error" label={`${Number(summary.blocked).toLocaleString()} blocked`} />}
          </Box>

          {(decoratorMatrixPreview?.warnings || []).length > 0 && (
            <Alert severity="info">
              {(decoratorMatrixPreview.warnings || []).join(' · ')}
            </Alert>
          )}

          {(decoratorMatrixPreview?.blocked || []).length > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Blocking decorator matches</Typography>
              {(decoratorMatrixPreview.blocked || []).slice(0, 100).map((item, index) => (
                <Typography key={`${item.sourceChargeId || index}`} variant="body2" color="error.main">
                  {item.message || `${item.chargeName || 'Unnamed charge'} → ${item.decoratorTitle || `Decorator ID ${item.sourceDecoratorId || 'missing'}`}`}
                </Typography>
              ))}
            </Paper>
          )}

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Incoming charges</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Showing {`${visibleRows.length ? start + 1 : 0}-${start + visibleRows.length} of ${rows.length.toLocaleString()} incoming charges.`}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Decorator</TableCell>
                  <TableCell>Charge</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Pricing</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row) => (
                  <TableRow key={`${row.sourceChargeId || row.row}`}>
                    <TableCell>{row.decorator || '—'}</TableCell>
                    <TableCell>{row.chargeName || 'Unnamed charge'}</TableCell>
                    <TableCell>{row.chargeType || '—'}</TableCell>
                    <TableCell>{row.calculationType || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={row.status === 'ready' ? 'success' : row.status === 'skipped' ? 'warning' : 'error'}
                        label={row.status}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center', mt: 1.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={safePage <= 1}
                  onClick={() => setDecoratorMatrixPage((page) => Math.max(1, page - 1))}
                >
                  Previous
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Page {safePage} of {totalPages}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={safePage >= totalPages}
                  onClick={() => setDecoratorMatrixPage((page) => Math.min(totalPages, page + 1))}
                >
                  Next
                </Button>
              </Box>
            )}
          </Paper>


          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={() => setWorkflowStep(0)} sx={{ textTransform: 'none' }}>
              Choose Different File
            </Button>
            <Button
              variant="contained"
              disabled={!decoratorMatrixPreview?.commitEnabled || decoratorMatrixDryRunning || decoratorMatrixImporting}
              onClick={runDecoratorMatrixDryRun}
              sx={{ textTransform: 'none' }}
            >
              {decoratorMatrixDryRunning ? 'Building Dry Run…' : 'Run Detailed Dry Run'}
            </Button>
            <Button
              variant="outlined"
              color="warning"
              disabled={!decoratorMatrixPreview?.commitEnabled || decoratorMatrixImporting || decoratorMatrixDryRunning}
              onClick={commitDecoratorMatrixImport}
              sx={{ textTransform: 'none' }}
            >
              {decoratorMatrixImporting ? 'Importing Matrix…' : 'Skip Dry Run & Import'}
            </Button>
          </Box>
        </Box>
      );
    }

    if (activeStep === 2) {
      const dryRun = decoratorMatrixDryRun || {};
      const summary = dryRun.summary || {};
      const allReports = Array.isArray(dryRun.reports) ? dryRun.reports : [];
      const filteredReports = decoratorMatrixDryRunFilter === 'all'
        ? allReports
        : decoratorMatrixDryRunFilter === 'meaningful'
          ? allReports.filter((item) => item.action !== 'update' || (item.fieldChanges || []).length > 0)
          : decoratorMatrixDryRunFilter === 'unchanged'
            ? allReports.filter((item) => item.action === 'update' && (item.fieldChanges || []).length === 0)
            : allReports.filter((item) => item.action === decoratorMatrixDryRunFilter);
      const pageSize = 100;
      const totalPages = Math.max(1, Math.ceil(filteredReports.length / pageSize));
      const safePage = Math.min(decoratorMatrixDryRunPage, totalPages);
      const start = filteredReports.length ? (safePage - 1) * pageSize : 0;
      const visibleReports = filteredReports.slice(start, start + pageSize);

      const actionColor = (action) => ({
        update: 'primary',
        restore: 'info',
        create: 'success',
        archive: 'warning',
        skip: 'default',
      }[action] || 'default');


      const parseStructuredPreviewValue = (value) => {
        if (value == null) return null;
        if (typeof value === 'object') return value;
        if (typeof value !== 'string') return value;
        const trimmed = value.trim();
        if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('['))) return value;
        try {
          return JSON.parse(trimmed);
        } catch (_error) {
          return value;
        }
      };

      const renderStructuredPreview = (value) => {
        const parsed = parseStructuredPreviewValue(value);

        if (Array.isArray(parsed)) {
          if (!parsed.length) {
            return <Typography variant="body2" color="text.secondary">No rows</Typography>;
          }
          if (parsed.every((row) => row && typeof row === 'object' && !Array.isArray(row))) {
            const columns = Array.from(new Set(parsed.flatMap((row) => Object.keys(row))));
            return (
              <Box sx={{ overflowX: 'auto', maxWidth: 560 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => <TableCell key={column}>{column}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsed.map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        {columns.map((column) => (
                          <TableCell key={column}>{String(row?.[column] ?? '—')}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            );
          }
        }

        if (parsed && typeof parsed === 'object') {
          if (Array.isArray(parsed.headers) && Array.isArray(parsed.rows)) {
            const headers = parsed.headers;
            return (
              <Box sx={{ overflowX: 'auto', maxWidth: 680 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {headers.map((header) => <TableCell key={header}>{header}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsed.rows.map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        {headers.map((header) => (
                          <TableCell key={header}>{String(row?.[header] ?? '—')}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            );
          }

          const entries = Object.entries(parsed);
          if (entries.length) {
            return (
              <Table size="small">
                <TableBody>
                  {entries.map(([key, entryValue]) => (
                    <TableRow key={key}>
                      <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{key}</TableCell>
                      <TableCell sx={{ overflowWrap: 'anywhere' }}>
                        {typeof entryValue === 'object' ? JSON.stringify(entryValue) : String(entryValue ?? '—')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            );
          }
        }

        return (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {String(parsed ?? '—')}
          </Typography>
        );
      };

      const openValuePreview = (event, change) => {
        setDecoratorMatrixValuePreview({
          anchorEl: event.currentTarget,
          field: change.field,
          before: change.before,
          after: change.after,
        });
      };

      return (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Detailed dry run</Typography>
            <Typography variant="body2" color="text.secondary">
              No database writes were made. Relevant changes are shown first; use the filter to inspect unchanged matches or every planned operation.
            </Typography>
          </Box>

          <Alert severity="info">
            {Number(summary.update || 0).toLocaleString()} existing charges would be updated,{' '}
            {Number(summary.restore || 0).toLocaleString()} archived charges would be restored,{' '}
            {Number(summary.create || 0).toLocaleString()} charges would be created,{' '}
            {Number(summary.archive || 0).toLocaleString()} charges would be archived, and{' '}
            {Number(summary.skip || 0).toLocaleString()} source rows would be skipped.
          </Alert>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip color="primary" label={`${Number(summary.update || 0).toLocaleString()} updates`} />
            {Number(summary.restore || 0) > 0 && (
              <Chip color="info" label={`${Number(summary.restore || 0).toLocaleString()} restores`} />
            )}
            <Chip color="success" label={`${Number(summary.create || 0).toLocaleString()} creates`} />
            <Chip color="warning" label={`${Number(summary.archive || 0).toLocaleString()} archives`} />
            <Chip label={`${Number(summary.skip || 0).toLocaleString()} skips`} />
            <Chip color="warning" label={`${Number(summary.updatedWithFieldChanges || 0).toLocaleString()} matched charges with material changes`} />
            <Chip color="success" label={`${Number(summary.updatedWithoutFieldChanges || 0).toLocaleString()} matched charges unchanged`} />
            {Number(summary.matchedBlankIdByName || 0) > 0 && (
              <Chip color="info" label={`${Number(summary.matchedBlankIdByName).toLocaleString()} blank-ID rows matched to existing charges by name`} />
            )}
            {Number(summary.createdFromUnmatchedSourceId || 0) > 0 && (
              <Chip color="warning" label={`${Number(summary.createdFromUnmatchedSourceId).toLocaleString()} creates from unmatched source IDs`} />
            )}
            {Number(summary.createdWhereArchivedSourceExists || 0) > 0 && (
              <Chip color="warning" label={`${Number(summary.createdWhereArchivedSourceExists).toLocaleString()} source IDs already exist archived`} />
            )}
          </Box>

          <FormControl size="small" sx={{ maxWidth: 260 }}>
            <InputLabel>Show actions</InputLabel>
            <Select
              label="Show actions"
              value={decoratorMatrixDryRunFilter}
              onChange={(event) => {
                setDecoratorMatrixDryRunFilter(event.target.value);
                setDecoratorMatrixDryRunPage(1);
              }}
            >
              <MenuItem value="meaningful">Relevant changes only</MenuItem>
              <MenuItem value="all">All operations</MenuItem>
              <MenuItem value="update">All matched charges</MenuItem>
              <MenuItem value="restore">Restores</MenuItem>
              <MenuItem value="unchanged">Matched with no material changes</MenuItem>
              <MenuItem value="create">Creates</MenuItem>
              <MenuItem value="archive">Archives</MenuItem>
              <MenuItem value="skip">Skipped rows</MenuItem>
            </Select>
          </FormControl>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Planned database operations</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Showing {`${visibleReports.length ? start + 1 : 0}-${start + visibleReports.length} of ${filteredReports.length.toLocaleString()} operations.`}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Action</TableCell>
                  <TableCell>Charge ID</TableCell>
                  <TableCell>Decorator</TableCell>
                  <TableCell>Charge</TableCell>
                  <TableCell>What changes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleReports.map((item, index) => {
                  const fieldNames = (item.fieldChanges || []).map((change) => change.field);
                  const formatDryRunValue = (value) => {
                    if (value == null) return 'null';
                    const rendered = typeof value === 'object' ? JSON.stringify(value) : String(value);
                    return rendered.length > 220 ? `${rendered.slice(0, 217)}…` : rendered;
                  };
                  const childText = item.childReplacement
                    ? Object.entries(item.childReplacement)
                        .filter(([, counts]) => Number(counts.before || 0) !== 0 || Number(counts.after || 0) !== 0)
                        .map(([name, counts]) => `${name}: ${counts.before} → ${counts.after}`)
                        .join(' · ')
                    : item.incomingChildren
                      ? Object.entries(item.incomingChildren)
                          .filter(([, count]) => Number(count || 0) !== 0)
                          .map(([name, count]) => `${name}: ${count}`)
                          .join(' · ')
                      : item.childRowsPreserved
                        ? (() => {
                            const preserved = Object.entries(item.childRowsPreserved)
                              .filter(([, count]) => Number(count || 0) !== 0)
                              .map(([name, count]) => `${name} ${count}`)
                              .join(' · ');
                            return preserved ? `Archived children preserved: ${preserved}` : '';
                          })()
                        : '';

                  return (
                    <TableRow key={`${item.action}-${item.chargeId || item.incomingChargeId || item.row || index}-${index}`}>
                      <TableCell>
                        <Chip size="small" color={actionColor(item.action)} label={item.action} />
                      </TableCell>
                      <TableCell>
                        {item.chargeId || item.incomingChargeId || 'New ID'}
                        {item.action === 'create' && item.incomingChargeId && (
                          <Typography variant="caption" color="warning.main" display="block">
                            Source ID not active
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{item.decorator || '—'}</TableCell>
                      <TableCell>{item.chargeName || 'Unnamed charge'}</TableCell>
                      <TableCell sx={{ minWidth: 320 }}>
                        {item.action === 'update' && fieldNames.length === 0 ? (
                          <Typography variant="caption" color="success.main" display="block">
                            No material charge-field changes.
                          </Typography>
                        ) : (
                          <>
                            <Typography variant="body2">{item.reason || '—'}</Typography>
                            {fieldNames.length > 0 && (
                              <Box sx={{ mt: 0.5, display: 'grid', gap: 0.25 }}>
                                {(item.fieldChanges || []).map((change) => (
                                  <Box
                                    key={change.field}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ overflowWrap: 'anywhere', minWidth: 0, flex: '1 1 260px' }}
                                    >
                                      <strong>{change.field}:</strong> {formatDryRunValue(change.before)} → {formatDryRunValue(change.after)}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label="Preview"
                                      onMouseEnter={(event) => openValuePreview(event, change)}
                                      onClick={(event) => openValuePreview(event, change)}
                                      sx={{ cursor: 'pointer', height: 22 }}
                                    />
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </>
                        )}
                        {childText && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                            Related rows: {childText}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center', mt: 1.5 }}>
                <Button size="small" variant="outlined" disabled={safePage <= 1} onClick={() => setDecoratorMatrixDryRunPage((page) => Math.max(1, page - 1))}>
                  Previous
                </Button>
                <Typography variant="caption" color="text.secondary">Page {safePage} of {totalPages}</Typography>
                <Button size="small" variant="outlined" disabled={safePage >= totalPages} onClick={() => setDecoratorMatrixDryRunPage((page) => Math.min(totalPages, page + 1))}>
                  Next
                </Button>
              </Box>
            )}
          </Paper>

          <Popover
            open={Boolean(decoratorMatrixValuePreview?.anchorEl)}
            anchorEl={decoratorMatrixValuePreview?.anchorEl || null}
            onClose={() => setDecoratorMatrixValuePreview(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            disableRestoreFocus
            PaperProps={{
              sx: {
                p: 2,
                width: 'min(920px, calc(100vw - 48px))',
                maxHeight: '70vh',
                overflow: 'auto',
              },
              onMouseLeave: () => setDecoratorMatrixValuePreview(null),
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              {decoratorMatrixValuePreview?.field || 'Field'} preview
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Current
                </Typography>
                {renderStructuredPreview(decoratorMatrixValuePreview?.before)}
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Incoming
                </Typography>
                {renderStructuredPreview(decoratorMatrixValuePreview?.after)}
              </Paper>
            </Box>
          </Popover>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={() => setWorkflowStep(1)} sx={{ textTransform: 'none' }}>
              Back to Preview
            </Button>
            <Button
              variant="contained"
              color="warning"
              disabled={!decoratorMatrixPreview?.commitEnabled || decoratorMatrixImporting}
              onClick={commitDecoratorMatrixImport}
              sx={{ textTransform: 'none' }}
            >
              {decoratorMatrixImporting
                ? 'Importing Matrix…'
                : decoratorMatrixArchiveExisting
                  ? 'Import Exactly This Plan'
                  : 'Import Updates & Adds'}
            </Button>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'grid', gap: 2 }}>

        <Alert severity="success">
          Decoration matrix import complete.
        </Alert>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip color="primary" label={`${Number(decoratorMatrixResult?.updatedCharges || 0).toLocaleString()} existing charges updated`} />
          {Number(decoratorMatrixResult?.restoredCharges || 0) > 0 && (
            <Chip color="info" label={`${Number(decoratorMatrixResult.restoredCharges).toLocaleString()} archived charges restored`} />
          )}
          <Chip color="success" label={`${Number(decoratorMatrixResult?.createdCharges || 0).toLocaleString()} new charges created`} />
          <Chip label={`${Number(decoratorMatrixResult?.archivedCharges || 0).toLocaleString()} charges archived`} />
          {Number(decoratorMatrixResult?.skippedUnnamedCharges || 0) > 0 && (
            <Chip color="warning" label={`${Number(decoratorMatrixResult.skippedUnnamedCharges).toLocaleString()} unnamed charges skipped`} />
          )}
          <Chip label={`${Number(decoratorMatrixResult?.rangeTableEntries || 0).toLocaleString()} range rows`} />
          <Chip label={`${Number(decoratorMatrixResult?.chargePrices || 0).toLocaleString()} charge prices`} />
          <Chip label={`${Number(decoratorMatrixResult?.materialRules || 0).toLocaleString()} material rules`} />
          <Chip label={`${Number(decoratorMatrixResult?.productionRouting || 0).toLocaleString()} routing rows`} />
        </Box>
        <Alert severity="info">
          Archived legacy charges remain in the database for historical orders. When an older order already references one, Edit Item can hydrate it as a disabled “Archived / legacy” option; it is not selectable for new charges.
        </Alert>
        <Button variant="outlined" onClick={resetWorkflow} sx={{ justifySelf: 'start', textTransform: 'none' }}>
          Start Another Import
        </Button>
      </Box>
    );
  };

  const renderToolPicker = () => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: 2,
      }}
    >
      {importOptions.filter((option) => !option.administratorOnly || canRepairContacts).map((option) => (
        <Card
          key={option.key}
          elevation={3}
          onClick={() => option.key === 'contact-repair' ? onOpenContactRepair?.() : selectTool(option.key)}
          sx={{
            padding: 2,
            cursor: 'pointer',
            border: '1px solid #e0e0e0',
            '&:hover': { boxShadow: 6, backgroundColor: '#f9f9f9' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {option.icon}
            <CardContent sx={{ padding: '0 !important' }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {option.title}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                {option.description}
              </Typography>
            </CardContent>
          </Box>
        </Card>
      ))}
    </Box>
  );

  const artworkFolders = artworkSession?.analysis?.customerFolders || [];
  const filteredArtworkFolders = artworkFolders.filter((folder) => {
    const mapped = artworkSession?.folderMappings?.[folder.key];
    const status = mapped?.status || folder.status;
    if (artworkFilter === 'all') return true;
    if (artworkFilter === 'matched') return status === 'matched';
    if (artworkFilter === 'suggested') return folder.suggestedCustomer && status !== 'matched';
    if (artworkFilter === 'unmapped') return status === 'unmapped' || status === 'multiple';
    if (artworkFilter === 'ignored') return status === 'ignored';
    return true;
  });

  const renderArtworkUploadStep = () => (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Box
        onDragOver={(event) => event.preventDefault()}
        onDrop={handleArtworkDrop}
        sx={{
          border: '1px dashed #9aa7b1',
          borderRadius: 1,
          p: 3,
          backgroundColor: '#fff',
          textAlign: 'center',
        }}
      >
        <DriveFolderUploadOutlinedIcon color="primary" sx={{ fontSize: 46, mb: 1 }} />
        <Typography variant="subtitle1" fontWeight={600}>Drag a folder here</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload an existing artwork library while preserving its folder structure.
        </Typography>
        <Button variant="contained" onClick={() => inputRef.current?.click()} sx={{ textTransform: 'none' }}>
          Choose Folder
        </Button>
        <input
          ref={inputRef}
          type="file"
          hidden
          multiple
          webkitdirectory=""
          directory=""
          onChange={(event) => handleArtworkFiles(event.target.files)}
        />
      </Box>
      {artworkManifesting && (
        <Alert severity="info">
          Building artwork manifest and fingerprints. Large folders may take a few minutes.
        </Alert>
      )}
      {artworkSession && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={600}>{artworkSession.sourceName}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            <Chip size="small" label={`${artworkSession.totalFiles} files`} />
            <Chip size="small" label={formatBytes(artworkSession.totalBytes)} />
            <Chip size="small" color="warning" label={`${artworkSession.duplicateFiles} duplicates`} />
            <Chip size="small" color="error" label={`${artworkSession.failedFiles} require attention`} />
          </Box>
        </Paper>
      )}
    </Box>
  );

  const renderArtworkAnalyzeStep = () => (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <FormControl size="small" sx={{ maxWidth: 280 }}>
        <InputLabel>Customer folders begin at</InputLabel>
        <Select
          label="Customer folders begin at"
          value={artworkFolderLevel}
          onChange={(event) => reanalyzeArtwork(Number(event.target.value))}
        >
          <MenuItem value={1}>Level 1</MenuItem>
          <MenuItem value={2}>Level 2</MenuItem>
          <MenuItem value={3}>Level 3</MenuItem>
        </Select>
      </FormControl>
      {artworkSession?.analysis && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip label={`${artworkSession.analysis.totalFiles} files`} />
          <Chip label={`${artworkSession.analysis.folderCount} folders`} />
          <Chip label={`${artworkSession.analysis.topLevelFolderCount} top-level folders`} />
          <Chip color="success" label={`${artworkSession.analysis.uniqueFiles} unique files`} />
          <Chip color="warning" label={`${artworkSession.analysis.duplicateFiles} exact duplicates`} />
          <Chip color="error" label={`${artworkSession.analysis.attentionFiles} require attention`} />
        </Box>
      )}
    </Box>
  );

  const renderArtworkMappingStep = () => (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {[
          ['all', 'All'],
          ['matched', 'Matched'],
          ['suggested', 'Suggested'],
          ['unmapped', 'Unmapped'],
          ['ignored', 'Ignored'],
        ].map(([key, label]) => (
          <Chip key={key} label={label} color={artworkFilter === key ? 'primary' : 'default'} onClick={() => setArtworkFilter(key)} />
        ))}
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Folder</TableCell>
            <TableCell>Files</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Elite Core Customer</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredArtworkFolders.slice(0, 250).map((folder) => {
            const mapped = artworkSession?.folderMappings?.[folder.key];
            const status = mapped?.status || folder.status;
            return (
              <TableRow key={folder.key}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{folder.folderName}</Typography>
                  <Typography variant="caption" color="text.secondary">{folder.sourcePath || 'Root files'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{folder.fileCount} files - {folder.subfolderCount} subfolders</Typography>
                  <Typography variant="caption" color="text.secondary">{formatBytes(folder.totalBytes)}</Typography>
                </TableCell>
                <TableCell>
                  <Chip size="small" color={status === 'matched' ? 'success' : status === 'multiple' ? 'warning' : status === 'ignored' ? 'default' : 'info'} label={status} />
                </TableCell>
                <TableCell>
                  {folder.suggestedCustomer ? (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography variant="body2">{mapped?.customerName || folder.suggestedCustomer.businessName}</Typography>
                      <Button size="small" onClick={() => updateArtworkMapping(folder.key, {
                        status: 'matched',
                        customerId: folder.suggestedCustomer.id,
                        customerName: folder.suggestedCustomer.businessName,
                      })}>Accept</Button>
                      <Button size="small" color="inherit" onClick={() => updateArtworkMapping(folder.key, { status: 'ignored', customerId: null, customerName: null })}>Ignore</Button>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography variant="body2" color="text.secondary">No customer found</Typography>
                      <Button size="small" color="inherit" onClick={() => updateArtworkMapping(folder.key, { status: 'ignored' })}>Ignore</Button>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {filteredArtworkFolders.length > 250 && (
        <Alert severity="info">Showing first 250 folders. Use filters to narrow large migrations.</Alert>
      )}
    </Box>
  );

  const renderArtworkReviewStep = () => (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Alert severity="warning">
        Ready to import review only. Final artwork attachment is disabled until this mapping output is audited.
      </Alert>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={600}>Ready to import</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>{artworkFolders.length} customer folders analyzed.</Typography>
        <Typography variant="body2">{artworkSession?.mappedFolders || 0} customers mapped.</Typography>
        <Typography variant="body2">{artworkSession?.analysis?.uniqueFiles || 0} unique artwork files.</Typography>
        <Typography variant="body2">{artworkSession?.analysis?.duplicateFiles || 0} duplicates will be skipped.</Typography>
        <Typography variant="body2">{artworkSession?.unmappedFolders || 0} unmapped folders will be skipped.</Typography>
      </Paper>
      <Button variant="contained" disabled sx={{ justifySelf: 'start', textTransform: 'none' }}>
        Import Artwork Disabled
      </Button>
    </Box>
  );

  const renderArtworkStep = () => {
    if (activeStep === 0) return renderArtworkUploadStep();
    if (activeStep === 1) return renderArtworkAnalyzeStep();
    if (activeStep === 2) return renderArtworkMappingStep();
    if (activeStep === 3) return renderArtworkReviewStep();
    if (activeStep === 4) return <Alert severity="warning">Artwork commit is disabled for this phase.</Alert>;
    return <Alert severity="info">Artwork import results will appear here after commit is enabled.</Alert>;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        disableEscapeKeyDown
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { width: { xs: '100vw', md: '97vw' }, maxWidth: { xs: '100vw', md: '97vw' }, height: { xs: '100vh', md: '95vh' }, maxHeight: { xs: '100vh', md: '95vh' }, m: { xs: 0, md: 1 } } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box>
            <Typography variant="h6">
              {selectedTool === 'customer-terms'
                ? 'Customer Terms Update'
                : selectedTool === 'customers'
                ? 'Import Customers'
                : selectedTool === 'decorator-matrix'
                  ? 'Import Decoration Matrix'
                  : selectedTool === 'custom-suppliers'
                    ? 'Import Custom Suppliers'
                    : 'Import'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {selectedTool === 'customer-terms'
                ? 'Review and approve Net 30 changes for existing customers only.'
                : selectedTool === 'customers'
                ? 'Upload the customer files you already have. Elite Core will help assemble them.'
                : selectedTool === 'decorator-matrix'
                  ? 'Replace the active decoration charge matrix from an Elite Core export while retaining archived legacy charges.'
                  : selectedTool === 'custom-suppliers'
                    ? 'Create instance-owned suppliers or connect existing Core Suppliers without changing shared supplier identity.'
                    : 'Move data from another system into Elite Core.'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedTool === 'customers' && (
              <FormControlLabel
                control={(
                  <Switch
                    size="small"
                    checked={livePreviewEnabled}
                    onChange={(event) => setLivePreviewEnabled(event.target.checked)}
                    inputProps={{ 'aria-label': 'Toggle live customer import preview' }}
                  />
                )}
                label={(
                  <Box>
                    <Typography variant="body2" fontWeight={600}>Live Preview</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {livePreviewEnabled ? 'On' : 'Off · performance mode'}
                    </Typography>
                  </Box>
                )}
                sx={{ m: 0, mr: 1 }}
              />
            )}
            <IconButton onClick={requestClose} size="small" aria-label="Close import dialog">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: '#f8fafb', overflowY: 'auto' }}>
          {loading && !(selectedTool === 'customers' && activeStep === 0 && uploadPhase) && !(selectedTool === 'customers' && !livePreviewEnabled) && <LinearProgress sx={{ mb: 2 }} />}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2">{error}</Typography>
              {selectedTool === 'customers' && importErrorDetails && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" display="block">
                    {importErrorDetails.status ? `HTTP ${importErrorDetails.status}` : 'Import error'}
                    {importErrorDetails.code ? ` · ${importErrorDetails.code}` : ''}
                  </Typography>
                  <details>
                    <summary style={{ cursor: 'pointer', marginTop: 8 }}>View error details</summary>
                    {Array.isArray(importErrorDetails.details) ? (
                      <Box component="ol" sx={{ mt: 1, mb: 1, maxHeight: 240, overflow: 'auto', pl: 3 }}>
                        {importErrorDetails.details.map((entry, index) => (
                          <li key={index}>
                            <Typography variant="body2" sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
                              {typeof entry === 'string' ? entry : JSON.stringify(entry)}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    ) : (
                      <Box component="pre" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 240, overflowY: 'auto', fontSize: 12 }}>
                        {JSON.stringify(importErrorDetails.details || { message: importErrorDetails.message }, null, 2)}
                      </Box>
                    )}
                  </details>
                  <Button size="small" onClick={copyCustomerImportError} sx={{ mt: 1 }}>Copy diagnostics</Button>
                </Box>
              )}
            </Alert>
          )}

          {!selectedTool ? (
            renderToolPicker()
          ) : (
            <Box sx={{ display: 'grid', gap: 3 }}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {activeSteps.map((label, index) => {
                  const canNavigate = !loading
                    && !importing
                    && activeStep !== 5
                    && index <= highestReachedStep
                    && !(selectedTool === 'customers' && previewNeedsRefresh && index > activeStep);
                  return (
                  <Step key={label}>
                    <StepButton disabled={!canNavigate} onClick={() => navigateToStep(index)}>
                      {label}
                    </StepButton>
                  </Step>
                  );
                })}
              </Stepper>
              {selectedTool === 'customer-terms'
                ? <CustomerTermsRepair instanceUniqueId={instanceUniqueId} />
                : selectedTool === 'artwork'
                ? renderArtworkStep()
                : selectedTool === 'decorator-matrix'
                  ? renderDecoratorMatrixStep()
                  : selectedTool === 'custom-suppliers'
                    ? renderCustomSupplierStep()
                    : (
                      <>
                        {activeStep === 0 && renderUploadStep()}
                        {activeStep === 1 && renderMappingStep()}
                        {activeStep === 2 && renderEnrichmentStep()}
                        {activeStep === 3 && renderPreviewStep()}
                        {activeStep === 4 && renderImportStep()}
                        {activeStep === 5 && renderResultsStep()}
                      </>
                    )}
            </Box>
          )}
        </DialogContent>
        <Dialog open={asiBatchReviewOpen} onClose={() => !loading && setAsiBatchReviewOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Review suggested ASI supplier connections</DialogTitle>
          <DialogContent dividers sx={{ display: 'grid', gap: 1.5 }}>
            <Alert severity="info">
              Review suggested same-market ASI matches. For existing custom suppliers, link the two existing records;
              otherwise, attach contacts and addresses to this instance only. No ASI master records are modified.
            </Alert>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" onClick={() => setAsiBatchSelectedIds(asiBatchCandidates.map(({ record }) => record.id))}>Select all suggestions</Button>
              <Button size="small" onClick={() => setAsiBatchSelectedIds([])}>Clear selections</Button>
            </Box>
            {asiBatchCandidates.map(({ record, supplier, customSupplierId }) => (
              <Paper key={record.id} variant="outlined" sx={{ px: 1.5, py: 1 }}>
                <FormControlLabel
                  control={<Checkbox checked={asiBatchSelectedIds.includes(record.id)} onChange={(event) => setAsiBatchSelectedIds((current) => event.target.checked ? [...current, record.id] : current.filter((id) => id !== record.id))} />}
                  label={<Box>
                    <Typography variant="body2" fontWeight={600}>{record.name} → ASI #{supplier.Id} · {supplier.Name}</Typography>
                    {customSupplierId && <Typography variant="caption" display="block" color="success.main">Link existing Custom Core #{customSupplierId}; no duplicate supplier</Typography>}
                    <Typography variant="caption" color="text.secondary" display="block">Market: {supplier.supplierMarket || record.suggestedAssociation?.market || 'Unknown'}</Typography>
                    <Typography variant="caption" color="text.secondary">{(record.contacts || []).length} contacts · {(record.addresses || []).length} addresses · {record.reason || 'ASI supplier match'}</Typography>
                    {record.matchEvidence?.length > 0 && <Typography variant="caption" color="text.secondary" display="block">Match evidence: {record.matchEvidence.join(' · ')}</Typography>}
                  </Box>}
                />
              </Paper>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAsiBatchReviewOpen(false)} disabled={loading}>Cancel</Button>
            <Button variant="contained" onClick={applyAsiBatchReview} disabled={loading || asiBatchSelectedIds.length === 0}>
              Confirm {asiBatchSelectedIds.length} and update preview
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
        open={Boolean(customSupplierDetailsId)}
        onClose={() => setCustomSupplierDetailsId(null)}
        maxWidth="md"
        fullWidth
      >
        {(() => {
          const supplier = (customSupplierPreview?.records || []).find((entry) => entry.id === customSupplierDetailsId);
          if (!supplier) return null;
          const isAsiMatch = hasAsiSupplierReference(supplier.existingSupplier) || Boolean(supplier.companionAsiSupplierId);
          const details = [
            ['Email', supplier.email], ['Phone', supplier.phone], ['Fax', supplier.fax],
            ['Website', supplier.website], ['Currency (from file)', supplier.currency],
            ['Supplier account # (from file)', supplier.supplierAccountNumber],
            ['Notes', supplier.notes],
          ];
          return (
            <>
              <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6">{supplier.name || 'Unnamed supplier'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Preview only · {supplier.action?.replaceAll('_', ' ')}
                  </Typography>
                </Box>
                <IconButton onClick={() => setCustomSupplierDetailsId(null)} aria-label="Close supplier details"><CloseIcon /></IconButton>
              </DialogTitle>
              <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
                <Alert severity="info">
                  These are the parsed values from the upload, not a guarantee that every field will be persisted.
                  Currency and supplier account number are currently read but not saved by the supplier importer.
                </Alert>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Supplier information</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                    {details.map(([label, value]) => (
                      <Box key={label}>
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{value || '—'}</Typography>
                      </Box>
                    ))}
                  </Box>
                  {supplier.existingSupplier && (
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Matched {isAsiMatch ? 'ASI Supplier' : 'Core Supplier'}: #{supplier.existingSupplier.Id} · {supplier.existingSupplier.Name}
                    </Typography>
                  )}
                  {isAsiMatch && (
                    <Alert severity="info" sx={{ mt: 1.5 }}>
                      <strong>Already exists in ASI.</strong> This is an ASI-backed Core Supplier record,
                      not a custom supplier to merge or replace. No import is needed just to use its ASI catalogue.
                      The supplier spreadsheet does not create custom products.
                      {supplier.action === 'asi_existing'
                        ? ' This record will be left unchanged.'
                        : ' Any imported details are stored only for your instance; the ASI record is unchanged.'}
                    </Alert>
                  )}
                  {supplier.matchType === 'linked_existing_custom_asi' && (
                    <Alert severity="success" sx={{ mt: 1.5 }}>
                      Update existing custom #{supplier.existingSupplier?.Id} and link to ASI #{supplier.linkedAsiSupplierId}.
                      <Button size="small" disabled={loading} sx={{ display: 'block', mt: 1 }}
                        onClick={() => selectCustomSupplierMatch(supplier.id, null)}>Cancel association</Button>
                    </Alert>
                  )}
                  {supplier.matchType === 'asi_custom_unlinked_default' && (
                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                      A separate custom supplier will be created with uploaded contacts. The company email conflicts with the suggested ASI match, so no ASI relationship is created automatically.
                    </Alert>
                  )}
                  {supplier.action === 'create_asi_custom' && (
                    <Alert severity="success" sx={{ mt: 1.5 }}>
                      <strong>Separate custom supplier planned.</strong> Import will create a new instance-owned supplier
                      linked to ASI #{supplier.companionAsiSupplierId}. The ASI supplier stays unchanged;
                      nothing is merged or deleted. You can later add your own products to the new custom supplier
                      from Custom Products. No products are imported by this spreadsheet.
                      {supplier.matchType !== 'asi_custom_default' && <Button size="small" onClick={() => selectCustomSupplierMatch(supplier.id, null)}
                        disabled={loading} sx={{ display: 'block', mt: 1 }}>Restore default</Button>}
                      {supplier.matchType === 'asi_custom_default' && <Typography variant="caption" display="block" sx={{ mt: 1 }}>Selected by default · Carey and other uploaded contacts are saved on the new custom supplier when you import.</Typography>}
                    </Alert>
                  )}
                  {supplier.action === 'asi_existing' && (
                    <Box sx={{ mt: 1.5, display: 'grid', gap: 1 }}>
                      <Button variant="outlined" size="small" disabled={loading}
                        onClick={() => selectCustomSupplierMatch(supplier.id, { action: 'connect_asi', supplierId: supplier.existingSupplier.Id })}>
                        Attach contacts / addresses locally
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        Optional: use this to attach contacts or addresses without creating a new supplier.
                      </Typography>
                      {supplier.canCreateAsiCompanion ? (
                        <Box>
                          <Button variant="outlined" size="small" color="primary" disabled={loading}
                            onClick={() => selectCustomSupplierMatch(supplier.id, {
                              action: 'create_asi_custom', supplierId: supplier.existingSupplier.Id,
                            })}>
                            Create a linked custom supplier
                          </Button>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Use this when ASI does not list all the supplier’s products. Your custom products can be
                            added under the separate supplier afterward. No ASI data is overwritten.
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          You already have an instance-owned custom supplier for this ASI company.
                          Use that record instead of creating another duplicate.
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Otherwise, this ASI supplier is skipped automatically.
                      </Typography>
                    </Box>
                  )}
                  {['asi_local_default', 'asi_local_opt_in'].includes(supplier.matchType) && (
                    <Paper variant="outlined" sx={{ p: 2, mt: 1.5, borderColor: 'success.main', bgcolor: 'background.paper' }}>
                      <Typography variant="subtitle2" color="success.main" fontWeight={700}>
                        Attach contacts / addresses locally — {supplier.matchType === 'asi_local_default' ? 'Selected by default' : 'Selected'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Will attach {supplier.contacts?.length || 0} contacts and {supplier.addresses?.length || 0} addresses to this instance on import.
                        ASI catalogue and master supplier data are unchanged. No custom supplier is created.
                      </Typography>
                      <Box sx={{ mt: 1, p: 1.5, borderRadius: 1, bgcolor: 'background.default' }}>
                        <Typography variant="subtitle2" fontWeight={700}>Purchase order email default (ASI only)</Typography>
                        {supplier.poEmailSuggestion?.mode === 'contact' ? (
                          <>
                            <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
                              Suggested Orders contact: {supplier.poEmailSuggestion.contactEmail}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Automatically set on import only if this instance and its linked suppliers have no saved PO email preference.
                              A previously selected contact or Supplier.Email preference always wins. No email is sent during import.
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Use Supplier.Email (default). No single unambiguous Orders / Order Entry / Order Desk contact was found,
                            or the Orders contact is already the supplier email.
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        <Button size="small" variant="outlined" disabled={loading}
                          onClick={() => selectCustomSupplierMatch(supplier.id, { action: 'skip' })}>
                          Skip this supplier
                        </Button>
                        {supplier.canCreateAsiCompanion && (
                          <Button size="small" variant="outlined" disabled={loading}
                            onClick={() => selectCustomSupplierMatch(supplier.id, { action: 'create_asi_custom', supplierId: supplier.existingSupplier.Id })}>
                            Create a linked custom supplier instead
                          </Button>
                        )}
                      </Box>
                    </Paper>
                  )}
                  {supplier.action === 'skip' && supplier.matchType === 'reviewer_skip' && (
                    <Button size="small" variant="outlined" disabled={loading} onClick={() => selectCustomSupplierMatch(supplier.id, null)}>
                      Restore default selection
                    </Button>
                  )}
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Decision: {supplier.action?.replaceAll('_', ' ')} · {supplier.reason || supplier.matchType || '—'}
                  </Typography>
                  {supplier.matchEvidence?.length > 0 && (
                    <Typography variant="caption" color="success.main" display="block" sx={{ mt: 1 }}>
                      Matching evidence: {supplier.matchEvidence.join(' · ')}
                    </Typography>
                  )}
                  {supplier.wasBlocked && supplier.existingSupplier && (
                    <Alert severity="info" sx={{ mt: 1.5 }}>
                      <strong>Selected #{supplier.existingSupplier.Id}.</strong> When you run Import, this
                      {supplier.action === 'update_instance'
                        ? ' supplier will receive the uploaded information while keeping its populated details.'
                        : isAsiMatch
                          ? ' ASI supplier will receive instance-only details without changing the ASI source.'
                          : ' existing Core Supplier will be connected or enriched for this instance only.'}
                      {' '}Other matching supplier records stay unchanged. None will be merged together or deleted.
                      Your selection has not changed the database.
                    </Alert>
                  )}
                  {supplier.action === 'skip' && (
                    <Alert severity="info" sx={{ mt: 1.5 }}>
                      This uploaded supplier will be skipped. No supplier records will be changed or deleted for it.
                    </Alert>
                  )}
                  {supplier.action === 'blocked' && supplier.matchCandidates?.length > 0 && (
                    <Box sx={{ mt: 2, display: 'grid', gap: 1 }}>
                      <Typography variant="subtitle2">Choose the existing Core Supplier</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {supplier.matchCandidates.length} existing suppliers match. Choose how to handle the uploaded
                        details ({(supplier.contacts || []).length} contacts, {(supplier.addresses || []).length} addresses).
                        ASI suppliers are foundational records. You can attach local details or create a separate
                        custom supplier for products ASI does not list. Nothing merges into or replaces ASI,
                        and the other supplier records remain unchanged.
                      </Typography>
                      {supplier.suggestedAssociation && (
                        <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'success.main' }}>
                          <Typography variant="subtitle2">Suggested existing-supplier association</Typography>
                          <Typography variant="body2">
                            Custom #{supplier.suggestedAssociation.customSupplierId} + ASI #{supplier.suggestedAssociation.asiSupplierId}
                            {' · '}{supplier.suggestedAssociation.market}. Neither supplier will be duplicated.
                          </Typography>
                          <Button size="small" variant="contained" disabled={loading} sx={{ mt: 1 }}
                            onClick={() => selectCustomSupplierMatch(supplier.id, {
                              action: 'link_existing_custom_asi',
                              supplierId: supplier.suggestedAssociation.customSupplierId,
                              asiSupplierId: supplier.suggestedAssociation.asiSupplierId,
                            })}>Confirm existing-record link</Button>
                        </Paper>
                      )}
                      {supplier.matchCandidates.map((candidate) => (
                        <Paper key={candidate.Id} variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="body2" fontWeight={600}>#{candidate.Id} · {candidate.Name}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {candidate.Email || 'No company email'} · {candidate.supplierSource === 'asi-live'
                              ? 'ASI catalogue supplier · original record protected'
                              : candidate.action === 'update_instance' ? 'Instance-owned custom supplier' : 'Existing Core Supplier · local connection only'}
                          </Typography>
                          <Typography variant="caption" display="block" color={candidate.marketConflict ? 'error.main' : 'text.secondary'}>
                            Market: {candidate.supplierMarket || 'Unknown'}
                            {candidate.importedMarket ? ` · Imported: ${candidate.importedMarket}` : ''}
                            {candidate.marketConflict ? ' · Country conflict; cannot auto-select' : ''}
                          </Typography>
                          {(candidate.matchEvidence || []).map((evidence) => (
                            <Typography key={evidence} variant="caption" color="success.main" display="block">{evidence}</Typography>
                          ))}
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            <Button size="small" variant="outlined" disabled={loading || candidate.marketConflict}
                              onClick={() => selectCustomSupplierMatch(supplier.id, {
                                action: candidate.supplierSource === 'asi-live' ? 'connect_asi' : 'use_existing',
                                supplierId: candidate.Id,
                              })}>
                              {candidate.supplierSource === 'asi-live' ? 'Attach details locally' : 'Use existing custom supplier'}
                            </Button>
                            {candidate.supplierSource === 'asi-live' && candidate.canCreateAsiCompanion && (
                              <Button size="small" variant="outlined" disabled={loading || candidate.marketConflict}
                                onClick={() => selectCustomSupplierMatch(supplier.id, {
                                  action: 'create_asi_custom', supplierId: candidate.Id,
                                })}>
                                Create separate custom supplier
                              </Button>
                            )}
                          </Box>
                          {candidate.supplierSource === 'asi-live' && !candidate.canCreateAsiCompanion && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              A custom supplier already exists. Select that supplier instead of creating a duplicate.
                            </Typography>
                          )}
                        </Paper>
                      ))}
                      {supplier.matchCandidates.length > 0 &&
                        supplier.matchCandidates.every((candidate) => candidate.marketConflict) &&
                        supplier.matchCandidates.some((candidate) => candidate.importedMarket &&
                          candidate.importedMarket !== 'MIXED') && (
                          <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'warning.main', borderRadius: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              No matching supplier in the imported country was found. You can create a
                              separate instance-owned custom supplier without linking to any
                              conflicting ASI catalogue record.
                            </Typography>
                            <Button size="small" variant="contained" disabled={loading}
                              onClick={() => selectCustomSupplierMatch(supplier.id, { action: 'create_custom' })}>
                              Create separate custom supplier for {supplier.matchCandidates[0].importedMarket}
                            </Button>
                          </Box>
                        )}
                      <Button size="small" color="inherit" disabled={loading}
                        onClick={() => selectCustomSupplierMatch(supplier.id, { action: 'skip' })}>
                        Skip this supplier
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        Selecting a supplier only updates this preview. Database changes happen when you run Import.
                      </Typography>
                    </Box>
                  )}
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    Contacts ({(supplier.contacts || []).length})
                  </Typography>
                  {(supplier.contacts || []).length ? (
                    <Table size="small">
                      <TableHead><TableRow>
                        <TableCell>Name</TableCell><TableCell>Title / Department</TableCell>
                        <TableCell>Email</TableCell><TableCell>Phone / Mobile</TableCell><TableCell>Created At</TableCell>
                      </TableRow></TableHead>
                      <TableBody>{supplier.contacts.map((contact, index) => (
                        <TableRow key={index}>
                          <TableCell>{[contact.firstName, contact.lastName].filter(Boolean).join(' ') || '—'}</TableCell>
                          <TableCell>{[contact.title, contact.department].filter(Boolean).join(' · ') || '—'}</TableCell>
                          <TableCell>{contact.email || '—'}</TableCell>
                          <TableCell>{[contact.phone, contact.mobile].filter(Boolean).join(' · ') || '—'}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {(() => {
                              const timestamp = contact.createdAt || contact.importedAt;
                              return timestamp && !Number.isNaN(new Date(timestamp).getTime())
                                ? new Date(timestamp).toLocaleDateString()
                                : <Typography variant="caption" color="text.secondary">On import</Typography>;
                            })()}
                          </TableCell>
                        </TableRow>
                      ))}</TableBody>
                    </Table>
                  ) : <Typography variant="body2" color="text.secondary">No contacts in this file for this supplier.</Typography>}
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    Addresses ({(supplier.addresses || []).length})
                  </Typography>
                  {(supplier.addresses || []).length ? (
                    <Table size="small">
                      <TableHead><TableRow>
                        <TableCell>Name / Type</TableCell><TableCell>Street</TableCell>
                        <TableCell>City / Province</TableCell><TableCell>Postal / Country</TableCell><TableCell>Created At</TableCell>
                      </TableRow></TableHead>
                      <TableBody>{supplier.addresses.map((address, index) => (
                        <TableRow key={index}>
                          <TableCell>{[address.addressName, address.addressType].filter(Boolean).join(' · ') || '—'}</TableCell>
                          <TableCell>{[address.streetAddress, address.streetAddress2].filter(Boolean).join(', ') || '—'}</TableCell>
                          <TableCell>{[address.city, address.stateProvince].filter(Boolean).join(', ') || '—'}</TableCell>
                          <TableCell>{[address.postalZipCode, address.country].filter(Boolean).join(' · ') || '—'}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {(() => {
                              const timestamp = address.createdAt || address.importedAt;
                              return timestamp && !Number.isNaN(new Date(timestamp).getTime())
                                ? new Date(timestamp).toLocaleDateString()
                                : <Typography variant="caption" color="text.secondary">On import</Typography>;
                            })()}
                          </TableCell>
                        </TableRow>
                      ))}</TableBody>
                    </Table>
                  ) : <Typography variant="body2" color="text.secondary">No addresses in this file for this supplier.</Typography>}
                </Paper>
                <details>
                  <summary style={{ cursor: 'pointer' }}>
                    Source rows ({(supplier.sourceRows || []).length})
                  </summary>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, overflowWrap: 'anywhere' }}>
                    {(supplier.sourceRows || []).join(', ') || '—'}
                  </Typography>
                </details>
              </DialogContent>
              <DialogActions><Button onClick={() => setCustomSupplierDetailsId(null)}>Close</Button></DialogActions>
            </>
          );
        })()}
      </Dialog>
      {selectedTool && !['decorator-matrix', 'custom-suppliers', 'customer-terms'].includes(selectedTool) && (
          <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
            <Button
              onClick={() => navigateToStep(activeStep - 1)}
              disabled={activeStep === 0 || activeStep === 5 || loading || importing}
              sx={{ textTransform: 'none' }}
            >
              Back
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={() => runPreview()}
                disabled={selectedTool !== 'customers' || !file || loading || importing || activeStep === 5}
                sx={{ textTransform: 'none' }}
              >
                Refresh Preview
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  if (selectedTool === 'customers' && activeStep === 3) {
                    if (representativeMappings.length > 0 && !representativeReviewComplete) {
                      setRepresentativeReviewComplete(true);
                      return;
                    }
                    continueToImportReview();
                    return;
                  }
                  navigateToStep(Math.min(activeSteps.length - 1, activeStep + 1), { allowAdvance: true });
                }}
                disabled={
                  loading
                  || importing
                  || (selectedTool === 'customers' && previewNeedsRefresh)
                  || (selectedTool === 'customers' && ((activeStep === 0 && !preview) || activeStep >= 4))
                  || (selectedTool === 'artwork' && ((activeStep === 0 && !artworkSession) || activeStep >= 3))
                }
                sx={{ textTransform: 'none' }}
              >
                {activeStep === 3
                  ? (!representativeReviewComplete && representativeMappings.length > 0
                    ? 'Continue to Assign & Review'
                    : needsReviewCount > 0 && !skipUnresolvedMatches
                      ? 'Review Import Options'
                      : 'Review Import')
                  : 'Next'}
              </Button>
            </Box>
          </DialogActions>
        )}
      </Dialog>

      <Dialog
        open={testImportInspectOpen}
        onClose={() => setTestImportInspectOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: { xs: '100vh', md: '92vh' }, maxHeight: { xs: '100vh', md: '92vh' } } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box>
            <Typography variant="h6">Test Import — database Before / After</Typography>
            <Typography variant="body2" color="text.secondary">
              These are the actual database snapshots produced by the real create/merge persistence path inside the rollback-only test transaction.
            </Typography>
          </Box>
          <IconButton onClick={() => setTestImportInspectOpen(false)} size="small" aria-label="Close test import comparison">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {(() => {
            const testRows = testImportResult?.results || [];
            const selected = testRows[Math.min(testImportInspectIndex, Math.max(0, testRows.length - 1))] || null;
            if (!selected) {
              return <Alert severity="info">Run Test Import to generate database Before / After examples.</Alert>;
            }

            return (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '280px minmax(0, 1fr)' }, gap: 2, minHeight: 0 }}>
                <Paper variant="outlined" sx={{ p: 1.25, maxHeight: '72vh', overflow: 'auto' }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ px: 0.75, pb: 1 }}>
                    Tested records
                  </Typography>
                  <Box sx={{ display: 'grid', gap: 0.75 }}>
                    {testRows.map((row, index) => (
                      <Button
                        key={row.recordId}
                        variant={index === testImportInspectIndex ? 'contained' : 'outlined'}
                        color={row.success ? 'primary' : 'error'}
                        onClick={() => setTestImportInspectIndex(index)}
                        sx={{
                          textTransform: 'none',
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          px: 1.25,
                          py: 1,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {row.businessName || 'Unnamed customer'}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
                            {row.sourceAction === 'update' ? 'Existing · merge' : 'New · create'}
                            {row.customerId ? ` · #${row.customerId}` : ''}
                            {!row.success ? ' · failed' : ''}
                          </Typography>
                        </Box>
                      </Button>
                    ))}
                  </Box>
                </Paper>

                <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight={700}>
                      {selected.businessName || 'Unnamed customer'}
                    </Typography>
                    <Chip
                      size="small"
                      color={selected.sourceAction === 'update' ? 'info' : 'success'}
                      label={selected.sourceAction === 'update' ? 'Existing customer · merge' : 'New customer · create'}
                    />
                    <Chip size="small" color="success" label="Rolled back · 0 retained writes" />
                  </Box>

                  {!selected.success && (
                    <Alert severity="error">{selected.error || 'Test Import failed for this record.'}</Alert>
                  )}

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, minHeight: 0 }}>
                    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={700}>BEFORE</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {selected.before
                              ? 'Current Elite Core database record before the test write.'
                              : 'No existing customer record — this test would create a new customer.'}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          onClick={() => copyDryRunJson(selected.before ?? null, 'Test Import BEFORE')}
                          sx={{ textTransform: 'none' }}
                        >
                          Copy JSON
                        </Button>
                      </Box>
                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          p: 1.25,
                          height: '58vh',
                          overflow: 'auto',
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          fontSize: 12,
                          lineHeight: 1.45,
                          whiteSpace: 'pre-wrap',
                          overflowWrap: 'anywhere',
                          fontFamily: 'monospace',
                        }}
                      >
                        {JSON.stringify(selected.before ?? null, null, 2)}
                      </Box>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={700}>AFTER</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Exact customer row after running the real importer, immediately before the test transaction was rolled back.
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          onClick={() => copyDryRunJson(selected.after ?? null, 'Test Import AFTER')}
                          sx={{ textTransform: 'none' }}
                        >
                          Copy JSON
                        </Button>
                      </Box>
                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          p: 1.25,
                          height: '58vh',
                          overflow: 'auto',
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          fontSize: 12,
                          lineHeight: 1.45,
                          whiteSpace: 'pre-wrap',
                          overflowWrap: 'anywhere',
                          fontFamily: 'monospace',
                        }}
                      >
                        {JSON.stringify(selected.after ?? null, null, 2)}
                      </Box>
                    </Paper>
                  </Box>
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">
            Test Import executes the real persistence path and rolls back the transaction after capturing AFTER.
          </Typography>
          <Button onClick={() => setTestImportInspectOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(dryRunInspectRecord)}
        onClose={() => {
          setDryRunInspectRecord(null);
          setDryRunCopyStatus('');
        }}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { height: { xs: '100vh', md: '92vh' }, maxHeight: { xs: '100vh', md: '92vh' } } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box>
            <Typography variant="h6">Dry Run record inspector</Typography>
            <Typography variant="body2" color="text.secondary">
              {dryRunInspectRecord?.businessName || 'Unnamed customer'}
              {dryRunInspectRecord?.targetCustomerId ? ` · Existing Customer #${dryRunInspectRecord.targetCustomerId}` : ''}
              {dryRunInspectRecord?.action ? ` · ${String(dryRunInspectRecord.action).replaceAll('_', ' ')}` : ''}
            </Typography>
          </Box>
          <IconButton onClick={() => setDryRunInspectRecord(null)} size="small" aria-label="Close dry run inspector">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {dryRunInspectRecord && (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => copyDryRunJson(dryRunInspectRecord, 'Full inspection JSON')}
                  sx={{ textTransform: 'none' }}
                >
                  Copy full inspection JSON
                </Button>
                {dryRunCopyStatus && <Chip size="small" color="success" label={dryRunCopyStatus} />}
              </Box>

              {(dryRunInspectRecord.warnings || []).length > 0 && (
                <Alert severity={dryRunInspectRecord.action === 'blocked' ? 'error' : 'warning'}>
                  {(dryRunInspectRecord.warnings || []).join(' · ')}
                </Alert>
              )}

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
                {[
                  ['Imported / staged record', dryRunInspectRecord.sourceRecord],
                  ['Current Elite Core — BEFORE', dryRunInspectRecord.before],
                  ['Result if imported — AFTER', dryRunInspectRecord.after],
                  ['Planned changes', dryRunInspectRecord.plannedChanges],
                  ['Database operations', dryRunInspectRecord.operations],
                  ['Field diffs', dryRunInspectRecord.diffs],
                ].map(([label, value]) => (
                  <Paper key={label} variant="outlined" sx={{ p: 1.5, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700}>{label}</Typography>
                      <Button size="small" onClick={() => copyDryRunJson(value, label)} sx={{ textTransform: 'none' }}>
                        Copy JSON
                      </Button>
                    </Box>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.25,
                        maxHeight: 360,
                        overflow: 'auto',
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        fontSize: 12,
                        lineHeight: 1.45,
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                        fontFamily: 'monospace',
                      }}
                    >
                      {JSON.stringify(value ?? null, null, 2)}
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDryRunInspectRecord(null)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkAssignOpen}
        onClose={() => !bulkAssigning && setBulkAssignOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Mass assign filtered customers</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Alert severity={bulkAssignMode === 'replace' ? 'warning' : 'info'}>
              This action applies to all <strong>{filteredReadyRecordCount.toLocaleString()}</strong> customers currently matching this filter, not only the {visiblePreviewRecords.length} customers on this page.
              {!previewProgress?.complete && ' New customers that finish processing and match this same filter will inherit the rule as the file continues.'}
            </Alert>

            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                FILTER SCOPE
              </Typography>
              <Typography variant="body2">
                {representativeFilter === 'all'
                  ? 'All Customers'
                  : representativeFilter === 'unassigned'
                    ? 'Unassigned'
                    : representativeFilter === 'needs_review'
                      ? 'Needs Review'
                      : userLabel((preview?.instanceUsers || []).find((user) => String(user.id) === String(representativeFilter)) || {})}
              </Typography>
            </Box>

            <Autocomplete
              multiple
              size="small"
              options={(preview?.instanceUsers || [])
                .filter((user) => ['client-rep', 'order-rep', 'administrator'].includes(String(user.role || '').toLowerCase()))
                .sort((a, b) => userLabel(a).localeCompare(userLabel(b)))}
              value={bulkAssignUsers}
              getOptionLabel={userLabel}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              onChange={(_event, value) => setBulkAssignUsers(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Elite Core users"
                  helperText="Choose one or more users to assign to every customer in this filtered set."
                />
              )}
            />

            <FormControl fullWidth size="small">
              <InputLabel id="bulk-assignment-mode-label">Assignment behavior</InputLabel>
              <Select
                labelId="bulk-assignment-mode-label"
                label="Assignment behavior"
                value={bulkAssignMode}
                onChange={(event) => setBulkAssignMode(event.target.value)}
              >
                <MenuItem value="add">Add these users and keep current assignments</MenuItem>
                <MenuItem value="replace">Replace current assignments with these users</MenuItem>
              </Select>
            </FormControl>

            {bulkAssignMode === 'replace' && (
              <Alert severity="warning">
                Replace mode will remove existing assigned users from matched Elite Core customers in this filtered set when the import is committed. The Dry Run will show those removals before any production write occurs.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkAssignOpen(false)}
            disabled={bulkAssigning}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={applyBulkAssignment}
            disabled={bulkAssigning || !bulkAssignUsers.length}
            sx={{ textTransform: 'none' }}
          >
            {bulkAssigning ? 'Applying…' : `Apply to ${filteredReadyRecordCount.toLocaleString()} customers`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(reviewCustomer)} onClose={() => setReviewCustomer(null)} maxWidth="md" fullWidth>
        <DialogTitle>{reviewCustomer?.businessName || 'Customer review'}</DialogTitle>
        <DialogContent dividers>
          {reviewCustomer && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 2 }}>
              <Box><Typography variant="subtitle2">Imported values</Typography><Typography variant="body2">Email: {reviewCustomer.companyEmail || reviewCustomer.primaryEmail || '—'}</Typography><Typography variant="body2">Phone: {reviewCustomer.phone || '—'}</Typography><Typography variant="body2">Location: {[reviewCustomer.city, reviewCustomer.stateProvince].filter(Boolean).join(', ') || '—'}</Typography></Box>
              <Box><Typography variant="subtitle2">Existing Elite Core customer</Typography><Typography variant="body2">{reviewCustomer.existingCustomer?.businessName || 'No existing match'}</Typography><Typography variant="caption" color="text.secondary">{reviewCustomer.existingCustomer?.matchType?.replaceAll('_', ' ') || ''}</Typography></Box>
              <Box><Typography variant="subtitle2">Planned result</Typography><Typography variant="body2">{plannedChangeLabel(reviewCustomer)}</Typography><Typography variant="caption" color="text.secondary">Changes below are staged only. Production customer data is not changed until the final Import step.</Typography></Box>
              <Box sx={{ gridColumn: '1 / -1', display: 'grid', gap: 1.25, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2">Assignments</Typography>
                {reviewCustomer.existingCustomer && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>Currently in Elite Core</Typography>
                    <Box sx={{ display: 'flex', gap: .5, flexWrap: 'wrap', mt: .5 }}>
                      {existingAssignmentsForRecord(reviewCustomer, preview?.instanceUsers || []).length
                        ? existingAssignmentsForRecord(reviewCustomer, preview?.instanceUsers || []).map((user) => <Chip key={user.id} size="small" variant="outlined" label={`${userLabel(user)} · ${roleLabel(user.role)}`} />)
                        : <Typography variant="caption" color="text.secondary">None</Typography>}
                    </Box>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>{reviewCustomer.existingCustomer ? 'Being added by this import' : 'Being assigned by this import'}</Typography>
                  <Box sx={{ display: 'flex', gap: .5, flexWrap: 'wrap', mt: .5 }}>
                    {(reviewCustomer.existingCustomer
                      ? newAssignmentsForRecord(reviewCustomer, preview?.instanceUsers || [])
                      : effectiveAssignmentsForRecord(reviewCustomer, preview?.instanceUsers || [])
                    ).length
                      ? (reviewCustomer.existingCustomer
                        ? newAssignmentsForRecord(reviewCustomer, preview?.instanceUsers || [])
                        : effectiveAssignmentsForRecord(reviewCustomer, preview?.instanceUsers || [])
                      ).map((user) => <Chip key={user.id} size="small" color="primary" label={`${userLabel(user)} · ${roleLabel(user.role)}`} />)
                      : <Typography variant="caption" color="text.secondary">No staged assignments</Typography>}
                  </Box>
                </Box>
                <Autocomplete
                  size="small"
                  options={preview?.instanceUsers || []}
                  value={null}
                  getOptionLabel={userLabel}
                  onChange={(_event, user) => {
                    if (!user) return;
                    setRepresentativeDecisions((current) => ({ ...current, [`${reviewCustomer.id}:manual`]: { action: 'select_user', userId: user.id } }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Add Elite Core user to this import record" />}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setReviewCustomer(null)}>Done</Button></DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(representativeGroupTarget)}
        onClose={closeRepresentativeGroup}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Representative Group</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Assign everyone who should inherit when <strong>{representativeGroupTarget?.sourceValue}</strong> appears.
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            This applies to every matching customer in this import. Most representative values only need the normal single-user mapping.
          </Typography>

          <Box sx={{ display: 'grid', gap: 1.25 }}>
            {representativeGroupDraft.map((assignment, index) => {
              const selectedUser = (preview?.instanceUsers || []).find((user) => String(user.id) === String(assignment.userId)) || null;
              return (
                <Box
                  key={`group-member-${index}`}
                  sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 150px 36px', gap: 1, alignItems: 'center' }}
                >
                  <Autocomplete
                    size="small"
                    options={preview?.instanceUsers || []}
                    value={selectedUser}
                    getOptionLabel={userLabel}
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                    onChange={(_event, user) => {
                      setRepresentativeGroupDraft((current) => current.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, userId: user?.id ? String(user.id) : '' } : item
                      ));
                    }}
                    renderInput={(params) => <TextField {...params} label="Elite Core user" />}
                  />
                  <Select
                    size="small"
                    value={assignment.role}
                    onChange={(event) => {
                      setRepresentativeGroupDraft((current) => current.map((item, itemIndex) =>
                        itemIndex === index ? { ...item, role: event.target.value } : item
                      ));
                    }}
                  >
                    <MenuItem value="client-rep">Client Rep</MenuItem>
                    <MenuItem value="order-rep">Order Rep</MenuItem>
                  </Select>
                  <IconButton
                    size="small"
                    aria-label="Remove group member"
                    onClick={() => setRepresentativeGroupDraft((current) => current.filter((_item, itemIndex) => itemIndex !== index))}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}

            {!representativeGroupDraft.length && (
              <Alert severity="info">
                Add the Elite Core users who should be inherited for this source value.
              </Alert>
            )}

            <Button
              variant="outlined"
              onClick={addRepresentativeGroupMember}
              sx={{ justifySelf: 'start', textTransform: 'none' }}
            >
              Add user
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          {representativeMappingDecision(representativeGroupTarget || {})?.action === 'assign_group' && (
            <Button
              color="error"
              onClick={() => {
                setRepresentativeSourceDecision(
                  representativeGroupTarget.field,
                  representativeGroupTarget.sourceValue,
                  { action: 'leave_unassigned' },
                );
                closeRepresentativeGroup();
              }}
              sx={{ mr: 'auto', textTransform: 'none' }}
            >
              Remove group
            </Button>
          )}
          <Button onClick={closeRepresentativeGroup} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={saveRepresentativeGroup} sx={{ textTransform: 'none' }}>
            Apply to all matching customers
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={templateHelpOpen}
        onClose={() => setTemplateHelpOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>How to prepare a customer file</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>Start simple</Typography>
              <Typography variant="body2" color="text.secondary">
                Each row should represent a customer. Business Name is the main customer name. Add the other columns you have; blank cells are okay.
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={700}>Contacts: use either method</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                If you have one contact, use Contact First Name, Contact Last Name, Contact Email, Contact Phone and Contact Title.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                If one customer has several contacts, you can either use several rows for that same Business Name, or put all of the contacts into Contact List.
              </Typography>
            </Box>

            <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: 'grey.50' }}>
              <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
                Contact List example
              </Typography>
              <Typography
                component="pre"
                variant="caption"
                sx={{
                  m: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                }}
              >
                {"[\\n  {\"firstName\":\"Alex\",\"lastName\":\"Chen\",\"email\":\"alex@example.com\",\"phone\":\"780-555-0120\",\"title\":\"Operations\"},\\n  {\"firstName\":\"Taylor\",\"lastName\":\"Morgan\",\"email\":\"taylor@example.com\",\"title\":\"Accounting\"}\\n]"}
              </Typography>
            </Paper>

            <Alert severity="info">
              You do not need to use Contact List. It is only a convenient option when you want one customer per row but that customer has several contacts.
            </Alert>

            <Box>
              <Typography variant="subtitle2" fontWeight={700}>One-row helpers are optional</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                If you prefer one customer per row, the example file also includes Address List and Assigned Users.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Address List can hold several addresses. Assigned Users can hold the employee name, email and role (client-rep or order-rep). Elite Core still matches those employees against the users in your instance before assigning them.
              </Typography>
            </Box>

            <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: 'grey.50' }}>
              <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
                Assigned Users example
              </Typography>
              <Typography component="pre" variant="caption" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace' }}>
                {'[{"firstName":"Charlotte","lastName":"Horton","email":"charlotte@example.com","role":"client-rep"}]'}
              </Typography>
            </Paper>

            <Typography variant="caption" color="text.secondary">
              The normal Contact, Address, Client Rep and Order Rep columns still work. These list columns are just another option for compact one-row files.
            </Typography>

            <Box>
              <Typography variant="subtitle2" fontWeight={700}>You can still use your own export</Typography>
              <Typography variant="body2" color="text.secondary">
                Elite Core recognizes common column names and lets you review the field mapping before anything is imported. The example file simply uses the preferred names.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateHelpOpen(false)} sx={{ textTransform: 'none' }}>
            Got it
          </Button>
          <Button variant="contained" onClick={downloadCustomerTemplate} sx={{ textTransform: 'none' }}>
            Download Example File
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={needsReviewContinueConfirmOpen}
        onClose={() => setNeedsReviewContinueConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Continue to import review?</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Possible customer matches that are still set to <strong>Create as new</strong> will be imported as separate new customers.
          </Alert>
          {needsReviewCount > 0 && (
            <>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                {needsReviewCount.toLocaleString()} customer{needsReviewCount === 1 ? '' : 's'} still have a genuinely unresolved blocking match decision. Those records cannot be safely created or merged automatically.
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                Only those {needsReviewCount.toLocaleString()} unresolved blocking record{needsReviewCount === 1 ? '' : 's'} will be skipped if you continue.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Dry Run will show any skipped blocking records separately. Customers with the default Create as new selection will appear under Would create.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNeedsReviewContinueConfirmOpen(false)} sx={{ textTransform: 'none' }}>
            Back to Review
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={confirmSkipNeedsReviewAndContinue}
            sx={{ textTransform: 'none' }}
          >
            Continue to Import Review
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={unassignedContinueConfirmOpen}
        onClose={() => setUnassignedContinueConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Continue with unassigned customers?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {unassignedCount.toLocaleString()} customer{unassignedCount === 1 ? '' : 's'} do not have an assigned Elite Core user.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1.25 }}>
            Assignment is recommended, but it is not required to import a customer.
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            If you continue, these customers will be imported without an assigned user.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            No customer will be skipped solely because it is unassigned. Dry Run will still validate customer matches and required customer data before any production write.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUnassignedContinueConfirmOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={confirmContinueWithUnassignedCustomers}
            sx={{ textTransform: 'none' }}
          >
            Continue Without Assignments
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={closeConfirmOpen} onClose={() => setCloseConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Close import?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Your current import progress will be discarded.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseConfirmOpen(false)} sx={{ textTransform: 'none' }}>
            Keep Working
          </Button>
          <Button color="error" variant="contained" onClick={closeAndReset} sx={{ textTransform: 'none' }}>
            Close Import
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ImportToolDialog;
